import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, ChevronRight, ChevronDown, CalendarClock, Plus, Search, GripVertical,
  Trash2, ExternalLink, AlertTriangle, MapPin, X, PanelRightOpen, PanelRightClose,
  CalendarDays, Phone, User as UserIcon, ClipboardList, Flag, History, Filter, Check,
  Send, CheckCircle2, Copy, MessageSquare, Mail, Loader2, UserRound, CornerDownRight, Wand2, Map, Store, Users, ShieldCheck, MoreVertical, ArrowRightLeft, Tag, Footprints, Link2, ClipboardCheck, Circle, CalendarRange, Clock, Building2, Undo2, Redo2, Ban, RotateCcw, Heart, Lock, Pencil, Trophy, TrendingUp, Sparkles, RefreshCw, EyeOff, ListFilter
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { Gauge } from "@/components/Kpi";
import { BookingBadge } from "@/components/StatusBadge";
import { BulletinsDialog } from "@/components/BulletinsDialog";
import { OptimizeAttributionSheet } from "@/components/OptimizeAttribution";
import { SavedViews } from "@/components/SavedViews";
import { RotationMarker } from "@/components/RotationMarker";
import { CoordinationBoard, buildCoordinationWeek } from "@/components/CoordinationBoard";
import { TeamSheet } from "@/components/TeamSheet";
import { RemerciementDialog } from "@/components/RemerciementDialog";
import { cdpCellStyle, fieldRebookCellStyle, cancelledCellStyle, FIELD_REBOOK_COLOR, frenchDateShort, frenchDate, frenchDateTime, frenchDayChip } from "@/lib/shariti";
import { dispoForDate, dispoBadge, creneauOutsideDispo } from "@/lib/dispo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "sonner";

function toISODate(d) {
  // Formate en YYYY-MM-DD à partir des composantes LOCALES (aucun décalage UTC).
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return toISODate(d);
}
function mondayOf(date) {
  const d = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = lundi
  d.setDate(d.getDate() - day);
  return toISODate(d);
}

// Numéro de semaine ISO (1-53) à partir d'une date ISO (YYYY-MM-DD).
function isoWeekNumber(iso) {
  const d = new Date(iso + "T00:00:00");
  const t = new Date(d.getTime());
  t.setDate(t.getDate() + 3 - ((d.getDay() + 6) % 7)); // jeudi de la semaine
  const week1 = new Date(t.getFullYear(), 0, 4);
  return 1 + Math.round(((t - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

// Normalise un libellé pour comparaison (minuscule, sans accents, tirets/slashes -> espace).
function normLabel(t) {
  return (t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-/]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Retire le préfixe de canal du libellé de lieu pour n'afficher que la zone géographique.
// Ex : "Sites privés Paris IDF" -> "Paris IDF" ; "Porte à Porte IDF" -> "IDF".
function stripCanalFromLieu(rawLieu, canalNom) {
  if (!rawLieu) return "";
  const words = rawLieu.trim().split(/\s+/);
  const normWords = words.map(normLabel);
  // Préfixes de canal connus (mots normalisés), du plus long au plus court.
  const prefixSets = [
    ["porte", "a", "porte"],
    ["sites", "prives"],
    ["rue", "mission"],
    ["pap", "itinerant"],
    ["rue"],
    ["pap"],
  ];
  if (canalNom) {
    const cn = normLabel(canalNom).split(" ").filter(Boolean);
    if (cn.length) prefixSets.push(cn);
  }
  prefixSets.sort((a, b) => b.length - a.length);
  for (const pre of prefixSets) {
    if (
      pre.length &&
      pre.length < words.length &&
      pre.every((w, i) => normWords[i] === w)
    ) {
      // Retire les mots du préfixe + d'éventuels séparateurs orphelins en tête.
      return harmonizeGeo(
        words
          .slice(pre.length)
          .join(" ")
          .replace(/^[\s/&-]+/, "")
          .trim()
      );
    }
  }
  return harmonizeGeo(rawLieu.trim());
}

// Harmonise les libellés de zone (Paris / Île-de-France).
function harmonizeGeo(geo) {
  let g = geo || "";
  g = g.replace(/\bparis\b\s*(?:&|et)?\s*\bidf\b/gi, "Paris et Île-de-France");
  g = g.replace(/\bidf\b/gi, "Île-de-France");
  return g.replace(/\s+/g, " ").trim();
}

// Badge du code campagne d'une ligne — CLIQUABLE / ÉDITABLE (admin/cdp) depuis le planning.
// Le code saisi devient PRIORITAIRE pour CETTE ligne, à partir du mois affiché et vers l'avant
// (les semaines passées conservent leur code). Permet ex. de fixer le vrai code de la mission
// Alsace sans impacter les autres lignes. En lecture seule : simple badge (ou rien si vide).
function CodeCampagneBadge({ ligne, canEdit, weekMonday, onSaved }) {
  const code = ligne.code_campagne || "";
  const hasOverride = !!ligne.code_campagne_override;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(code);
  const [busy, setBusy] = useState(false);

  // Initialise le champ à l'OUVERTURE du popover uniquement. On NE réinitialise PAS
  // pendant la saisie si le planning se rafraîchit en arrière-plan (softLoad) : sinon le
  // code tapé par l'utilisateur serait écrasé par l'ancien code affiché.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setValue(ligne.code_campagne || ""); }, [open]);

  const save = async (nextCode) => {
    setBusy(true);
    try {
      await api.put(`/planning/lignes/${ligne.id}/code-campagne`, {
        code: nextCode, week: weekMonday || undefined,
      });
      toast.success(nextCode
        ? `Code campagne « ${nextCode.toUpperCase()} » enregistré (cette ligne, à partir de ce mois).`
        : "Code campagne réinitialisé (résolution automatique).");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer le code campagne.");
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) {
    if (!code) return null;
    return (
      <span className="inline-flex shrink-0 items-center rounded bg-[rgba(238,114,46,0.12)] px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide text-[var(--shariti-orange)]"
        title="Code campagne" data-testid={`ligne-code-${ligne.id}`}>
        {code}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          title={hasOverride ? "Code campagne fixé manuellement — cliquer pour modifier" : "Cliquer pour définir / modifier le code campagne"}
          data-testid={`ligne-code-edit-${ligne.id}`}
          className={`group inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-extrabold tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] ${code ? "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.2)]" : "border border-dashed border-[rgba(238,114,46,0.4)] text-[rgba(238,114,46,0.75)] hover:bg-[rgba(238,114,46,0.08)]"}`}>
          {code || "+ code"}
          {hasOverride
            ? <Lock className="h-2.5 w-2.5 opacity-70" />
            : <Pencil className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover:opacity-70" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" data-testid={`ligne-code-popover-${ligne.id}`}
        onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-bold text-ink">Code campagne de la ligne</p>
        <p className="mt-0.5 text-[11px] leading-snug text-muted-ink">
          S'applique à <span className="font-semibold">cette ligne</span>, pour la semaine affichée et
          les semaines à venir. Les semaines passées gardent leur code.
        </p>
        <form className="mt-2 flex items-center gap-1.5"
          onSubmit={(e) => { e.preventDefault(); if (value.trim()) save(value.trim()); }}>
          <Input autoFocus value={value}
            onChange={(e) => setValue(e.target.value.toUpperCase())}
            placeholder="Ex : 26SITAA67I"
            className="h-8 font-mono text-xs"
            data-testid={`ligne-code-input-${ligne.id}`} />
          <Button type="submit" size="sm" disabled={busy || !value.trim()}
            className="h-8 shrink-0 bg-[var(--shariti-orange)] px-2 text-white hover:bg-[var(--shariti-orange)]/90"
            data-testid={`ligne-code-save-${ligne.id}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
        </form>
        {hasOverride && (
          <button type="button" onClick={() => save("")} disabled={busy}
            className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink underline-offset-2 transition-colors hover:text-[#D64545] hover:underline"
            data-testid={`ligne-code-reset-${ligne.id}`}>
            <RotateCcw className="h-3 w-3" /> Réinitialiser (code automatique)
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const BOOKING_STATUSES = [  { v: "ferme", l: "Ferme" },
  { v: "au_crayon", l: "Au crayon" },
  { v: "a_confirmer", l: "À confirmer" },
  { v: "proposition_envoyee", l: "Proposition envoyée" },
];

// Libellés FR des statuts (inclut les statuts d'historique importé).
const HIST_STATUS_LABELS = {
  ferme: "Fermé",
  au_crayon: "Au crayon",
  a_confirmer: "À confirmer",
  proposition_envoyee: "Proposition envoyée",
  realise: "Réalisé",
  planifie: "Planifié",
  annule: "Annulé",
};

// Libellés FR des motifs d'annulation d'un créneau.
const ANNUL_MOTIF_LABELS = {
  spot: "Le magasin s'est rétracté",
  nous: "Décision interne",
  meteo: "Météo",
  autre: "Autre",
};

// Alerte de sur-exploitation d'un spot (niveaux renvoyés par le backend).
const OVEREXP = {
  over: { label: "Sur-exploité", cls: "border-[rgba(214,69,69,0.45)] bg-[rgba(214,69,69,0.12)] text-[#9B2226]" },
  warn: { label: "Proche du max", cls: "border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.4)] text-[#8A6D00]" },
};

// Regroupe les créneaux par ligne puis par jour. Décision produit : UNE SEULE rangée
// par ligne — plusieurs spots le même jour sont EMPILÉS dans la même cellule (au lieu
// de dupliquer la ligne en « lanes »). Les cartes d'une cellule sont triées par ``lane``
// (placement manuel éventuel) puis par nom de spot, pour un ordre stable.
function buildLigneRows(lignes, allBookings, days) {
  const dayset = new Set(days.map((d) => d.date));
  const byLigne = {};
  (allBookings || []).forEach((b) => {
    const date = String(b.date).slice(0, 10);
    if (!dayset.has(date)) return;
    (byLigne[b.ligne_id] = byLigne[b.ligne_id] || []).push({ ...b, _date: date });
  });
  return (lignes || []).map((ligne) => {
    const bks = byLigne[ligne.id] || [];
    const byDate = {};
    bks.forEach((b) => { (byDate[b._date] = byDate[b._date] || []).push(b); });
    Object.keys(byDate).forEach((d) => {
      byDate[d].sort((a, b) => {
        const la = Number.isInteger(a.lane) ? a.lane : 999;
        const lb = Number.isInteger(b.lane) ? b.lane : 999;
        return (la - lb) || String(a.spot_name || "").localeCompare(String(b.spot_name || ""));
      });
    });
    return { ligne, byDate };
  });
}



// --- Couverture journalière (barre de progression, vue d'ensemble en haut) ---
// Code couleur (décision utilisateur) : Vert = 100 % couvert · Orange = 50–99 %
// (au moins un trou) · Rouge = < 50 % (beaucoup de trous / rien). Une journée est
// « couverte » pour une ligne dès qu'elle porte au moins un créneau ce jour-là.
// Les jours fériés sont neutres (aucune couverture attendue).
function coverageColor(pct) {
  if (pct >= 1) return "var(--coverage-ok)";
  if (pct >= 0.5) return "var(--coverage-warn)";
  return "var(--coverage-bad)";
}


// --- Rebooking forcé : blocs « pourquoi ? » réutilisés (bandeau rouge + pop-up de conflit) ---
// conflictEvents : passage(s) qui repoussent la disponibilité (asso + date + règle).
// history : 5 derniers passages de l'emplacement (le passage en conflit est surligné).
function RotationHistoryBlocks({ conflictEvents = [], history = [], nextAvailable }) {
  const hasConflict = (conflictEvents || []).length > 0;
  return (
    <div className="space-y-2.5" data-testid="rotation-blocks">
      {hasConflict && (
        <div>
          <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-[#9B2226]">Passage(s) en conflit</div>
          <div className="space-y-1">
            {conflictEvents.map((c, i) => (
              <div key={i} data-testid={`rotation-conflict-${i}`}
                className="flex flex-wrap items-center gap-1.5 rounded-md border border-[rgba(214,69,69,0.45)] bg-[rgba(214,69,69,0.09)] px-2 py-1.5 text-[11px]">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-[#D64545]" />
                <span className="font-bold text-[#9B2226]">{c.association_acronym}</span>
                <span className="text-[#7a2a2d]">· {frenchDate(c.date)}</span>
                {c.iso_week && <span className="rounded bg-[rgba(214,69,69,0.15)] px-1 py-0.5 text-[9px] font-bold text-[#9B2226]">{c.iso_week}</span>}
                <span className="text-muted-ink">— {c.rule_label}</span>
                {c.available_from && <span className="ml-auto font-semibold text-[#9B2226]">Libre dès le {frenchDate(c.available_from)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="mb-1 text-[10px] font-extrabold uppercase tracking-wide text-muted-ink">Historique récent de l'emplacement</div>
        {(history || []).length === 0 ? (
          <div className="text-[11px] text-muted-ink">Aucun autre passage enregistré sur cet emplacement.</div>
        ) : (
          <div className="space-y-1">
            {history.map((h, i) => {
              const isConflict = (conflictEvents || []).some((c) => c.date === h.date && c.association_id === h.association_id);
              return (
                <div key={i} data-testid={`rotation-history-${i}`}
                  className={`flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] ${isConflict ? "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.06)]" : "border-[var(--border-soft)] bg-white"}`}>
                  <span className={`font-bold ${isConflict ? "text-[#9B2226]" : "text-ink"}`}>{h.association_acronym}</span>
                  <span className="text-muted-ink">· {frenchDate(h.date)}</span>
                  {h.iso_week && <span className="rounded bg-[var(--surface-2)] px-1 py-0.5 text-[9px] font-bold text-muted-ink">{h.iso_week}</span>}
                  {h.when === "après" && <span className="text-[9px] font-semibold uppercase text-muted-ink">(à venir)</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {nextAvailable && (
        <div className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-ink" data-testid="rotation-next-available">
          Prochaine disponibilité conforme : <span className="font-bold text-[#9B2226]">{frenchDate(nextAvailable)}</span>
        </div>
      )}
    </div>
  );
}

// --- Contrainte Paris : bloc explicatif (DISTINCT de la rotation) ---
// Utilisé quand le blocage vient de la règle « exploitation Paris = dernière
// semaine du mois » (association avec paris_last_week_constraint) et NON de la
// rotation. On n'affiche donc pas d'historique de rotation ici, qui brouillerait
// le vrai motif.
function ParisConstraintBlock({ reason, nextAvailable }) {
  return (
    <div className="space-y-2" data-testid="paris-constraint-block">
      <div className="flex items-start gap-2 rounded-md border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.06)] px-2.5 py-2 text-[12px] text-[#9B2226]">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#D64545]" />
        <span className="font-semibold">{reason || "Cette association ne peut exploiter Paris que la dernière semaine du mois."}</span>
      </div>
      {nextAvailable && (
        <div className="flex items-center gap-1.5 rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1.5 text-[11px] font-semibold text-ink" data-testid="paris-next-available">
          <CalendarClock className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          Réservable à partir du <span className="font-bold text-[#9B2226]">{frenchDate(nextAvailable)}</span> (dernière semaine du mois).
        </div>
      )}
    </div>
  );
}

function ForcedReasonDetail({ detail }) {
  const nextAvail = detail?.conflict_events?.[0]?.available_from || null;
  const isParis = detail?.forced_type === "paris";
  return (
    <div className="space-y-2" data-testid="forced-reason-detail">
      {isParis
        ? <ParisConstraintBlock reason={detail?.forced_reason} nextAvailable={null} />
        : <RotationHistoryBlocks conflictEvents={detail?.conflict_events} history={detail?.history} nextAvailable={nextAvail} />}
      {detail?.forced_by_user_name && (
        <div className="text-[10px] text-muted-ink">
          Forcé par <span className="font-semibold text-ink">{detail.forced_by_user_name}</span>
          {detail.forced_at ? ` le ${frenchDate(String(detail.forced_at).slice(0, 10))}` : ""}.
        </div>
      )}
    </div>
  );
}


export default function Planning() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // --- Capacités d'édition (rôle « Lecture Seule » à permissions configurables) ---
  // Pour admin / cdp / chef : comportement historique inchangé.
  // Pour lecture_seule : chaque action n'est autorisée que si l'admin l'a activée.
  const role = user?.role;
  const isViewer = role === "lecture_seule";
  // Responsable d'équipe terrain / chef d'équipe : accès au planning en LECTURE SEULE
  // (voir l'avancée, repérer et anticiper les trous). Aucune édition/déplacement.
  const isFieldLead = role === "terrain" || role === "chef";
  const vperm = user?.permissions || {};
  const canEditSpots = (!isViewer && !isFieldLead) || (isViewer && !!vperm.edit_spots);       // ajouter/retirer/modifier un créneau
  const canDragDrop = (!isViewer && !isFieldLead) || (isViewer && !!vperm.drag_drop);          // déplacer un créneau / réorganiser les lignes
  const canNewLigne = role === "admin" || role === "cdp" || (isViewer && !!vperm.manage_lines);
  const canDeleteLigne = role === "admin" || (isViewer && !!vperm.manage_lines);
  const canEditPeriod = role === "admin" || (isViewer && !!vperm.edit_period);
  const canRenameReassign = role === "admin";                 // renommer équipe / réattribuer (admin only)
  const showLigneMenu = canDeleteLigne || canEditPeriod || canRenameReassign;
  const canDiffuse = role === "admin" || (isViewer && !!vperm.diffuse);
  const canDeclare = role === "admin" || (isViewer && !!vperm.declare_coordination);
  // Gestion de l'équipe du jour (pointage) — admin/cdp, OU « lecture seule » à qui
  // l'admin a explicitement accordé la permission « manage_equipe » (bouton « Équipe »).
  const canManageTeam = role === "admin" || role === "cdp" || (isViewer && !!vperm.manage_equipe);
  // Distribution IA + Journal IA : réservés à l'ADMIN et à la « chargée des opérations »
  // (lecture seule avec « manage_equipe »). Les CDP n'y ont PAS accès (décision métier).
  const canAutoDistribute = role === "admin" || (isViewer && !!vperm.manage_equipe);
  // Blacklister un spot (admin + CDP, ou « lecture seule » avec droit edit_spots).
  const canBlacklist = role === "admin" || role === "cdp" || (isViewer && !!vperm.edit_spots);
  const [view, setView] = useState("grid"); // grid | coordination (onglet Suivi coordination)
  // --- UX mobile : bascule Jour/Semaine (uniquement < md). Défaut « Jour » (1 colonne
  // lisible + drop large au doigt). `dayIndex` = jour sélectionné en mode Jour (null =
  // auto : aujourd'hui si dans la semaine, sinon 1er jour). ---
  const isMobile = useIsMobile();
  const [weekMode, setWeekMode] = useState("jour");
  const [dayIndex, setDayIndex] = useState(null);
  const [monday, setMonday] = useState(() => {
    const wk = searchParams.get("week");
    return wk && /^\d{4}-\d{2}-\d{2}$/.test(wk) ? mondayOf(wk) : mondayOf(new Date());
  });
  const [weekPickerOpen, setWeekPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoneMap, setZoneMap] = useState(null); // rappel discret CDP ↔ zone (figé en haut)
  const [assignCtx, setAssignCtx] = useState(null); // {ligne, date}
  const [conflict, setConflict] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [spotSearch, setSpotSearch] = useState("");
  const [spotResults, setSpotResults] = useState([]);
  const [showSunday, setShowSunday] = useState(false);
  const [bulletinsBk, setBulletinsBk] = useState(null);
  const [diffusionOpen, setDiffusionOpen] = useState(false);
  const [confirmFerme, setConfirmFerme] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // garde-fou suppression d'un créneau confirmé/trouvé
  const [trashOpen, setTrashOpen] = useState(false);         // corbeille des créneaux (suppression douce)
  const [trashData, setTrashData] = useState(null);          // { count, items } | null
  const [trashBusyId, setTrashBusyId] = useState("");        // id en cours (restaurer / supprimer définitivement)
  const [ferming, setFerming] = useState(false);
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [fitOpen, setFitOpen] = useState(false);   // assistant « où placer un collaborateur ? »
  const [autoDistOpen, setAutoDistOpen] = useState(false);   // distribution IA de la semaine
  const [iaJournalOpen, setIaJournalOpen] = useState(false); // journal IA (historique des distributions)
  const [geocodingWeek, setGeocodingWeek] = useState(false);
  // --- Historique de session : Annuler / Rétablir (drag&drop, création, suppression, CDP) ---
  // Pile de commandes réversibles ({label, undo, redo}). Session uniquement :
  // remise à zéro au rechargement de la semaine / de la page (choix utilisateur).
  const [history, setHistory] = useState({ past: [], future: [] });
  const suppressHistory = useRef(false); // vrai pendant un undo/redo (n'enregistre pas)
  const undoingRef = useRef(false);       // évite les doubles déclenchements
  const historyRef = useRef({ past: [], future: [] }); // miroir pour les raccourcis clavier
  const undoRef = useRef(() => {});       // dernières fonctions undo/redo (raccourcis clavier)
  const redoRef = useRef(() => {});
  const [highlightBookings, setHighlightBookings] = useState({}); // booking_id -> true (surbrillance après optimisation)
  // --- Rebooking forcé : historique dépliable dans le bandeau rouge (chargé à la demande) ---
  const [forcedOpen, setForcedOpen] = useState({});       // booking_id -> déplié ?
  const [forcedDetails, setForcedDetails] = useState({}); // booking_id -> {loading|data|error}
  const toggleForcedDetail = useCallback(async (id) => {
    setForcedOpen((o) => ({ ...o, [id]: !o[id] }));
    // Chargement paresseux + cache : on ne rappelle pas l'API si déjà chargé.
    setForcedDetails((prev) => {
      if (prev[id]?.data || prev[id]?.loading) return prev;
      (async () => {
        try {
          const { data } = await api.get(`/planning/booking/${id}/rotation`);
          setForcedDetails((d) => ({ ...d, [id]: { loading: false, data } }));
        } catch (_) {
          setForcedDetails((d) => ({ ...d, [id]: { loading: false, error: true } }));
        }
      })();
      return { ...prev, [id]: { loading: true } };
    });
  }, []);
  const [pendingSpot, setPendingSpot] = useState(null); // spot à placer (deep-link depuis la fiche)
  const [reassignLigne, setReassignLigne] = useState(null); // #4 — réattribution de ligne à un CDP
  const [changeAssocLigne, setChangeAssocLigne] = useState(null); // bascule de ligne vers une autre association
  const [teamBooking, setTeamBooking] = useState(null);   // booking dont on gère l'équipe du jour (panneau)
  const [thanksSpot, setThanksSpot] = useState(null);     // spot (hydraté) à remercier depuis le planning
  const [thanksAssoc, setThanksAssoc] = useState(null);   // association du créneau remercié (branding email)
  const [thanksBusyId, setThanksBusyId] = useState("");   // booking en cours de chargement pour remerciement
  const [teamCounts, setTeamCounts] = useState({});       // booking_id -> nb d'affectations (badge « Équipe (N) »)
  const [renameLigne, setRenameLigne] = useState(null); // nommer / renommer l'équipe depuis le planning
  const [deleteLigneCtx, setDeleteLigneCtx] = useState(null); // suppression d'une ligne (choix de portée)
  const [editPeriodLigne, setEditPeriodLigne] = useState(null); // modification de la période (début/fin)
  const [cdpFilter, setCdpFilter] = useState(() => {
    try { const s = localStorage.getItem("shariti_planning_cdp_filter"); return s ? JSON.parse(s) : null; }
    catch { return null; }
  }); // null = auto (CDP actifs uniquement) ; sinon tableau d'ids sélectionnés
  // Trace LOCALE (par appareil) de la dernière activation du filtre CDP : qui et quand.
  // Sert à rassurer l'utilisateur (« ce filtre est personnel à cet appareil, rien n'est
  // supprimé ni masqué pour les autres »). Jamais envoyée au serveur (choix d'affichage).
  const [cdpFilterAudit, setCdpFilterAudit] = useState(() => {
    try { const s = localStorage.getItem("shariti_planning_cdp_filter_audit"); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });
  // Filtre « Ligne » : permet de n'afficher QU'UNE seule ligne (focus), sans scroller.
  // null = toutes les lignes. Mémorisé par appareil pour rester pratique entre visites.
  const [lineFilter, setLineFilter] = useState(() => {
    try { return localStorage.getItem("shariti_planning_line_filter") || null; }
    catch { return null; }
  });
  const [lineFilterOpen, setLineFilterOpen] = useState(false);

  // --- Recherche d'historique de spot (barre en haut du planning) ---
  const [histQuery, setHistQuery] = useState("");
  const [histResults, setHistResults] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [histExpanded, setHistExpanded] = useState(null); // id du spot déplié
  const [histAssocFilter, setHistAssocFilter] = useState(null); // filtre association dans l'historique
  const [histMaxPerYear, setHistMaxPerYear] = useState(6); // plafond conseillé /an/association

  // Liste des chefs d'équipe (référentiel) — pour l'autocomplétion du "responsable terrain".
  const [chefs, setChefs] = useState([]);
  useEffect(() => {
    api.get("/chefs-equipe").then((r) => setChefs((r.data || []).filter((c) => c.active !== false))).catch(() => {});
  }, []);
  // Référentiels pour la création de ligne depuis le planning (associations + canaux).
  const [associations, setAssociations] = useState([]);
  const [canaux, setCanaux] = useState([]);
  const [newLigneOpen, setNewLigneOpen] = useState(false);
  useEffect(() => {
    api.get("/associations").then((r) => setAssociations((r.data || []).filter((a) => a.active !== false))).catch(() => {});
    api.get("/canaux").then((r) => setCanaux((r.data || []).filter((c) => c.active !== false))).catch(() => {});
  }, []);
  // Création rapide d'un responsable (si absent de la liste) directement depuis le champ.
  const createChef = async (nom) => {
    const { data } = await api.post("/chefs-equipe", { nom });
    setChefs((prev) => (prev.some((c) => c.id === data.id) ? prev : [...prev, data].sort((a, b) => (a.nom || "").localeCompare(b.nom || ""))));
    return data;
  };
  // Complète la fiche d'un responsable (email/téléphone) juste après sa création.
  const updateChef = async (id, patch) => {
    const { data } = await api.patch(`/chefs-equipe/${id}`, patch);
    setChefs((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    return data;
  };

  // Deep-link "?spot=<id>" : pré-charge le spot à placer et ouvre le panneau
  useEffect(() => {
    const sid = searchParams.get("spot");
    if (!sid) return;
    api.get(`/spots/${sid}`).then((r) => {
      setPendingSpot(r.data);
      setPanelOpen(true);
      setSpotSearch(r.data.normalized_name || "");
      toast.success(`Placez « ${r.data.normalized_name} » sur un créneau libre du planning`);
    }).catch(() => {});
    searchParams.delete("spot");
    setSearchParams(searchParams, { replace: true });
  }, []); // eslint-disable-line

  // Recherche d'historique de spot (debounce 300ms) via /planning/spot-search
  useEffect(() => {
    const q = histQuery.trim();
    if (q.length < 2) { setHistResults([]); setHistLoading(false); return; }
    setHistLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/planning/spot-search", { params: { q, limit: 8 } });
        const results = data.results || [];
        setHistResults(results);
        setHistMaxPerYear(data.max_per_year || 6);
        setHistExpanded(results[0]?.id || null); // 1er résultat déplié par défaut
        setHistAssocFilter(null);
        setHistOpen(true);
      } catch { setHistResults([]); }
      finally { setHistLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [histQuery]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/planning", { params: { week: monday } });
      setData(data);
    } finally { setLoading(false); }
  }, [monday]);

  // Rechargement SILENCIEUX : resynchronise les données sans repasser en état "loading"
  // (donc sans skeleton/flash). Utilisé après les mises à jour optimistes.
  const softLoad = useCallback(async () => {
    try {
      const { data } = await api.get("/planning", { params: { week: monday } });
      setData(data);
    } catch { /* silencieux : l'état optimiste reste affiché */ }
  }, [monday]);

  // ── Synchro PREREC (collaborateurs) ─────────────────────────────────────────
  // Fait remonter les RD fraîchement onboardés dans PREREC SANS quitter le planning.
  // Admin uniquement (même périmètre que le référentiel PREREC de l'écran Pointage).
  const [syncingRds, setSyncingRds] = useState(false);
  const syncRds = useCallback(async () => {
    setSyncingRds(true);
    try {
      const { data: r } = await api.post("/pointage/sync");
      if (r?.ok) {
        const created = r.created ?? 0;
        toast.success(`Synchro PREREC : ${r.matched ?? 0} rapproché(s), ${created} créé(s).`);
        if (created > 0) await softLoad();
      } else {
        toast.warning(r?.message || "Synchro PREREC indisponible (jeton non configuré).");
      }
    } catch { toast.error("Synchro PREREC impossible."); }
    finally { setSyncingRds(false); }
  }, [softLoad]);

  // Synchro AUTOMATIQUE à l'ouverture du planning (admin) : throttlée côté serveur, donc
  // sans surcoût si une synchro récente existe. Objectif : zéro clic pour voir remonter
  // un collaborateur onboardé dans PREREC.
  useEffect(() => {
    if (role !== "admin") return;
    let alive = true;
    (async () => {
      try {
        const { data: r } = await api.post("/pointage/sync/auto");
        if (alive && r?.ok && !r?.skipped && (r.created || 0) > 0) {
          toast.success(`${r.created} collaborateur(s) synchronisé(s) depuis PREREC.`);
          await softLoad();
        }
      } catch { /* silencieux : non bloquant pour le planning */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // 1 clic : renseigne AUTOMATIQUEMENT les GPS manquants de tous les spots
  // planifiés cette semaine (débloque le pointage). N'affecte ni les heures ni
  // les équipes ; les spots déjà géolocalisés ne sont pas touchés.
  const geocodeMissingWeek = useCallback(async () => {
    if (geocodingWeek) return;
    setGeocodingWeek(true);
    const tId = toast.loading("Recherche des spots sans GPS de la semaine…");
    try {
      const { data: res } = await api.post("/spots/geocode-missing", {
        date_from: monday, date_to: addDays(monday, 6),
      });
      toast.dismiss(tId);
      if (!res.candidates) {
        toast.success("Aucun spot sans GPS cette semaine — tout est déjà géolocalisé.");
      } else {
        toast.success(
          `${res.updated} GPS renseigné(s)${res.failed ? `, ${res.failed} échec(s)` : ""} sur ${res.candidates} spot(s) sans position.`
        );
      }
      await softLoad();
    } catch (e) {
      toast.dismiss(tId);
      toast.error(e?.response?.data?.detail || "Correction GPS impossible.");
    } finally { setGeocodingWeek(false); }
  }, [geocodingWeek, monday, softLoad]);

  useEffect(() => { load(); }, [load]);

  // Rappel territoires (chargé une fois, indépendant de la semaine affichée).
  useEffect(() => {
    api.get("/dashboard/zone-map").then((r) => setZoneMap(r.data)).catch(() => {});
  }, []);

  const crayonCount = useMemo(
    () => (data?.bookings || []).filter((b) => b.status === "au_crayon" && !b.is_historical).length,
    [data]
  );
  // Suivi coordination (onglet) : dérive la structure jour par jour depuis les
  // bookings déjà chargés — reste synchronisé avec la grille via softLoad().
  const coordinationWeek = useMemo(
    () => buildCoordinationWeek(data?.bookings || [], data?.days || [], data?.lignes || []),
    [data]
  );
  const confirmFermeWeek = async () => {
    setFerming(true);
    try {
      const { data: res } = await api.post("/planning/bookings/bulk-status", {
        week: monday, from_status: "au_crayon", to_status: "ferme",
      });
      toast.success(`${res.updated} créneau(x) confirmé(s) en Ferme`);
      setConfirmFerme(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la confirmation");
    } finally { setFerming(false); }
  };

  // spots search for the side panel + assign dialog
  useEffect(() => {
    const t = setTimeout(async () => {
      const { data } = await api.get("/spots", { params: { search: spotSearch || undefined, limit: 40 } });
      setSpotResults(data);
    }, 250);
    return () => clearTimeout(t);
  }, [spotSearch]);

  // Repère de rotation « en amont » pour les spots du panneau (avant booking).
  // Chargé en lot quand la liste change ; la date de référence = lundi affiché.
  const [spotRotation, setSpotRotation] = useState({});
  useEffect(() => {
    const ids = (spotResults || []).map((s) => s.id).filter(Boolean);
    if (!panelOpen || ids.length === 0) { setSpotRotation({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/planning/spots-rotation-status", { spot_ids: ids, ref_date: monday });
        if (!cancelled) setSpotRotation(data.by_spot_id || {});
      } catch (_) { if (!cancelled) setSpotRotation({}); }
    })();
    return () => { cancelled = true; };
  }, [spotResults, panelOpen, monday]);

  const bookingsByCell = useMemo(() => {
    const map = {};
    (data?.bookings || []).forEach((b) => {
      const key = `${b.ligne_id}__${String(b.date).slice(0, 10)}`;
      (map[key] = map[key] || []).push(b);
    });
    return map;
  }, [data]);

  // Compteurs « Équipe (N) » : nombre d'affectations pointage par booking (badge sur les cartes).
  const refreshTeamCounts = useCallback(async (bookings) => {
    if (!canManageTeam) return;
    const ids = (bookings || [])
      .filter((b) => b && b.id && !b.is_historical && !b.readonly)
      .map((b) => b.id);
    if (!ids.length) { setTeamCounts({}); return; }
    try {
      const { data: counts } = await api.post("/pointage/affectations/counts", { booking_ids: ids });
      setTeamCounts(counts || {});
    } catch { /* silencieux : le badge restera vide */ }
  }, [canManageTeam]);
  useEffect(() => { refreshTeamCounts(data?.bookings); }, [data?.bookings, refreshTeamCounts]);

  // UX mobile : recentre automatiquement le chip du jour sélectionné dans sa barre
  // défilante — SCOPÉ à cette barre uniquement (scrollTo), jamais scrollIntoView qui
  // ferait défiler les conteneurs parents (et décalerait toute la page).
  useEffect(() => {
    if (!(isMobile && weekMode === "jour")) return undefined;
    const id = requestAnimationFrame(() => {
      const container = document.querySelector('[data-testid="planning-day-chips"]');
      const el = container && container.querySelector('[data-selected="true"]');
      if (container && el) {
        const target = el.offsetLeft - (container.clientWidth - el.offsetWidth) / 2;
        container.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [isMobile, weekMode, dayIndex, data]);

  /* ============================================================== */
  /* Historique de session : Annuler / Rétablir                     */
  /* ============================================================== */
  // Miroir de l'historique pour les raccourcis clavier (closures stables).
  useEffect(() => { historyRef.current = history; }, [history]);
  // Portée « session » : on repart d'une pile vide à chaque changement de semaine
  // (les commandes réfèrent des créneaux de la semaine affichée).
  useEffect(() => { setHistory({ past: [], future: [] }); }, [monday]);

  // Enregistre une commande réversible (ignorée pendant un undo/redo).
  const recordCommand = (cmd) => {
    if (suppressHistory.current) return;
    setHistory((h) => ({ past: [...h.past, cmd].slice(-80), future: [] }));
  };

  // Opérations « brutes » (sans toast, force=true) réutilisées par Annuler/Rétablir.
  // Elles n'utilisent que des références stables (api / setData / softLoad).
  const opMove = async (id, ligne_id, date, lane = null) => {
    const newLane = Number.isInteger(lane) ? lane : null;
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).map((b) => (b.id === id ? { ...b, ligne_id, date, lane: newLane } : b)) } : d));
    await api.put(`/planning/booking/${id}`, { ligne_id, date, force: true, lane: newLane });
    await softLoad();
  };
  const opAssign = async (args) => {
    const { data: res } = await api.post("/planning/assign", { force: true, ...args });
    await softLoad();
    return res?.id;
  };
  const opDelete = async (id) => {
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).filter((b) => b.id !== id) } : d));
    await api.post(`/planning/booking/${id}/trash`);
    await softLoad();
  };
  const opRestoreTrash = async (id) => {
    await api.post(`/planning/booking/${id}/restore-from-trash`);
    await softLoad();
  };
  const opSetCdp = async (id, cdp) => {
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).map((b) => (b.id === id ? { ...b, cdp_id: cdp?.id, cdp: cdp?.name ? { name: cdp.name, color: cdp.color } : b.cdp } : b)) } : d));
    await api.put(`/planning/booking/${id}/cdp`, { cdp_id: cdp?.id });
    await softLoad();
  };

  const undo = async () => {
    const h = historyRef.current;
    if (undoingRef.current || h.past.length === 0) return;
    const cmd = h.past[h.past.length - 1];
    undoingRef.current = true; suppressHistory.current = true;
    try {
      await cmd.undo();
      setHistory((cur) => ({ past: cur.past.slice(0, -1), future: [...cur.future, cmd] }));
      toast.success(`Annulé : ${cmd.label}`);
    } catch (e) {
      toast.error("Annulation impossible");
      softLoad();
    } finally { suppressHistory.current = false; undoingRef.current = false; }
  };
  const redo = async () => {
    const h = historyRef.current;
    if (undoingRef.current || h.future.length === 0) return;
    const cmd = h.future[h.future.length - 1];
    undoingRef.current = true; suppressHistory.current = true;
    try {
      await cmd.redo();
      setHistory((cur) => ({ past: [...cur.past, cmd], future: cur.future.slice(0, -1) }));
      toast.success(`Rétabli : ${cmd.label}`);
    } catch (e) {
      toast.error("Rétablissement impossible");
      softLoad();
    } finally { suppressHistory.current = false; undoingRef.current = false; }
  };

  // Raccourcis clavier : Ctrl/Cmd+Z = annuler · Ctrl/Cmd+Maj+Z ou Ctrl+Y = rétablir.
  undoRef.current = undo; redoRef.current = redo;
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = (e.key || "").toLowerCase();
      if (key === "z" && !e.shiftKey) { e.preventDefault(); undoRef.current(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); redoRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const doAssign = async ({ spot_id, ligne_id, date, status, consignes, force = false, field_rebooking = false, field_rebooked_by, credited_cdp_id, lane = null }) => {
    const newLane = Number.isInteger(lane) ? lane : null;
    try {
      const { data: res } = await api.post("/planning/assign", { spot_id, ligne_id, date, status, consignes, force, field_rebooking, field_rebooked_by, credited_cdp_id, lane: newLane });
      toast.success(field_rebooking ? "Spot rebooké (terrain) ajouté au planning" : "Spot assigné au planning");
      // Remplissage auto de l'équipe depuis les disponibilités déclarées par le responsable.
      const nAuto = res?.autofilled?.created_count || 0;
      if (nAuto > 0) {
        toast.success(`${nAuto} collaborateur(s) placé(s) automatiquement depuis les disponibilités`);
      }
      setAssignCtx(null); setConflict(null); setPendingSpot(null);
      softLoad();
      // Historique : la création est annulable (suppression du créneau créé).
      if (!suppressHistory.current && res?.id) {
        const holder = { id: res.id };
        const assignArgs = { spot_id, ligne_id, date: String(date).slice(0, 10), status, consignes, field_rebooking, field_rebooked_by, credited_cdp_id, lane: newLane };
        recordCommand({
          label: "ajout d'un créneau",
          undo: () => opDelete(holder.id),
          redo: async () => { const nid = await opAssign(assignArgs); if (nid) holder.id = nid; },
        });
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.rule_conflict) {
        setConflict({ type: "assign", spot_id, ligne_id, date, status, consignes, force, field_rebooking, field_rebooked_by, credited_cdp_id, lane: newLane, reason: d.reason, next: d.next_available, history: d.history, conflict_events: d.conflict_events, conflict_type: d.conflict_type, paris_next_available: d.paris_next_available });
      } else {
        toast.error(typeof d === "string" ? d : "Erreur lors de l'assignation");
      }
    }
  };

  // Duplique un créneau existant sur d'autres jours de la SEMAINE AFFICHÉE en 1 clic.
  // On réutilise le même spot / ligne / statut / consignes / tag terrain. Comme il
  // s'agit de la même opération hebdomadaire (même spot + même asso, même semaine),
  // le backend l'autorise sans contrôle de rotation (force=true = filet de sécurité).
  const duplicateBooking = async (b, dates) => {
    if (!b || !Array.isArray(dates) || dates.length === 0) return;
    let ok = 0, fail = 0;
    for (const date of dates) {
      try {
        await api.post("/planning/assign", {
          spot_id: b.spot_id,
          ligne_id: b.ligne_id,
          date,
          status: b.status,
          consignes: b.consignes || undefined,
          force: true,
          field_rebooking: !!b.field_rebooking,
          field_rebooked_by: b.field_rebooking ? (b.field_rebooked_by || undefined) : undefined,
        });
        ok += 1;
      } catch (_) { fail += 1; }
    }
    if (ok > 0) {
      toast.success(`« ${b.spot_name} » dupliqué sur ${ok} jour${ok > 1 ? "s" : ""}${fail ? ` — ${fail} échec(s)` : ""}`);
    } else {
      toast.error("Duplication impossible");
    }
    softLoad();
  };

  // Définit le contact du magasin ayant AUTORISÉ ce créneau (snapshot stable).
  // `contact = null` réinitialise l'affichage au contact principal du spot.
  const setBookingContact = async (b, contact) => {
    const label = (c) => (c ? [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim() : "");
    const principal = (b.spot_contacts || []).find((c) => c.is_principal) || null;
    const shown = contact || principal;
    setData((d) => {
      if (!d) return d;
      const bookings = (d.bookings || []).map((x) => x.id === b.id ? {
        ...x,
        authorized_contact: contact || null,
        spot_responsable: label(shown) || null,
        spot_responsable_fonction: (shown && shown.fonction) || null,
        spot_contact: label(shown) || null,
      } : x);
      return { ...d, bookings };
    });
    try {
      await api.put(`/planning/booking/${b.id}/contact`, { contact: contact || null });
      toast.success(contact ? `Contact qui a autorisé : ${label(contact)}` : "Contact réinitialisé (contact principal)");
      softLoad();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification impossible");
      softLoad();
    }
  };

  // Crée un NOUVEAU contact (enregistré durablement sur le spot) et l'applique
  // comme contact ayant autorisé ce créneau.
  const createBookingContact = async (b, form) => {
    const label = (c) => (c ? [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim() : "");
    const clean = {
      civilite: (form.civilite || "").trim() || undefined,
      prenom: (form.prenom || "").trim() || undefined,
      nom: (form.nom || "").trim() || undefined,
      fonction: (form.fonction || "").trim() || undefined,
      telephone: (form.telephone || "").trim() || undefined,
      email: (form.email || "").trim() || undefined,
    };
    if (!label(clean) && !clean.telephone && !clean.email) {
      toast.error("Renseignez au moins un nom, un téléphone ou un e-mail.");
      return false;
    }
    try {
      const { data: res } = await api.post(`/planning/booking/${b.id}/contact/new`, clean);
      const saved = res?.authorized_contact || clean;
      setData((d) => {
        if (!d) return d;
        const bookings = (d.bookings || []).map((x) => x.id === b.id ? {
          ...x,
          authorized_contact: saved,
          spot_responsable: res?.spot_responsable || label(saved) || null,
          spot_responsable_fonction: res?.spot_responsable_fonction || saved.fonction || null,
          spot_contact: res?.spot_responsable || label(saved) || null,
          // On l'ajoute à la liste des contacts du spot (réutilisable sans recharger).
          spot_contacts: [...(x.spot_contacts || []), { ...saved, is_principal: false }],
        } : x);
        return { ...d, bookings };
      });
      toast.success(`Contact « ${label(saved) || saved.telephone} » ajouté et appliqué au créneau`);
      softLoad();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création du contact impossible");
      return false;
    }
  };

  // Change le CDP « qui a réservé » un créneau ET adapte sa couleur (choix explicite).
  const setBookingCdp = async (b, cdp) => {
    if (!cdp) return;
    const oldCdpId = b.cdp_id;
    const oldCdp = b.cdp?.name ? { id: oldCdpId, name: b.cdp.name, color: b.cdp.color } : (oldCdpId ? { id: oldCdpId } : null);
    setData((d) => {
      if (!d) return d;
      const bookings = (d.bookings || []).map((x) => x.id === b.id ? {
        ...x, cdp_id: cdp.id, cdp: { name: cdp.name, color: cdp.color },
      } : x);
      return { ...d, bookings };
    });
    try {
      await api.put(`/planning/booking/${b.id}/cdp`, { cdp_id: cdp.id });
      toast.success(`Réservé par : ${cdp.name}`);
      softLoad();
      // Historique : réassignation du CDP « qui a réservé » (annulable).
      if (!suppressHistory.current && oldCdp && oldCdpId !== cdp.id) {
        recordCommand({
          label: `réservation par « ${cdp.name} »`,
          undo: () => opSetCdp(b.id, oldCdp),
          redo: () => opSetCdp(b.id, { id: cdp.id, name: cdp.name, color: cdp.color }),
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification impossible");
      softLoad();
    }
  };

  // Modifie les consignes d'un créneau (carte planning) → reprises en diffusion.
  const setBookingConsignes = async (b, consignes) => {
    const val = (consignes || "").trim() || null;
    setData((d) => {
      if (!d) return d;
      const bookings = (d.bookings || []).map((x) => x.id === b.id
        ? { ...x, consignes: val, consignes_snapshot: val } : x);
      return { ...d, bookings };
    });
    try {
      await api.put(`/planning/booking/${b.id}/consignes`, { consignes: val });
      toast.success("Consignes mises à jour");
      softLoad();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Modification impossible");
      softLoad();
    }
  };

  const deleteBooking = async (id) => {
    const snapshot = data;
    const b = (data?.bookings || []).find((x) => x.id === id);
    // Optimiste : on retire la carte immédiatement.
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).filter((x) => x.id !== id) } : d));
    try {
      await api.post(`/planning/booking/${id}/trash`);
      toast.success("Créneau mis à la corbeille (restaurable)");
      softLoad();
      // Historique : la mise à la corbeille est réversible — on restaure le MÊME créneau.
      if (!suppressHistory.current && b) {
        recordCommand({
          label: `suppression de « ${b.spot_name || "créneau"} »`,
          undo: () => opRestoreTrash(id),
          redo: () => opDelete(id),
        });
      }
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Suppression impossible");
    }
  };

  // Garde-fou anti-mauvaise manipulation : un créneau CONFIRMÉ/TROUVÉ (ferme, confirmé
  // par le responsable, ou déclaré à la coordination) exige une confirmation explicite,
  // et sa suppression est réservée à la direction (admin). Les créneaux « au crayon »
  // partent directement à la corbeille (réversible).
  const requestDeleteBooking = (id) => {
    const b = (data?.bookings || []).find((x) => x.id === id);
    const isProtected = !!b && (
      (b.status && b.status !== "au_crayon" && b.status !== "annule") ||
      b.director_confirmed || b.declared_to_coordination
    );
    if (!isProtected) { deleteBooking(id); return; }
    setConfirmDelete({ id, booking: b, blocked: role !== "admin" });
  };

  // --- Corbeille des créneaux (suppression douce, réversible) ---
  const loadTrash = async () => {
    setTrashData(null);
    try {
      const { data } = await api.get("/planning/trash", { params: { limit: 300 } });
      setTrashData(data);
    } catch { setTrashData({ count: 0, items: [] }); toast.error("Chargement de la corbeille impossible."); }
  };
  useEffect(() => { if (trashOpen) loadTrash(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trashOpen]);

  const restoreFromTrash = async (id) => {
    setTrashBusyId(id);
    try {
      await api.post(`/planning/booking/${id}/restore-from-trash`);
      toast.success("Créneau restauré");
      await loadTrash();
      softLoad();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restauration impossible"); }
    finally { setTrashBusyId(""); }
  };
  const permanentDelete = async (id) => {
    setTrashBusyId(id);
    try {
      await api.delete(`/planning/booking/${id}`);
      toast.success("Créneau supprimé définitivement");
      await loadTrash();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
    finally { setTrashBusyId(""); }
  };

  // Valider une exception de rebooking forcé : on garde le booking, on retire l'alerte rouge.
  const validateForced = async (id) => {
    const snapshot = data;
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).map((b) => (b.id === id ? { ...b, rebooking_forced: false } : b)) } : d));
    try {
      await api.put(`/planning/booking/${id}`, { clear_forced: true });
      toast.success("Exception validée — alerte retirée");
      softLoad();
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Validation impossible");
    }
  };

  // Refuser une exception : on ARCHIVE le créneau (avec motif) au lieu de supprimer,
  // pour tracer dans le Journal des exceptions. Ouvre un dialog de saisie du motif.
  const [refuseCtx, setRefuseCtx] = useState(null); // {id, spot, iso}
  const [refuseReason, setRefuseReason] = useState("");
  const [refusing, setRefusing] = useState(false);
  const submitRefuse = async () => {
    if (!refuseCtx) return;
    setRefusing(true);
    const id = refuseCtx.id;
    try {
      await api.post(`/planning/booking/${id}/refuse-forced`, { reason: refuseReason.trim() || undefined });
      // Le créneau passe en "annule" -> il disparaît du planning.
      setData((d) => (d ? { ...d, bookings: (d.bookings || []).filter((b) => b.id !== id) } : d));
      toast.success("Rebooking refusé et archivé dans le journal");
      setRefuseCtx(null); setRefuseReason("");
      softLoad();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refus impossible");
    } finally { setRefusing(false); }
  };

  const changeStatus = async (id, status) => {
    const snapshot = data;
    // Optimiste : mise à jour immédiate du statut (le badge lit b.status).
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).map((b) => (b.id === id ? { ...b, status } : b)) } : d));
    try {
      await api.put(`/planning/booking/${id}`, { status });
      toast.success("Statut mis à jour");
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Mise à jour impossible");
    }
  };

  // Remercier un magasin directement depuis un créneau passé du planning :
  // on hydrate la fiche complète du spot (contacts) puis on ouvre le dialogue
  // de remerciement (mail brandé + CTA « prochaines dates »).
  const openThanks = async (b) => {
    if (!b?.spot_id) { toast.error("Ce créneau n'est pas rattaché à un magasin"); return; }
    setThanksBusyId(b.id);
    try {
      const { data: spot } = await api.get(`/spots/${b.spot_id}`);
      // On mémorise l'association du créneau remercié : l'email sera brandé à ses
      // couleurs (logo + signature), comme les mails de confirmation/proposition.
      setThanksAssoc(b.association_id || null);
      setThanksSpot(spot);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger la fiche du magasin");
    } finally {
      setThanksBusyId("");
    }
  };

  // Annuler un créneau SANS le supprimer (admin) : il reste visible mais barré,
  // conservé pour l'historique et exclu de la facturation/stats. Réversible.
  const cancelBooking = async (id, motif, note) => {
    const snapshot = data;
    const nowIso = new Date().toISOString();
    setData((d) => (d ? {
      ...d,
      bookings: (d.bookings || []).map((b) => (b.id === id ? {
        ...b, status: "annule", annule_visible: true,
        status_before_annule: b.status_before_annule || b.status,
        annule_by: user?.name || b.annule_by, annule_at: nowIso, annulation_motif: motif || "autre",
      } : b)),
    } : d));
    try {
      await api.post(`/planning/booking/${id}/cancel`, { motif: motif || undefined, note: note || undefined });
      toast.success("Créneau annulé (conservé, barré)");
      softLoad();
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Annulation impossible");
    }
  };

  // Changer le MAGASIN d'un créneau EN PLACE (correction d'erreur : mauvais magasin)
  // SANS perdre les heures pointées ni les bulletins (même booking_id conservé).
  const changeBookingSpot = async (id, spot_id, motif) => {
    try {
      const { data: res } = await api.put(`/planning/booking/${id}/spot`, { spot_id, motif: motif || undefined });
      if (res?.already) { toast.info("C'est déjà ce magasin."); return true; }
      toast.success(`Magasin changé → ${res?.new_spot || "nouveau magasin"}`, {
        description: "Heures et bulletins conservés.",
      });
      softLoad();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Changement de magasin impossible");
      return false;
    }
  };

  // Blacklister un spot EN UN CLIC (admin + CDP) : il disparaît de la prospection,
  // de la recherche de spots et n'est plus réservable. Réversible (dé-blacklistage).
  const blacklistSpot = async (spot_id, spot_name, blacklist = true) => {
    if (!spot_id) { toast.error("Ce créneau n'est pas rattaché à un magasin."); return false; }
    try {
      await api.post(`/spots/${spot_id}/blacklist`, { blacklist });
      toast.success(blacklist
        ? `« ${spot_name || "Spot"} » blacklisté — plus réservable ni en prospection. À réactiver dans Prospection ▸ Liste noire.`
        : `« ${spot_name || "Spot"} » retiré de la liste noire.`);
      softLoad();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action impossible");
      return false;
    }
  };
  const restoreBooking = async (id) => {    const snapshot = data;
    setData((d) => (d ? {
      ...d,
      bookings: (d.bookings || []).map((b) => (b.id === id ? {
        ...b, status: b.status_before_annule || "au_crayon", annule_visible: false,
        annule_by: null, annule_at: null, status_before_annule: null, annulation_motif: null,
      } : b)),
    } : d));
    try {
      const { data: res } = await api.post(`/planning/booking/${id}/restore`);
      toast.success(`Créneau rétabli${res?.status ? ` (${HIST_STATUS_LABELS[res.status] || res.status})` : ""}`);
      softLoad();
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Rétablissement impossible");
    }
  };

  // Déclaration à la coordination : (dé)marque un créneau comme déclaré (suivi
  // de la chargée des opérations). Marqueur visible de tous sur la carte.
  const declareBooking = async (b, declared) => {
    const id = b.id;
    const snapshot = data;
    setData((d) => (d ? {
      ...d,
      bookings: (d.bookings || []).map((x) => (x.id === id ? {
        ...x,
        declared_to_coordination: declared,
        declared_by: declared ? (user?.name || x.declared_by) : null,
        declared_at: declared ? new Date().toISOString() : null,
      } : x)),
    } : d));
    try {
      await api.post(`/planning/booking/${id}/declare`, { declared });
      toast.success(declared ? "Déclaré à la coordination" : "Déclaration retirée");
      softLoad();
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Action impossible");
    }
  };

  // Rebooking terrain : (dé)marquer un créneau existant comme rebooké par un responsable terrain.
  const toggleFieldRebooking = async (id, on, by) => {
    const snapshot = data;
    setData((d) => (d ? { ...d, bookings: (d.bookings || []).map((b) => (b.id === id ? { ...b, field_rebooking: on, field_rebooked_by: on ? (by || b.field_rebooked_by) : null } : b)) } : d));
    try {
      await api.put(`/planning/booking/${id}`, { field_rebooking: on, field_rebooked_by: by });
      toast.success(on ? "Marqué comme rebooking terrain" : "Tag terrain retiré");
      softLoad();
    } catch (err) {
      setData(snapshot); // rollback
      toast.error(err?.response?.data?.detail || "Mise à jour impossible");
    }
  };

  const moveBooking = async (id, ligne_id, date, force = false, lane = null) => {
    const snapshot = data;
    const prev = (data?.bookings || []).find((b) => b.id === id);
    const oldLigneId = prev?.ligne_id;
    const oldDate = prev?.date ? String(prev.date).slice(0, 10) : prev?.date;
    const oldLane = Number.isInteger(prev?.lane) ? prev.lane : null;
    const newDate = String(date).slice(0, 10);
    // ``lane`` = rangée cible choisie manuellement (glisser-déposer vertical). On la
    // fige pour que l'affichage respecte STRICTEMENT ce placement (fin du repacking).
    const newLane = Number.isInteger(lane) ? lane : null;
    // Optimiste : la carte se déplace immédiatement (aucun rechargement/flash).
    // On CONSERVE le cdp_id / la couleur d'ORIGINE : déplacer un créneau sur la
    // ligne d'un autre CDP (entraide) ne doit PAS changer sa couleur.
    setData((d) => {
      if (!d) return d;
      const bookings = (d.bookings || []).map((b) =>
        b.id === id ? { ...b, ligne_id, date, lane: newLane } : b
      );
      return { ...d, bookings };
    });
    try {
      await api.put(`/planning/booking/${id}`, { ligne_id, date, force, lane: newLane });
      softLoad(); // resync silencieux des champs dérivés (code campagne, compteurs)
      // Historique : déplacement annulable (retour à la ligne / date / lane d'origine).
      if (!suppressHistory.current && (oldLigneId !== ligne_id || oldDate !== newDate || oldLane !== newLane)) {
        recordCommand({
          label: `déplacement de « ${prev?.spot_name || "créneau"} »`,
          undo: () => opMove(id, oldLigneId, oldDate, oldLane),
          redo: () => opMove(id, ligne_id, newDate, newLane),
        });
      }
    } catch (err) {
      setData(snapshot); // rollback
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.rule_conflict) {
        setConflict({ type: "move", booking_id: id, ligne_id, date, lane: newLane, reason: d.reason, next: d.next_available, history: d.history, conflict_events: d.conflict_events, conflict_type: d.conflict_type, paris_next_available: d.paris_next_available });
      } else {
        toast.error(typeof d === "string" ? d : "Déplacement impossible");
      }
    }
  };

  const onDropCell = (ligne, date, laneIndex = null) => (e) => {
    e.preventDefault();
    e.currentTarget.removeAttribute("data-drag-over");
    const booking_id = e.dataTransfer.getData("booking_id");
    const spot_id = e.dataTransfer.getData("spot_id");
    if (booking_id) {
      if (!canDragDrop) return;
      moveBooking(booking_id, ligne.id, date, false, laneIndex);
    } else if (spot_id) {
      if (!canEditSpots) return;
      doAssign({ spot_id, ligne_id: ligne.id, date, status: "au_crayon", lane: laneIndex });
    }
  };

  // Tri d'affichage des cartes empilées dans une cellule (identique à buildLigneRows).
  const cellCardSort = (a, b) => {
    const la = Number.isInteger(a.lane) ? a.lane : 999;
    const lb = Number.isInteger(b.lane) ? b.lane : 999;
    return (la - lb) || String(a.spot_name || "").localeCompare(String(b.spot_name || ""));
  };

  // Réordonne les cartes d'une MÊME cellule (glisser-déposer vertical). On insère la
  // carte déplacée juste AVANT la carte cible, puis on réattribue lane = 0..n pour
  // figer l'ordre choisi (persisté via PUT lane, sans revérif de rotation).
  const reorderCardsInCell = async (ligneId, date, draggedId, targetId) => {
    const d10 = String(date).slice(0, 10);
    const cell = (data?.bookings || [])
      .filter((b) => b.ligne_id === ligneId && String(b.date).slice(0, 10) === d10)
      .sort(cellCardSort);
    const from = cell.findIndex((b) => b.id === draggedId);
    const to = cell.findIndex((b) => b.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const ordered = [...cell];
    const [moved] = ordered.splice(from, 1);
    ordered.splice(to, 0, moved);
    const laneOf = {};
    ordered.forEach((b, idx) => { laneOf[b.id] = idx; });
    // Optimiste : on applique immédiatement les nouvelles lanes.
    setData((cur) => cur ? { ...cur, bookings: cur.bookings.map((b) => (b.id in laneOf ? { ...b, lane: laneOf[b.id] } : b)) } : cur);
    try {
      await Promise.all(ordered
        .filter((b) => b.lane !== laneOf[b.id])
        .map((b) => api.put(`/planning/booking/${b.id}`, { lane: laneOf[b.id] })));
      softLoad();
    } catch (_) {
      toast.error("Réordonnancement non enregistré");
      softLoad();
    }
  };

  // Dépôt SUR une carte : dans la même cellule => réordonne ; dans une autre cellule
  // => déplace/empile ; un spot déposé sur une carte => ajoute une carte à ce jour.
  const onDropOnCard = (ligne, date, target) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.removeAttribute("data-card-over");
    const booking_id = e.dataTransfer.getData("booking_id");
    const spot_id = e.dataTransfer.getData("spot_id");
    if (spot_id) {
      if (!canEditSpots) return;
      doAssign({ spot_id, ligne_id: ligne.id, date, status: "au_crayon", lane: null });
      return;
    }
    if (!booking_id || booking_id === target.id) return;
    if (!canDragDrop) return;
    const dragged = (data?.bookings || []).find((b) => b.id === booking_id);
    const sameCell = dragged && dragged.ligne_id === ligne.id
      && String(dragged.date).slice(0, 10) === String(date).slice(0, 10);
    if (sameCell) reorderCardsInCell(ligne.id, date, booking_id, target.id);
    else moveBooking(booking_id, ligne.id, date, false, null);
  };


  // --- Réorganisation des lignes (glisser-déposer, style Excel) ---
  // On glisse une rangée par sa poignée et on la dépose sur l'en-tête d'une autre
  // rangée. L'ordre est mémorisé (display_order) et partagé pour tous.
  const dragLigneRef = useRef(null);
  const [dragOverLigneId, setDragOverLigneId] = useState(null);

  const persistLigneOrder = async (orderedLignes) => {
    try {
      await api.post("/planning/reorder-lignes", { ordered_ids: orderedLignes.map((l) => l.id) });
    } catch {
      toast.error("Réorganisation non enregistrée");
      softLoad();
    }
  };

  const reorderLignes = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setData((d) => {
      if (!d) return d;
      const arr = [...(d.lignes || [])];
      const from = arr.findIndex((l) => l.id === sourceId);
      const to = arr.findIndex((l) => l.id === targetId);
      if (from < 0 || to < 0) return d;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      persistLigneOrder(arr);
      return { ...d, lignes: arr };
    });
  };

  // La suppression d'une ligne est désormais gérée par <RemoveLigneDialog/> : elle offre
  // un choix de portée (cette semaine, à partir de cette semaine, semaines choisies, ou
  // suppression totale) pour que chaque semaine reste indépendante.

  const groupLignesByCdp = () => {
    setData((d) => {
      if (!d) return d;
      const cdpName = (id) => ((d.cdps || []).find((c) => c.id === id)?.name || "zzz").toUpperCase();
      const withIdx = (d.lignes || []).map((l, i) => ({ l, i }));
      withIdx.sort((a, b) => {
        const na = cdpName(a.l.cdp_id), nb = cdpName(b.l.cdp_id);
        if (na !== nb) return na < nb ? -1 : 1;
        return a.i - b.i; // ordre relatif courant conservé au sein d'un même CDP
      });
      const sorted = withIdx.map((x) => x.l);
      persistLigneOrder(sorted);
      return { ...d, lignes: sorted };
    });
    toast.success("Lignes regroupées par CDP");
  };

  // Regroupe (trie) les lignes par ASSOCIATION — même logique que le regroupement
  // par CDP mais sur l'acronyme de l'association. L'ordre relatif courant est
  // conservé au sein d'une même association. Persisté et partagé pour tous.
  const groupLignesByAssociation = () => {
    setData((d) => {
      if (!d) return d;
      const assocName = (l) => (l.assoc?.acronym || l.assoc?.name || "zzz").toUpperCase();
      const withIdx = (d.lignes || []).map((l, i) => ({ l, i }));
      withIdx.sort((a, b) => {
        const na = assocName(a.l), nb = assocName(b.l);
        if (na !== nb) return na < nb ? -1 : 1;
        return a.i - b.i; // ordre relatif courant conservé au sein d'une même association
      });
      const sorted = withIdx.map((x) => x.l);
      persistLigneOrder(sorted);
      return { ...d, lignes: sorted };
    });
    toast.success("Lignes regroupées par association");
  };

  const allDays = data?.days || [];
  const sundayAuto = !!data?.sunday_has_bookings;
  const sundayVisible = showSunday || sundayAuto;
  const days = sundayVisible ? allDays : allDays.slice(0, 6);
  // --- Vue mobile « Jour » : n'affiche qu'une journée à la fois (lisibilité +
  // drop large au doigt). Actif uniquement sur mobile (< md) avec la bascule Jour.
  // `isCompact` (< lg) déclenche une grille semaine à colonnes larges + scroll X. ---
  const isCompact = useIsMobile(1024);
  const singleDayMode = isMobile && weekMode === "jour" && days.length > 0;
  const todayForDay = toISODate(new Date());
  const autoDayIdx = Math.max(0, days.findIndex((d) => d.date === todayForDay));
  const selectedDayIdx = days.length === 0 ? 0
    : Math.min(days.length - 1, dayIndex == null ? autoDayIdx : dayIndex);
  const gridDays = singleDayMode ? [days[selectedDayIdx]] : days;
  const gridMinWidth = singleDayMode ? undefined
    : (isCompact ? `${132 + Math.max(gridDays.length, 1) * 150}px` : undefined);
  const fill = data?.fill_rates || {};
  const weekHolidays = days.filter((d) => d.is_holiday);
  // Rebookings forcés de la semaine (red flag traçable)
  const forcedBookings = (data?.bookings || []).filter((b) => b.rebooking_forced);
  const forcedCount = forcedBookings.length;
  const ligneNameById = {};
  (data?.lignes || []).forEach((l) => { ligneNameById[l.id] = l.identifiant; });
  const dayLabelFromISO = (iso) => {
    try {
      const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
      const wd = d.toLocaleDateString("fr-FR", { weekday: "long" });
      return wd.charAt(0).toUpperCase() + wd.slice(1);
    } catch { return ""; }
  };
  const forcedList = forcedBookings.map((b) => ({
    id: b.id, spot: b.spot_name, ligne: ligneNameById[b.ligne_id] || "—",
    iso: String(b.date).slice(0, 10),
    type: b.forced_type || (String(b.forced_reason || "").startsWith("Contrainte Paris") ? "paris" : "rotation"),
  }));

  // Met en avant une carte de booking (scroll + surbrillance temporaire)
  const focusBooking = (id) => {
    setHighlightBookings({ [id]: true });
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="booking-${id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    setTimeout(() => setHighlightBookings({}), 3200);
  };

  // Met en avant une ligne fraîchement créée (scroll vers son en-tête).
  const focusLigne = (ligneId) => {
    const el = document.querySelector(`[data-testid="ligne-header-${ligneId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // --- Filtre CDP (outil de FOCUS manuel — ne masque jamais une ligne « par erreur ») ---
  const cdps = data?.cdps || [];
  const allLignes = data?.lignes || [];
  // Lignes portant au moins un créneau (emplacement TROUVÉ) sur la semaine affichée.
  const bookedLigneIds = new Set((data?.bookings || []).map((b) => b.ligne_id));
  const activeCdps = cdps.filter((c) => c.active !== false);
  // CDP INACTIFS (contrat terminé / compte désactivé) qui portent ENCORE AU MOINS UNE
  // LIGNE (réattribuée, ou avec des dates à venir) : on les garde dans le filtre, marqués
  // « inactif », pour pouvoir les retrouver/réaffecter — MÊME si leurs créneaux ne sont
  // pas sur la semaine actuellement affichée (sinon leurs lignes deviennent introuvables).
  const lineOwnerIds = new Set(allLignes.map((l) => l.cdp_id).filter(Boolean));
  const orphanCdps = cdps.filter((c) => c.active === false && lineOwnerIds.has(c.id));
  const displayCdps = [...activeCdps, ...orphanCdps];
  const displayCdpIds = new Set(displayCdps.map((c) => c.id));
  const selectedCdpIds = cdpFilter === null ? displayCdps.map((c) => c.id) : cdpFilter;
  // Visibilité d'une ligne selon le filtre CDP.
  // RÈGLE DE SÛRETÉ (ne JAMAIS perdre une ligne) : en mode AUTO (aucun filtre manuel),
  // AUCUN masquage par CDP — une ligne opérationnelle reste visible quel que soit le
  // statut (actif/inactif) de son CDP, et même si le CDP est inconnu/supprimé. Le
  // masquage par DATE (semaine de début/fin, exclusions) est géré à part par
  // ``isLigneVisible``. Le filtre manuel ne sert qu'à SE CONCENTRER sur certains CDP.
  const isCdpVisible = (l) => {
    const cdpId = l.cdp_id;
    if (!cdpId) return true;                       // ligne sans CDP -> toujours visible
    if (cdpFilter === null) return true;           // mode auto -> rien n'est masqué par CDP
    if (!displayCdpIds.has(cdpId)) return true;    // CDP inconnu/supprimé -> jamais perdu
    return cdpFilter.includes(cdpId);              // sélection manuelle explicite (focus)
  };
  // Filtre CDP (actif / sélection) — sert aussi au compteur "lignes masquées par le filtre CDP".
  const cdpVisibleLignes = allLignes.filter(isCdpVisible);
  const hiddenLignesCount = allLignes.length - cdpVisibleLignes.length;
  // Une ligne n'est rendue qu'à partir de la semaine de sa date de début (vide = toujours visible).
  // EXCEPTION : si la ligne a AU MOINS un booking sur la semaine affichée (ex. une date actée
  // avec la direction, positionnée avant le démarrage officiel de la ligne), on l'affiche quand
  // même — sinon le booking confirmé serait invisible et ingérable.
  // Visibilité d'une ligne sur la SEMAINE affichée — TOTALEMENT INDÉPENDANTE des autres
  // semaines : masquée avant sa date de début, après sa date de fin, ou si la semaine
  // (lundi) figure dans ses exclusions ponctuelles. EXCEPTION : si un créneau existe
  // déjà cette semaine, on l'affiche quand même (l'historique n'est jamais perdu).
  const isLigneVisible = (l) => {
    if (bookedLigneIds.has(l.id)) return true;
    if (l.date_debut && monday < mondayOf(l.date_debut)) return false;
    if (l.date_fin && monday > mondayOf(l.date_fin)) return false;
    if ((l.weeks_excluded || []).includes(monday)) return false;
    return true;
  };
  const visibleLignes = cdpVisibleLignes.filter(isLigneVisible);
  // Libellé lisible d'une ligne pour le filtre (équipe + enseigne + identifiant).
  const ligneLabel = (l) => {
    const parts = [];
    if (l.equipe_nom) parts.push(l.equipe_nom);
    const acr = l.assoc?.acronym || l.assoc?.name;
    if (acr) parts.push(acr);
    if (l.identifiant && !parts.length) parts.push(l.identifiant);
    const base = parts.join(" · ") || l.identifiant || "Ligne";
    return l.lieu ? `${base} — ${l.lieu}` : base;
  };
  // Options du filtre « Ligne » (triées par libellé), + validité de la sélection courante.
  const lineOptions = visibleLignes
    .map((l) => ({ id: l.id, label: ligneLabel(l) }))
    .sort((a, b) => a.label.localeCompare(b.label, "fr"));
  const activeLineFilter = lineFilter && visibleLignes.some((l) => l.id === lineFilter) ? lineFilter : null;
  const selectedLineLabel = activeLineFilter
    ? (lineOptions.find((o) => o.id === activeLineFilter)?.label || "Ligne")
    : null;
  const applyLineFilter = (id) => {
    setLineFilter(id);
    setLineFilterOpen(false);
    try {
      if (id) localStorage.setItem("shariti_planning_line_filter", id);
      else localStorage.removeItem("shariti_planning_line_filter");
    } catch { /* ignore */ }
  };
  // Lignes RÉELLEMENT affichées dans la grille : filtrées sur UNE ligne si demandé.
  const shownLignes = activeLineFilter
    ? visibleLignes.filter((l) => l.id === activeLineFilter)
    : visibleLignes;
  // UNE rangée par ligne. Plusieurs spots le même jour = cartes empilées dans la cellule.
  const ligneRows = buildLigneRows(shownLignes, data?.bookings, gridDays);
  // Couverture par jour affiché : une ligne est « couverte » un jour donné dès
  // qu'elle porte au moins un créneau ce jour-là. Sert à la barre de progression.
  const bookedLigneDaySet = new Set(
    (data?.bookings || []).map((b) => `${b.ligne_id}|${String(b.date).slice(0, 10)}`));
  const coverageItems = days.map((d) => {
    let covered = 0;
    for (const l of visibleLignes) {
      if (bookedLigneDaySet.has(`${l.id}|${d.date}`)) covered += 1;
    }
    return {
      date: d.date, label: d.label, covered, total: visibleLignes.length,
      isHoliday: !!d.is_holiday, holidayName: d.holiday_name,
    };
  });
  // Accès direct à la couverture d'un jour depuis l'en-tête de colonne.
  const coverageByDate = {};
  coverageItems.forEach((c) => { coverageByDate[c.date] = c; });
  // Enregistre QUI applique le filtre CDP et QUAND (trace locale, par appareil).
  const stampCdpFilterAudit = () => {
    const audit = { set_by_name: (user?.name || user?.email || "cet appareil"), set_at: new Date().toISOString() };
    setCdpFilterAudit(audit);
    try { localStorage.setItem("shariti_planning_cdp_filter_audit", JSON.stringify(audit)); } catch { /* ignore */ }
  };
  const toggleCdp = (id) => {
    const set = new Set(selectedCdpIds);
    set.has(id) ? set.delete(id) : set.add(id);
    const arr = [...set];
    setCdpFilter(arr);
    localStorage.setItem("shariti_planning_cdp_filter", JSON.stringify(arr));
    stampCdpFilterAudit();
  };
  const resetCdpFilter = () => {
    setCdpFilter(null);
    localStorage.removeItem("shariti_planning_cdp_filter");
    setCdpFilterAudit(null);
    try { localStorage.removeItem("shariti_planning_cdp_filter_audit"); } catch { /* ignore */ }
  };
  const selectAllCdp = () => {
    const arr = displayCdps.map((c) => c.id);
    setCdpFilter(arr);
    localStorage.setItem("shariti_planning_cdp_filter", JSON.stringify(arr));
    stampCdpFilterAudit();
  };
  const deselectAllCdp = () => {
    setCdpFilter([]);
    localStorage.setItem("shariti_planning_cdp_filter", JSON.stringify([]));
    stampCdpFilterAudit();
  };

  const gridStyle = {
    gridTemplateColumns: singleDayMode
      ? `minmax(116px, 132px) minmax(0, 1fr)`
      : isCompact
        ? `minmax(132px, 132px) repeat(${Math.max(gridDays.length, 1)}, minmax(148px, 1fr))`
        : `minmax(160px, 220px) repeat(${Math.max(gridDays.length, 1)}, minmax(0, 1fr))`,
  };

  return (
    <>
      <PageHeader title="Planning des lignes"
        subtitle={isViewer && !canEditSpots && !canDragDrop
          ? "Consultation du planning • une rangée par ligne • fond coloré par CDP."
          : "Une rangée par ligne • fond coloré par CDP • glissez un spot sur un créneau • glissez la poignée ⠿ pour réorganiser les lignes."}
        action={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Filtre LIGNE — n'afficher qu'une seule ligne (focus, sans scroller) */}
            <Popover open={lineFilterOpen} onOpenChange={setLineFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="secondary" data-testid="line-filter-btn"
                  title="N'afficher qu'une seule ligne"
                  className={`border ${activeLineFilter ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white"}`}>
                  <ListFilter className="mr-2 h-4 w-4" />
                  <span className="max-w-[160px] truncate">{selectedLineLabel || "Ligne"}</span>
                  {activeLineFilter && (
                    <span role="button" tabIndex={0} data-testid="line-filter-clear"
                      onClick={(e) => { e.stopPropagation(); applyLineFilter(null); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); applyLineFilter(null); } }}
                      className="ml-1.5 grid h-4 w-4 place-items-center rounded-full hover:bg-[rgba(238,114,46,0.18)]">
                      <X className="h-3 w-3" />
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="end" data-testid="line-filter-popover">
                <Command>
                  <CommandInput placeholder="Rechercher une ligne…" data-testid="line-filter-search" />
                  <CommandList>
                    <CommandEmpty>Aucune ligne.</CommandEmpty>
                    <CommandGroup heading={`${lineOptions.length} ligne(s) cette semaine`}>
                      <CommandItem value="__all__" onSelect={() => applyLineFilter(null)} data-testid="line-filter-opt-all">
                        <Check className={`mr-2 h-4 w-4 ${activeLineFilter ? "opacity-0" : "opacity-100 text-[var(--shariti-orange)]"}`} />
                        Toutes les lignes
                      </CommandItem>
                      {lineOptions.map((o) => (
                        <CommandItem key={o.id} value={`${o.label} ${o.id}`} onSelect={() => applyLineFilter(o.id)} data-testid={`line-filter-opt-${o.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${activeLineFilter === o.id ? "opacity-100 text-[var(--shariti-orange)]" : "opacity-0"}`} />
                          <span className="truncate">{o.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {/* Filtre CDP (fréquent) — reste visible */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="secondary" data-testid="cdp-filter-btn"
                  className={`border ${cdpFilter !== null ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white"}`}>
                  <Filter className="mr-2 h-4 w-4" />CDP
                  {displayCdps.length > 0 && <span className="ml-1.5 rounded-full bg-[var(--surface-2)] px-1.5 text-[11px] font-bold">{selectedCdpIds.length}/{displayCdps.length}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="end">
                <div className="mb-1 flex items-center justify-between px-1">
                  <span className="text-xs font-bold">Afficher les CDP</span>
                  <button onClick={resetCdpFilter} className="text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="cdp-filter-reset" title="Réinitialiser : réafficher automatiquement toutes les lignes (actifs + inactifs porteurs de lignes)">Réinitialiser</button>
                </div>
                <div className="mb-1.5 flex items-center gap-1.5 px-1">
                  <button onClick={selectAllCdp} data-testid="cdp-select-all"
                    className="flex-1 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] hover:text-[var(--shariti-orange)]">
                    Tout sélectionner
                  </button>
                  <button onClick={deselectAllCdp} data-testid="cdp-deselect-all"
                    className="flex-1 rounded-lg border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] hover:text-[var(--shariti-orange)]">
                    Tout désélectionner
                  </button>
                </div>
                <div className="max-h-64 space-y-0.5 overflow-y-auto no-scrollbar">
                  {displayCdps.map((c) => {
                    const on = selectedCdpIds.includes(c.id);
                    const inactif = c.active === false;
                    return (
                      <button key={c.id} onClick={() => toggleCdp(c.id)} data-testid={`cdp-filter-opt-${c.id}`}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-[var(--surface-2)]">
                        <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white"}`}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                        <span className="flex-1 truncate">{c.name}</span>
                        {inactif && <span className="shrink-0 rounded-full bg-[#FBF1E3] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#B9770E]" title="Compte désactivé — ses lignes (réattribuées ou avec dates à venir) restent toujours visibles et gérables">inactif</span>}
                      </button>
                    );
                  })}
                  {displayCdps.length === 0 && <div className="py-3 text-center text-xs text-muted-ink">Aucun CDP.</div>}
                  {orphanCdps.length > 0 && (
                    <div className="mt-1 border-t border-[var(--border-soft)] px-1 pt-1.5 text-[10px] leading-snug text-muted-ink">
                      Les CDP « inactifs » apparaissent car ils portent encore des lignes (réattribuées ou avec des dates à venir). Ces lignes restent toujours visibles : rien n'est jamais perdu.
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Annuler / Rétablir (historique de session : drag&drop, création, suppression, CDP) */}
            {(canDragDrop || canEditSpots) && (
              <div className="inline-flex items-center gap-1" data-testid="planning-history-controls">
                <Button variant="secondary" size="icon" onClick={undo} disabled={history.past.length === 0}
                  title="Annuler la dernière action (Ctrl/Cmd+Z)" aria-label="Annuler" data-testid="planning-undo-btn"
                  className="h-9 w-9 border border-[var(--border-soft)] bg-white transition-colors hover:bg-[var(--surface-2)] disabled:opacity-40">
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button variant="secondary" size="icon" onClick={redo} disabled={history.future.length === 0}
                  title="Rétablir (Ctrl/Cmd+Maj+Z)" aria-label="Rétablir" data-testid="planning-redo-btn"
                  className="h-9 w-9 border border-[var(--border-soft)] bg-white transition-colors hover:bg-[var(--surface-2)] disabled:opacity-40">
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Synchro PREREC (RDs) — accès rapide depuis le planning (admin) */}
            {role === "admin" && (
              <Button variant="secondary" onClick={syncRds} disabled={syncingRds} data-testid="planning-sync-rds-btn"
                title="Synchroniser les collaborateurs depuis PREREC (faire remonter un RD fraîchement onboardé). Se fait aussi automatiquement à l'ouverture."
                className="border border-[var(--border-soft)] bg-white transition-colors hover:bg-[var(--surface-2)]">
                {syncingRds ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />}
                Synchroniser
              </Button>
            )}

            {/* Panneau spots (fréquent) — reste visible */}
            <Button variant="secondary" onClick={() => setPanelOpen((o) => !o)} data-testid="toggle-spots-panel"
              className="border border-[var(--border-soft)] bg-white">
              {panelOpen ? <PanelRightClose className="mr-2 h-4 w-4" /> : <PanelRightOpen className="mr-2 h-4 w-4" />} Spots disponibles
            </Button>

            {/* Menu compact « Actions » — regroupe les actions secondaires */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" data-testid="planning-actions-menu-btn"
                  className="border border-[var(--border-soft)] bg-white transition-transform duration-150 hover:-translate-y-px hover:bg-[var(--surface-2)]">
                  <MoreVertical className="mr-2 h-4 w-4" />Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>Actions du planning</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setFitOpen(true)} data-testid="planning-collab-fit-open">
                  <UserRound className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />Où placer un collaborateur ?
                </DropdownMenuItem>
                {canAutoDistribute && (
                  <DropdownMenuItem onSelect={() => setAutoDistOpen(true)} data-testid="planning-auto-distribute-open">
                    <Sparkles className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />Distribution IA de la semaine
                  </DropdownMenuItem>
                )}
                {canAutoDistribute && (
                  <DropdownMenuItem onSelect={() => setIaJournalOpen(true)} data-testid="planning-auto-distribute-history-open">
                    <History className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />Journal IA (distributions)
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onSelect={() => setOptimizeOpen(true)} data-testid="planning-optimize-open-button">
                    <Wand2 className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />Optimiser l'attribution
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem disabled={crayonCount === 0} onSelect={() => setConfirmFerme(true)} data-testid="confirm-ferme-btn">
                    <CheckCircle2 className="mr-2 h-4 w-4 text-[#1F7A44]" />
                    Confirmer les créneaux au crayon{crayonCount > 0 ? ` (${crayonCount})` : ""}
                  </DropdownMenuItem>
                )}
                {canNewLigne && (
                  <DropdownMenuItem onSelect={() => setNewLigneOpen(true)} data-testid="new-ligne-btn">
                    <Plus className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" />Nouvelle ligne
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onSelect={groupLignesByCdp} data-testid="group-by-cdp-btn">
                    <Users className="mr-2 h-4 w-4" />Regrouper par CDP
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onSelect={groupLignesByAssociation} data-testid="group-by-association-btn">
                    <Building2 className="mr-2 h-4 w-4" />Regrouper par association
                  </DropdownMenuItem>
                )}
                {(role === "admin" || role === "cdp") && (
                  <DropdownMenuItem onSelect={() => setTrashOpen(true)} data-testid="planning-trash-open">
                    <Trash2 className="mr-2 h-4 w-4 text-[#D64545]" />Corbeille des créneaux
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled={sundayAuto} onSelect={() => !sundayAuto && setShowSunday((o) => !o)} data-testid="toggle-sunday">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {sundayAuto ? "Dimanche (auto)" : sundayVisible ? "Masquer le dimanche" : "Afficher le dimanche"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* CTA principal — reste visible et mis en avant */}
            {canDiffuse && (
              <Button onClick={() => setDiffusionOpen(true)} data-testid="validate-week-btn"
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
                <Send className="mr-2 h-4 w-4" />Valider la semaine
              </Button>
            )}
          </div>
        }
      >
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          {/* Week nav */}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, -7))} data-testid="week-prev" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronLeft className="h-4 w-4" /></Button>
            <Popover open={weekPickerOpen} onOpenChange={setWeekPickerOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="week-jump-trigger"
                  title="Cliquer pour aller directement à une semaine"
                  className="group rounded-lg border border-[var(--border-soft)] bg-white px-4 py-1.5 text-center transition-colors hover:border-[var(--shariti-orange)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
                  <div className="text-[10px] font-semibold uppercase text-muted-ink">Semaine {data?.week_number}</div>
                  <div className="flex items-center justify-center gap-1.5 text-sm font-bold" data-testid="week-range">
                    {days[0] && frenchDate(days[0].date)} – {days[days.length - 1] && frenchDate(days[days.length - 1].date)}
                    <ChevronDown className="h-3.5 w-3.5 text-muted-ink" />
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent align="center" className="w-72" data-testid="week-jump-popover">
                <div className="space-y-2">
                  <div className="text-xs font-bold text-ink">Aller à une semaine</div>
                  <Input type="date" value={monday}
                    onChange={(e) => { if (e.target.value) { setMonday(mondayOf(e.target.value)); setWeekPickerOpen(false); } }}
                    className="h-9" data-testid="week-jump-date" />
                  <p className="text-[11px] text-muted-ink">Choisissez n'importe quel jour : le planning s'ouvre sur la semaine correspondante (lundi → dimanche).</p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="week-jump-today"
                      onClick={() => { setMonday(mondayOf(new Date())); setWeekPickerOpen(false); }}>Cette semaine</Button>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="week-jump-14sept"
                      onClick={() => { setMonday(mondayOf("2026-09-14")); setWeekPickerOpen(false); }}>Sem. 14 sept. 2026</Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, 7))} data-testid="week-next" className="h-9 w-9 border border-[var(--border-soft)] bg-white"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" onClick={() => setMonday(mondayOf(new Date()))} data-testid="week-today" className="h-9 border border-[var(--border-soft)] bg-white text-xs"><CalendarClock className="mr-1 h-4 w-4" /> Aujourd'hui</Button>
            <SavedViews page="planning"
              current={{ week: monday, cdpFilter }}
              onApply={(p) => {
                if (p.week) setMonday(mondayOf(p.week));
                if (p.cdpFilter !== undefined) {
                  setCdpFilter(p.cdpFilter);
                  if (p.cdpFilter === null) localStorage.removeItem("shariti_planning_cdp_filter");
                  else localStorage.setItem("shariti_planning_cdp_filter", JSON.stringify(p.cdpFilter));
                }
              }}
              describe={(p) => p.week ? `Semaine du ${frenchDate(mondayOf(p.week))}${Array.isArray(p.cdpFilter) ? ` · ${p.cdpFilter.length} CDP` : ""}` : ""}
              buttonVariant="ghost"
              buttonClassName="h-9 border border-[var(--border-soft)] bg-white text-xs"
            />
            {role === "admin" && (
              <Button variant="ghost" onClick={geocodeMissingWeek} disabled={geocodingWeek}
                data-testid="planning-geocode-week"
                title="Renseigner automatiquement les coordonnées GPS manquantes des spots de la semaine (débloque le pointage)"
                className="h-9 border border-[var(--border-soft)] bg-white text-xs">
                {geocodingWeek
                  ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  : <MapPin className="mr-1 h-4 w-4 text-[var(--shariti-orange)]" />}
                GPS manquants
              </Button>
            )}
          </div>
          {/* Fill gauges */}
          <div className="flex max-w-full items-center gap-4 overflow-x-auto no-scrollbar rounded-xl border border-[var(--border-soft)] bg-white px-4 py-2">
            {["s1", "s2", "s3", "s4"].map((k, i) => (
              <div key={k} className="scale-[0.7] -mx-2"><Gauge value={fill[k]?.rate || 0} target={fill[k]?.target || (i < 2 ? 100 : 70)} label={`S+${i + 1}`} sublabel={`${fill[k]?.booked || 0}/${fill[k]?.capacity || 0}`} testId={`plan-gauge-${k}`} /></div>
            ))}
          </div>
        </div>
      </PageHeader>

      <div className="flex">
        <div className="min-w-0 flex-1 px-4 py-4 sm:px-6 lg:px-8">
          {!isViewer && <PlanningZoneBar data={zoneMap} user={user} navigate={navigate} />}
          {/* Recherche d'historique d'un spot */}
          <div className="relative mb-3" data-testid="spot-history-search">
            <div className={`relative ${histOpen && histQuery.trim().length >= 2 ? "z-40" : ""}`}>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
              <Input
                value={histQuery}
                onChange={(e) => setHistQuery(e.target.value)}
                onFocus={() => histResults.length && setHistOpen(true)}
                placeholder="Rechercher un spot pour voir son historique d'intervention…"
                className="h-11 border-[var(--border-soft)] bg-white pl-9 pr-9"
                data-testid="spot-history-input"
              />
              {histLoading && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--shariti-orange)]" />}
              {!histLoading && histQuery && (
                <button onClick={() => { setHistQuery(""); setHistResults([]); setHistOpen(false); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-ink transition-colors hover:text-ink"
                  data-testid="spot-history-clear" aria-label="Effacer la recherche">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {histOpen && histQuery.trim().length >= 2 && (
              <div className="fixed inset-0 z-30" onClick={() => setHistOpen(false)} data-testid="spot-history-backdrop" />
            )}
            <AnimatePresence>
              {histOpen && histQuery.trim().length >= 2 && (
                <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-[60vh] overflow-y-auto rounded-2xl border border-[var(--border-soft)] bg-white p-2 shadow-[var(--shadow-md)] no-scrollbar"
                  data-testid="spot-history-panel">
                  {histResults.length === 0 && !histLoading && (
                    <div className="px-3 py-6 text-center text-sm text-muted-ink" data-testid="spot-history-empty">
                      Aucun spot trouvé pour « {histQuery.trim()} ».
                    </div>
                  )}
                  {histResults.map((s) => {
                    const open = histExpanded === s.id;
                    return (
                      <div key={s.id} className="mb-1.5 overflow-hidden rounded-xl border border-[var(--border-soft)] last:mb-0"
                        data-testid={`spot-history-result-${s.id}`}>
                        <button onClick={() => { setHistExpanded(open ? null : s.id); setHistAssocFilter(null); }}
                          className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-[var(--surface-2)]"
                          data-testid={`spot-history-toggle-${s.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="break-words text-sm font-bold">{s.name}</span>
                              <span className="shrink-0 rounded-full bg-[rgba(238,114,46,0.1)] px-2 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]">
                                {s.history_count} intervention{s.history_count > 1 ? "s" : ""}
                              </span>
                              {OVEREXP[s.over_level] && (
                                <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${OVEREXP[s.over_level].cls}`}
                                  title={`${s.max_assoc_12m} passage(s) sur 12 mois pour une même association — plafond conseillé ${histMaxPerYear}/an`}
                                  data-testid={`spot-history-overexp-${s.id}`}>
                                  <AlertTriangle className="h-3 w-3" />{OVEREXP[s.over_level].label}
                                </span>
                              )}
                              <RotationMarker status={s.rotation} />
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
                              {s.ville && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.ville}{s.dept ? ` (${s.dept})` : ""}</span>}
                              {s.telephone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{s.telephone}</span>}
                            </div>
                            {s.address_full && <div className="mt-0.5 break-words text-[11px] text-muted-ink">{s.address_full}</div>}
                          </div>
                          <ChevronRight className={`mt-1 h-4 w-4 shrink-0 text-muted-ink transition-transform ${open ? "rotate-90" : ""}`} />
                        </button>
                        {open && (
                          <div className="border-t border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid={`spot-history-detail-${s.id}`}>
                            {/* Synthèse sur-exploitation (12 mois glissants) */}
                            {s.history_count > 0 && (
                              <div className={`mb-2 flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${
                                s.over_level === "over" ? "border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.08)] text-[#9B2226]"
                                : s.over_level === "warn" ? "border-[rgba(240,190,20,0.55)] bg-[rgba(252,219,83,0.28)] text-[#8A6D00]"
                                : "border-[rgba(56,118,29,0.35)] bg-[rgba(56,118,29,0.07)] text-[#2F5E1A]"}`}
                                data-testid={`spot-history-summary-${s.id}`}>
                                {s.over_level === "ok"
                                  ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                  : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                                <span>
                                  {s.count_12m} passage{s.count_12m > 1 ? "s" : ""} sur 12 mois
                                  {s.max_assoc_12m > 0 && ` · jusqu'à ${s.max_assoc_12m}× pour une même association`}
                                  {" "}— plafond conseillé {histMaxPerYear}/an.
                                  {s.over_level === "over" && " Spot potentiellement sur-exploité, à éviter."}
                                  {s.over_level === "warn" && " Attention, proche du plafond."}
                                </span>
                              </div>
                            )}
                            {/* Filtre par association */}
                            {s.associations.length > 1 && (
                              <div className="mb-2 flex flex-wrap items-center gap-1" data-testid={`spot-history-assoc-filter-${s.id}`}>
                                <span className="mr-0.5 text-[10px] font-semibold uppercase text-muted-ink">Association :</span>
                                <button onClick={() => setHistAssocFilter(null)}
                                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors ${!histAssocFilter ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink hover:border-[var(--shariti-orange)]"}`}
                                  data-testid={`spot-history-assoc-all-${s.id}`}>Toutes</button>
                                {s.associations.map((a) => (
                                  <button key={a} onClick={() => setHistAssocFilter(histAssocFilter === a ? null : a)}
                                    className={`rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors ${histAssocFilter === a ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)]"}`}
                                    data-testid={`spot-history-assoc-${s.id}-${a}`}>{a}</button>
                                ))}
                              </div>
                            )}
                            {(() => {
                              const shown = histAssocFilter ? s.history.filter((h) => h.association === histAssocFilter) : s.history;
                              if (shown.length === 0) {
                                return <div className="py-2 text-center text-xs text-muted-ink">Aucune intervention {histAssocFilter ? `pour ${histAssocFilter}` : "enregistrée"}.</div>;
                              }
                              return (
                                <div className="max-h-56 space-y-1 overflow-y-auto no-scrollbar">
                                  {shown.map((h, idx) => (
                                    <div key={idx} className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-2 py-1.5 text-xs"
                                      data-testid={`spot-history-event-${s.id}-${idx}`}>
                                      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
                                      <span className="font-semibold tabular-nums">{frenchDate(h.date)}</span>
                                      {h.association && <span className="rounded bg-[rgba(238,114,46,0.1)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]">{h.association}</span>}
                                      {h.cdp && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-ink">
                                          <span className="h-2 w-2 shrink-0 rounded-full border border-black/10" style={{ background: h.cdp_color || "#ccc" }} />
                                          {h.cdp}
                                        </span>
                                      )}
                                      {h.status && <span className="text-[10px] font-medium text-muted-ink">{HIST_STATUS_LABELS[h.status] || h.status}</span>}
                                      {h.is_historical && <span className="ml-auto rounded bg-[rgba(24,24,24,0.06)] px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-ink">Historique</span>}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                            <button onClick={() => navigate(`/spots/${s.id}`)}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-[var(--shariti-orange)] hover:underline"
                              data-testid={`spot-history-open-${s.id}`}>
                              <ExternalLink className="h-3 w-3" /> Ouvrir la fiche du spot
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {view === "grid" && hiddenLignesCount > 0 && (
            <div className="mb-3 flex flex-col gap-1.5 rounded-xl border-2 border-[rgba(238,114,46,0.55)] bg-[rgba(238,114,46,0.1)] px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between" data-testid="cdp-filter-note">
              <div className="flex items-start gap-2.5">
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[rgba(238,114,46,0.18)]">
                  <EyeOff className="h-4 w-4 text-[var(--shariti-orange)]" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-[var(--shariti-orange)]">
                    {hiddenLignesCount} ligne{hiddenLignesCount > 1 ? "s" : ""} masquée{hiddenLignesCount > 1 ? "s" : ""} par le filtre CDP
                  </p>
                  <p className="text-[11px] font-semibold leading-snug text-[#9A4A17]">
                    Filtre d'affichage personnel à cet appareil — aucune donnée n'est supprimée et rien n'est masqué pour les autres utilisateurs.
                    {cdpFilterAudit?.set_at && (
                      <> Filtre appliqué sur cet appareil par <span className="font-extrabold">{cdpFilterAudit.set_by_name}</span> le {frenchDateTime(cdpFilterAudit.set_at)}.</>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={resetCdpFilter} data-testid="cdp-filter-reset-banner"
                className="shrink-0 self-start rounded-lg bg-[var(--shariti-orange)] px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-[var(--shariti-orange)]/90 sm:self-auto">
                Tout réafficher
              </button>
            </div>
          )}
          {view === "grid" && sundayAuto && (
            <div className="mb-2 inline-flex items-center gap-2 rounded-lg border border-[rgba(238,114,46,0.28)] bg-[rgba(238,114,46,0.06)] px-3 py-1.5 text-xs font-semibold text-[var(--shariti-orange)]" data-testid="sunday-auto-note">
              <CalendarDays className="h-3.5 w-3.5" /> Le dimanche est affiché automatiquement car des journées y sont planifiées.
            </div>
          )}
          {view === "grid" && forcedCount > 0 && (
            <div className="mb-3 overflow-hidden rounded-xl border-2 border-[#D64545] bg-[rgba(214,69,69,0.07)] shadow-[0_2px_10px_rgba(214,69,69,0.15)]" data-testid="forced-week-note">
              <div className="flex items-center gap-2 bg-[#D64545] px-3 py-2 text-white">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/20">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <span className="text-sm font-extrabold uppercase tracking-wide">
                  {forcedCount} créneau{forcedCount > 1 ? "x" : ""} forcé{forcedCount > 1 ? "s" : ""} cette semaine
                </span>
                <span className="ml-auto text-[11px] font-semibold text-white/85">Rotation ou contrainte Paris contournée — à vérifier</span>
              </div>
              <div className="flex flex-col gap-2 p-2.5">
                {forcedList.map((f) => {
                  const det = forcedDetails[f.id];
                  const open = !!forcedOpen[f.id];
                  return (
                  <div key={f.id}
                    data-testid={`forced-item-${f.id}`}
                    className="overflow-hidden rounded-lg border border-[rgba(214,69,69,0.35)] bg-white shadow-[var(--shadow-sm)]">
                    <div className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 text-[12px]">
                      <Flag className="h-3.5 w-3.5 shrink-0 text-[#D64545]" />
                      <span className="font-bold text-[#9B2226]">{f.spot}</span>
                      <span className="text-muted-ink">·</span>
                      <span className="font-semibold text-ink">{f.ligne}</span>
                      <span className="rounded-full bg-[rgba(214,69,69,0.12)] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#9B2226]">
                        {dayLabelFromISO(f.iso)} {frenchDate(f.iso)}
                      </span>
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase ${f.type === "paris" ? "bg-[rgba(124,58,237,0.14)] text-[#5B21B6]" : "bg-[rgba(214,69,69,0.14)] text-[#9B2226]"}`} data-testid={`forced-type-${f.id}`}>
                        {f.type === "paris" ? "Contrainte Paris" : "Rotation"}
                      </span>
                      <div className="ml-auto flex items-center gap-1.5">
                        <button onClick={() => toggleForcedDetail(f.id)}
                          data-testid={`forced-why-${f.id}`}
                          aria-expanded={open}
                          className="inline-flex items-center gap-1 rounded-md border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.06)] px-2 py-1 text-[11px] font-semibold text-[#9B2226] transition-colors hover:bg-[rgba(214,69,69,0.12)]">
                          <History className="h-3 w-3" /> Pourquoi ?
                          <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
                        </button>
                        <button onClick={() => focusBooking(f.id)}
                          data-testid={`forced-view-${f.id}`}
                          className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 text-[11px] font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]">
                          <Search className="h-3 w-3" /> Voir
                        </button>
                        {user?.role === "admin" && (
                          <>
                            <button onClick={() => validateForced(f.id)}
                              data-testid={`forced-validate-${f.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-[#1F9D55] px-2 py-1 text-[11px] font-bold text-white transition-opacity hover:opacity-90">
                              <CheckCircle2 className="h-3 w-3" /> Valider
                            </button>
                            <button onClick={() => { setRefuseReason(""); setRefuseCtx({ id: f.id, spot: f.spot, iso: f.iso }); }}
                              data-testid={`forced-refuse-${f.id}`}
                              className="inline-flex items-center gap-1 rounded-md bg-[#D64545] px-2 py-1 text-[11px] font-bold text-white transition-opacity hover:opacity-90">
                              <Trash2 className="h-3 w-3" /> Refuser
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {open && (
                      <div data-testid={`forced-detail-panel-${f.id}`}
                        className="border-t border-[rgba(214,69,69,0.2)] bg-[rgba(214,69,69,0.04)] px-3 py-2.5">
                        {det?.loading && (
                          <div className="flex items-center gap-2 text-[11px] font-semibold text-[#9B2226]">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse de l'historique de rotation…
                          </div>
                        )}
                        {det?.error && (
                          <div className="text-[11px] font-semibold text-[#9B2226]">Impossible de charger l'historique de cet emplacement.</div>
                        )}
                        {det?.data && <ForcedReasonDetail detail={det.data} />}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
          {view === "grid" && weekHolidays.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-[rgba(240,190,20,0.55)] bg-[rgba(252,219,83,0.30)] px-3 py-1.5 text-xs font-semibold text-[#5A4200]" data-testid="week-holiday-note">
              <Flag className="h-3.5 w-3.5 shrink-0 text-[#8A6D00]" />
              <span className="text-[#8A6D00]">
                Jour{weekHolidays.length > 1 ? "s" : ""} férié{weekHolidays.length > 1 ? "s" : ""} cette semaine :
              </span>
              {weekHolidays.map((d) => (
                <span key={d.date} className="inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5">
                  {d.holiday_name} <span className="font-normal text-[#8A6D00]">({frenchDateShort(d.date)})</span>
                </span>
              ))}
              <span className="font-normal text-[#8A6D00]">— repère informatif, vous pouvez tout de même booker.</span>
            </div>
          )}
          {/* Onglets : grille du planning / suivi des déclarations coordination */}
          <div className="mb-3 inline-flex rounded-xl border border-[var(--border-soft)] bg-white p-1 shadow-[var(--shadow-sm)]" data-testid="planning-view-tabs">
            <button type="button" onClick={() => setView("grid")} data-testid="planning-tab-grid"
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors ${view === "grid" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
              <CalendarRange className="h-4 w-4" /> Planning
            </button>
            <button type="button" onClick={() => setView("coordination")} data-testid="planning-tab-coordination"
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold transition-colors ${view === "coordination" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
              <ClipboardCheck className="h-4 w-4" /> Suivi coordination
              {coordinationWeek.pending_total > 0 && (
                <span className={`ml-0.5 grid h-5 min-w-[20px] place-items-center rounded-full px-1.5 text-[11px] font-bold ${view === "coordination" ? "bg-white/25 text-white" : "bg-[rgba(252,219,83,0.5)] text-[#8A6D00]"}`} data-testid="planning-tab-coordination-badge">
                  {coordinationWeek.pending_total}
                </span>
              )}
            </button>
          </div>
          {view === "coordination" ? (
            <CoordinationBoard
              weekData={coordinationWeek}
              loading={loading}
              canDeclare={canDeclare}
              onDeclare={declareBooking}
              weekMonday={monday}
              onOpenSpot={(role === "admin" || role === "cdp") ? (item) => navigate(`/spots/${item.spot_id}`) : undefined}
            />
          ) : loading ? (
            <div className="space-y-2">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-white" />)}</div>
          ) : (
            <>
            {isMobile && (
              <div className="mb-3 space-y-2" data-testid="planning-mobile-controls">
                <div className="flex items-center justify-between gap-2">
                  <ToggleGroup type="single" value={weekMode} onValueChange={(v) => v && setWeekMode(v)}
                    className="justify-start rounded-lg border border-[var(--border-soft)] bg-white p-0.5">
                    <ToggleGroupItem value="jour" className="h-9 rounded-md px-4 text-xs font-bold data-[state=on]:bg-[var(--shariti-orange)] data-[state=on]:text-white" data-testid="planning-view-jour">Jour</ToggleGroupItem>
                    <ToggleGroupItem value="semaine" className="h-9 rounded-md px-4 text-xs font-bold data-[state=on]:bg-[var(--shariti-orange)] data-[state=on]:text-white" data-testid="planning-view-semaine">Semaine</ToggleGroupItem>
                  </ToggleGroup>
                  {singleDayMode && (
                    <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-muted-ink" data-testid="planning-day-count">
                      {Number(gridDays[0]?.bulletins_total) > 0 && (
                        <span
                          data-testid="planning-day-bulletins-total"
                          title={`${gridDays[0].bulletins_total} bulletin(s) ce jour (total)`}
                          className="grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)]"
                          style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
                          {gridDays[0].bulletins_total}
                        </span>
                      )}
                      {gridDays[0]?.count ?? 0} spot(s)
                    </span>
                  )}
                </div>
                {singleDayMode && (
                  <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5" data-testid="planning-day-chips">
                    {days.map((d, i) => (
                      <button key={d.date} type="button" onClick={() => setDayIndex(i)}
                        className={`flex shrink-0 items-center gap-1 rounded-full border px-3 py-2 text-xs font-bold capitalize transition-[background-color,border-color,color] ${i === selectedDayIdx ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-muted-ink"} ${d.is_holiday ? "ring-1 ring-[rgba(252,219,83,0.7)]" : ""}`}
                        data-selected={i === selectedDayIdx ? "true" : undefined}
                        data-testid={`planning-day-chip-${i}`}>
                        {d.label.slice(0, 3)} <span className="font-normal opacity-80">{frenchDateShort(d.date)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="relative overflow-x-auto overscroll-x-contain rounded-2xl border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)] no-scrollbar lg:overflow-x-visible">
              <div className="lg:min-w-0" style={{ minWidth: gridMinWidth }}>
                {/* Header */}
                <div className="grid border-b border-[var(--border-soft)] bg-[var(--surface-2)]" style={gridStyle}>
                  <div className={`sticky left-0 z-20 border-r border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-muted-ink ${isCompact ? "sticky-col-shadow" : ""}`}>Ligne</div>
                  {gridDays.map((d, i) => {
                    // Couverture du jour → fine barre de progression collée à la
                    // lisière haute de la colonne (Vert 100 % · Orange 50–99 % · Rouge < 50 %).
                    const cov = coverageByDate[d.date];
                    const covNeutral = !cov || cov.isHoliday || cov.total === 0;
                    const covPct = cov && cov.total > 0 ? cov.covered / cov.total : 1;
                    const covWidth = cov && cov.total > 0 ? Math.round(covPct * 100) : 0;
                    const covColor = covNeutral ? "rgba(24,24,24,0.14)" : coverageColor(covPct);
                    return (
                    <div key={d.date} className={`px-2 py-2 ${d.is_holiday ? "bg-[rgba(252,219,83,0.30)]" : d.is_sunday ? "bg-[rgba(238,114,46,0.05)]" : ""} ${i === 2 || i === 4 || d.is_sunday ? "border-l-2 border-[rgba(238,114,46,0.22)]" : "border-l border-[var(--border-soft)]"}`} data-testid={`day-header-${i}`}>
                      <div className="-mx-2 -mt-2 mb-2 h-[9px] overflow-hidden bg-[rgba(24,24,24,0.09)]"
                        title={cov ? (cov.isHoliday ? "Jour férié" : `Couverture : ${cov.covered}/${cov.total} ligne(s) · ${covWidth}%`) : ""}
                        data-testid={`coverage-fill-${d.date}`}>
                        <div className="h-full rounded-r-full transition-[width] duration-300 ease-out"
                          style={{ width: `${covWidth}%`, background: covColor }} data-testid={`coverage-bar-${d.date}`} />
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <div className="min-w-0 truncate text-xs font-bold capitalize">{d.label} <span className="font-normal text-muted-ink">{frenchDateShort(d.date)}</span></div>
                        <div className="flex shrink-0 items-center gap-1">
                          {Number(d.bulletins_total) > 0 && (
                            <span
                              data-testid={`day-bulletins-total-${i}`}
                              title={`${d.bulletins_total} bulletin${d.bulletins_total > 1 ? "s" : ""} enregistré${d.bulletins_total > 1 ? "s" : ""} ce jour (total)`}
                              className="grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)] ring-1 ring-white/70"
                              style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
                              {d.bulletins_total}
                            </span>
                          )}
                          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-bold text-[var(--shariti-orange)]" data-testid={`daycount-${i}`} title={`${d.count} spot(s) planifié(s)`}>{d.count}</span>
                        </div>
                      </div>
                      {d.is_holiday && (
                        <div className="mt-1 flex items-center gap-1 rounded-md border border-[rgba(240,190,20,0.6)] bg-[rgba(252,219,83,0.55)] px-1.5 py-0.5"
                          title={`Jour férié : ${d.holiday_name}`} data-testid={`day-holiday-${i}`}>
                          <Flag className="h-3 w-3 shrink-0 text-[#8A6D00]" />
                          <span className="truncate text-[10px] font-bold text-[#5A4200]">{d.holiday_name}</span>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                {/* Rows (1 spot max par jour et par rangée ; lignes dupliquées si besoin) */}
                {data?.restricted_view && (
                  <div className="mx-2 mt-2 flex items-start gap-2 rounded-lg border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] px-3 py-2 text-[11px] leading-snug text-[#8A3A10]" data-testid="planning-restricted-banner">
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      <b>Vue restreinte</b> — vous ne voyez que <b>vos propres créneaux</b>. Les créneaux
                      des autres équipes apparaissent comme « Créneau occupé » (magasin masqué).
                    </span>
                  </div>
                )}
                {ligneRows.length === 0 && (
                  <div className="flex flex-col items-center justify-center px-6 py-14 text-center" data-testid="planning-empty">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--surface-2)]">
                      <Map className="h-6 w-6 text-[var(--shariti-orange)]" />
                    </div>
                    <div className="mt-3 text-sm font-bold">Aucune ligne à afficher pour l'instant</div>
                    {user?.role === "admin" ? (
                      <>
                        <p className="mt-1 max-w-md text-xs text-muted-ink">Aucune ligne ne correspond (vérifiez les filtres) ou aucune ligne n'a encore été créée. Créez une ligne dans l'administration pour qu'elle apparaisse ici.</p>
                        <button onClick={() => navigate("/admin")} data-testid="planning-empty-admin"
                          className="mt-3 inline-flex items-center gap-1 rounded-lg bg-[var(--shariti-orange)] px-3 py-1.5 text-xs font-bold text-white transition-transform hover:-translate-y-0.5">
                          <Plus className="h-3.5 w-3.5" /> Créer une ligne (Administration)
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="mt-1 max-w-md text-xs text-muted-ink">Vos lignes de planning ne sont pas encore configurées sur votre périmètre. Votre administrateur doit créer une ligne pour que vous puissiez planifier vos spots. En attendant, vous pouvez déjà alimenter la banque de spots via la prospection.</p>
                        <button onClick={() => navigate("/prospection")} data-testid="planning-empty-prospection"
                          className="mt-3 inline-flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--shariti-orange)] transition-colors hover:border-[var(--shariti-orange)]">
                          <Search className="h-3.5 w-3.5" /> Prospecter des spots
                        </button>
                      </>
                    )}
                  </div>
                )}
                {ligneRows.map((row) => {
                  const { ligne, byDate } = row;
                  // Dates déjà occupées par un créneau sur CETTE ligne : sert à
                  // désactiver ces jours dans la duplication d'un créneau.
                  const ligneBookingDates = new Set(
                    (data?.bookings || []).filter((x) => x.ligne_id === ligne.id).map((x) => String(x.date).slice(0, 10)));
                  const perimObj = (data?.perimetres || []).find((p) => p.id === ligne.perimetre_id);
                  const lieu = ligne.lieu || stripCanalFromLieu(perimObj?.nom || ligne.zones_label || "", ligne.canal_nom);
                  return (
                  <div key={ligne.id} className="grid border-b border-[var(--border-soft)] last:border-0" style={gridStyle} data-testid={`ligne-row-${ligne.id}`}>
                    <div
                      className={`sticky left-0 z-10 border-r border-[var(--border-soft)] bg-white px-3 py-2.5 transition-shadow ${isCompact ? "sticky-col-shadow" : ""} ${dragOverLigneId === ligne.id ? "shadow-[inset_0_3px_0_0_var(--shariti-orange)]" : ""}`}
                      onDragOver={(e) => { if (dragLigneRef.current) { e.preventDefault(); if (dragOverLigneId !== ligne.id) setDragOverLigneId(ligne.id); } }}
                      onDragLeave={() => { if (dragLigneRef.current) setDragOverLigneId((cur) => (cur === ligne.id ? null : cur)); }}
                      onDrop={(e) => { if (dragLigneRef.current) { e.preventDefault(); reorderLignes(dragLigneRef.current, ligne.id); dragLigneRef.current = null; setDragOverLigneId(null); } }}
                      data-testid={`ligne-header-${ligne.id}`}
                    >
                      {showLigneMenu && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              className="absolute right-1 top-1 z-20 grid h-6 w-6 place-items-center rounded-md text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
                              title="Actions sur la ligne"
                              data-testid={`ligne-menu-${ligne.id}`}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-56" data-testid={`ligne-menu-content-${ligne.id}`}>
                            <DropdownMenuLabel className="truncate">{ligne.equipe_nom || ligne.identifiant}</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {canRenameReassign && (
                              <>
                                <DropdownMenuItem onClick={() => setRenameLigne(ligne)} data-testid={`ligne-rename-equipe-${ligne.id}`}>
                                  <Tag className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Équipe &amp; responsable…
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setReassignLigne(ligne)} data-testid={`ligne-reassign-${ligne.id}`}>
                                  <ArrowRightLeft className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Réattribuer à un(e) CDP
                                </DropdownMenuItem>
                              </>
                            )}
                            {role === "admin" && (
                              <DropdownMenuItem onClick={() => setChangeAssocLigne(ligne)} data-testid={`ligne-change-assoc-${ligne.id}`}>
                                <Building2 className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Association &amp; code campagne…
                              </DropdownMenuItem>
                            )}
                            {canEditPeriod && (
                              <DropdownMenuItem onClick={() => setEditPeriodLigne(ligne)} data-testid={`ligne-period-${ligne.id}`}>
                                <CalendarDays className="mr-2 h-4 w-4 text-[var(--shariti-orange)]" /> Modifier la période…
                              </DropdownMenuItem>
                            )}
                            {canDeleteLigne && (canRenameReassign || canEditPeriod) && <DropdownMenuSeparator />}
                            {canDeleteLigne && (
                              <DropdownMenuItem onClick={() => setDeleteLigneCtx(ligne)} data-testid={`ligne-delete-${ligne.id}`}
                                className="text-[#D64545] focus:text-[#D64545]">
                                <Trash2 className="mr-2 h-4 w-4" /> Retirer / supprimer la ligne…
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <div className="flex flex-wrap items-center gap-1 text-[10px] font-semibold text-muted-ink">
                        {canDragDrop && (
                          <span
                            draggable
                            onDragStart={(e) => { dragLigneRef.current = ligne.id; e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", ligne.id); } catch (_) {} }}
                            onDragEnd={() => { dragLigneRef.current = null; setDragOverLigneId(null); }}
                            className="-ml-1 cursor-grab rounded p-0.5 text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-ink active:cursor-grabbing"
                            title="Glisser pour réorganiser la ligne (haut / bas)"
                            data-testid={`ligne-drag-handle-${ligne.id}`}
                          >
                            <GripVertical className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5">S{data?.week_number}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 leading-snug" title={ligne.identifiant}>
                        {ligne.assoc?.has_logo ? (
                          <img src={`${api.defaults.baseURL}/assets/association-logo/${ligne.assoc.id}`}
                            alt={ligne.assoc?.acronym || "association"} title={ligne.assoc?.name || ""}
                            className="h-8 w-auto max-w-[76px] shrink-0 object-contain" data-testid={`ligne-assoc-logo-${ligne.id}`} />
                        ) : (
                          <span className="inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[11px] font-extrabold text-white"
                            title={ligne.assoc?.name || ""} style={{ background: ligne.assoc?.color || "#EE722E" }}
                            data-testid={`ligne-assoc-acr-${ligne.id}`}>
                            {ligne.assoc?.acronym || "—"}
                          </span>
                        )}
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[10px] font-bold text-muted-ink"
                          title="Canal de recrutement">
                          <Store className="h-3 w-3" /> {ligne.canal_nom || "Sites privés"}
                        </span>
                        <CodeCampagneBadge ligne={ligne} canEdit={canNewLigne}
                          weekMonday={data?.monday} onSaved={load} />
                      </div>
                      {lieu && (
                        <div className="mt-1 flex items-start gap-1 text-[10px] font-semibold text-muted-ink" title="Lieu / périmètre" data-testid={`ligne-lieu-${ligne.id}`}>
                          <MapPin className="mt-[1px] h-3 w-3 shrink-0" /><span className="min-w-0 break-words">{lieu}</span>
                        </div>
                      )}
                      {(ligne.equipe_nom || ligne.chef_nom) && (() => {
                        const eq = (ligne.equipe_nom || "Équipe").toUpperCase();
                        // Nom COMPLET du responsable (ex: "ERIC MICHEL", pas seulement "ERIC").
                        const chef = ligne.chef_nom ? ligne.chef_nom.toUpperCase() : "";
                        const showChef = chef && !eq.includes(chef);
                        return (
                          <div className="mt-1 break-words text-[11px] font-bold text-ink" data-testid={`ligne-equipe-${ligne.id}`} title={showChef ? `${eq} - ${chef}` : eq}>
                            {eq}
                            {showChef && <span> - {chef}</span>}
                          </div>
                        );
                      })()}
                      <CdpTag ligne={ligne} cdps={data?.cdps || []} />
                    </div>
                    {gridDays.map((d, i) => {
                      // Toutes les cartes de CE jour empilées dans la même cellule.
                      const cellBks = byDate[d.date] || [];
                      const hasCards = cellBks.length > 0;
                      return (
                        <div key={d.date}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.setAttribute("data-drag-over", "true"); }}
                          onDragLeave={(e) => e.currentTarget.removeAttribute("data-drag-over")}
                          onDrop={onDropCell(ligne, d.date, null)}
                          className={`rounded-lg p-1 transition-colors data-[drag-over=true]:bg-[rgba(238,114,46,0.12)] data-[drag-over=true]:ring-2 data-[drag-over=true]:ring-[var(--shariti-orange)] data-[drag-over=true]:ring-inset ${d.is_holiday ? "bg-[rgba(252,219,83,0.10)]" : d.is_sunday ? "bg-[rgba(238,114,46,0.03)]" : ""} ${i === 2 || i === 4 || d.is_sunday ? "border-l-2 border-[rgba(238,114,46,0.14)]" : "border-l border-[var(--border-soft)]"}`}
                          data-testid={`cell-${ligne.id}-${i}`}>
                          <div className="flex h-full flex-col gap-1.5">
                            {cellBks.map((b) => (
                              <div key={b.id}
                                onDragOver={(e) => { if (canDragDrop) { e.preventDefault(); e.stopPropagation(); e.currentTarget.setAttribute("data-card-over", "true"); } }}
                                onDragLeave={(e) => e.currentTarget.removeAttribute("data-card-over")}
                                onDrop={onDropOnCard(ligne, d.date, b)}
                                className="rounded-xl transition-shadow data-[card-over=true]:ring-2 data-[card-over=true]:ring-[var(--shariti-orange)] data-[card-over=true]:ring-offset-1">
                                {b.masked ? (
                                  <MaskedBookingCard b={b} />
                                ) : (
                                  <BookingCard b={b} onView={() => navigate(`/spots/${b.spot_id}`)}
                                    onDelete={() => requestDeleteBooking(b.id)} onStatus={(s) => changeStatus(b.id, s)}
                                    onBulletins={() => setBulletinsBk(b.id)}
                                    canCancel={role === "admin"}
                                    onCancel={(motif, note) => cancelBooking(b.id, motif, note)}
                                    onRestore={() => restoreBooking(b.id)}
                                    onFieldToggle={(bk, on) => toggleFieldRebooking(bk.id, on, on ? (ligne.chef_nom || ligne.chef_prenom || undefined) : undefined)}
                                    days={days} ligneOccupiedDates={ligneBookingDates} onDuplicate={duplicateBooking}
                                    onSetContact={setBookingContact} onCreateContact={createBookingContact}
                                    cdps={data?.cdps || []} onSetCdp={setBookingCdp}
                                    onSetConsignes={setBookingConsignes}
                                    onChangeSpot={(spot_id, motif) => changeBookingSpot(b.id, spot_id, motif)}
                                    canBlacklist={canBlacklist} onBlacklist={(blk) => blacklistSpot(b.spot_id, b.spot_name, blk)}
                                    canEdit={canEditSpots} canDrag={canDragDrop}
                                    canDeclare={canDeclare} onDeclare={declareBooking}
                                    canManageTeam={canManageTeam} teamCount={teamCounts[b.id]} onTeam={() => setTeamBooking({ id: b.id, date: b.date, spot_id: b.spot_id, spot_name: b.spot_name, spot_address: b.spot_address })}
                                    onThank={() => openThanks(b)} thankBusy={thanksBusyId === b.id}
                                    highlight={!!highlightBookings[b.id]} />
                                )}
                              </div>
                            ))}
                            {canEditSpots ? (
                              hasCards ? (
                                <button onClick={() => setAssignCtx({ ligne, date: d.date, lane: cellBks.length })}
                                  data-testid={`add-card-${ligne.id}-${i}`}
                                  title="Ajouter une carte ce jour (plusieurs spots le même jour)"
                                  className="group flex min-h-[30px] w-full items-center justify-center gap-1 rounded-lg border border-dashed border-[rgba(238,114,46,0.28)] bg-[rgba(238,114,46,0.02)] text-[11px] font-bold text-[rgba(238,114,46,0.62)] transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.07)] hover:text-[var(--shariti-orange)]">
                                  <Plus className="h-3.5 w-3.5" /> Carte
                                </button>
                              ) : (
                                <button onClick={() => setAssignCtx({ ligne, date: d.date, lane: 0 })}
                                  data-testid={`empty-cell-${ligne.id}-${i}`}
                                  className="group flex h-full min-h-[64px] w-full items-center justify-center rounded-lg border border-dashed border-[rgba(214,69,69,0.24)] bg-[rgba(214,69,69,0.03)] text-[rgba(214,69,69,0.45)] transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] hover:text-[var(--shariti-orange)]">
                                  <Plus className="h-4 w-4" />
                                </button>
                              )
                            ) : (!hasCards ? (
                              isFieldLead && !d.is_sunday && !d.is_holiday ? (
                                <div className="grid min-h-[64px] w-full place-items-center rounded-lg border border-dashed border-[rgba(214,69,69,0.28)] bg-[rgba(214,69,69,0.04)]" title="Créneau libre — trou à anticiper" data-testid={`gap-cell-${ligne.id}-${i}`}>
                                  <span className="text-[9px] font-bold uppercase tracking-wide text-[rgba(214,69,69,0.55)]">Libre</span>
                                </div>
                              ) : (
                                <div className="min-h-[64px] w-full rounded-lg border border-dashed border-transparent" />
                              )
                            ) : null)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  );
                })}
              </div>
            </div>
            </>
          )}
        </div>

        {/* Spots panel */}
        <AnimatePresence>
          {panelOpen && (
            <motion.aside initial={{ width: 0, opacity: 0 }} animate={{ width: 300, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
              className="sticky top-0 hidden h-[calc(100vh)] shrink-0 overflow-hidden border-l border-[var(--border-soft)] bg-white lg:block">
              <div className="flex h-full w-[300px] flex-col p-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold">Spots disponibles</h3>
                  <button onClick={() => setPanelOpen(false)} className="rounded p-1 hover:bg-[var(--surface-2)]"><X className="h-4 w-4" /></button>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-ink">{canEditSpots ? "Glissez un spot sur un créneau vide." : "Consultation seule : liste des spots disponibles."}</p>
                <div className="relative mt-2">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                  <Input value={spotSearch} onChange={(e) => setSpotSearch(e.target.value)} placeholder="Rechercher…" className="h-9 pl-8" data-testid="panel-spot-search" />
                </div>
                <div className="mt-2 flex-1 space-y-1.5 overflow-y-auto no-scrollbar">
                  {spotResults.map((s) => (
                    <div key={s.id} draggable={canEditSpots} onDragStart={(e) => { if (!canEditSpots) { e.preventDefault(); return; } e.dataTransfer.setData("spot_id", s.id); }}
                      data-testid={`drag-spot-${s.id}`}
                      className={`flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-2 ${canEditSpots ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}>
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-ink" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <div className="break-words text-xs font-bold">{s.normalized_name}</div>
                          <RotationMarker status={spotRotation[s.id]} compact className="ml-auto" />
                        </div>
                        <div className="text-[10px] text-muted-ink">{s.ville} {s.dept ? `(${s.dept})` : ""} {s.flux_effective ? `• ${s.flux_effective}/j` : ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* Assign dialog */}
      <AssignDialog ctx={assignCtx} onClose={() => setAssignCtx(null)} spots={spotResults}
        search={spotSearch} setSearch={setSpotSearch} onAssign={doAssign} initialSpot={pendingSpot} chefs={chefs} onCreateChef={createChef} onUpdateChef={updateChef}
        cdps={activeCdps} canCredit={role === "admin"} />

      {/* Bulletins & présence */}
      <BulletinsDialog bookingId={bulletinsBk} open={!!bulletinsBk} onOpenChange={(o) => !o && setBulletinsBk(null)} />

      {/* Remerciement au magasin — déclenché depuis un créneau passé du planning */}
      <RemerciementDialog
        open={!!thanksSpot}
        spot={thanksSpot}
        associationId={thanksAssoc}
        onClose={() => { setThanksSpot(null); setThanksAssoc(null); }}
        onSent={() => { setThanksSpot(null); setThanksAssoc(null); }}
      />

      {/* #4 — Réattribution de ligne à un(e) CDP (depuis l'en-tête de ligne) */}
      <ReassignLigneDialog
        ligne={reassignLigne}
        cdps={data?.cdps || []}
        weekMonday={monday}
        onClose={() => setReassignLigne(null)}
        onDone={() => { setReassignLigne(null); load(); }}
      />

      {/* Équipe & responsable d'une ligne — portée maîtrisée (semaine + lignes) */}
      <RenameEquipeDialog
        ligne={renameLigne}
        chefs={chefs}
        onCreateChef={createChef}
        weekMonday={monday}
        onChanged={softLoad}
        onClose={() => setRenameLigne(null)}
        onDone={() => { setRenameLigne(null); load(); }}
      />

      {/* Équipe du jour (pointage) — panneau latéral d'affectation rapide */}
      <TeamSheet
        target={teamBooking ? {
          kind: "prive", id: teamBooking.id, date: teamBooking.date,
          name: teamBooking.spot_name, address: teamBooking.spot_address,
        } : null}
        weekDays={(data?.days || []).map((d) => d.date)}
        onClose={() => setTeamBooking(null)}
        onChanged={() => refreshTeamCounts(data?.bookings)}
        canEdit={canManageTeam}
      />

      {/* Créer une nouvelle ligne directement depuis le planning */}
      <NewLigneDialog
        open={newLigneOpen}
        onClose={() => setNewLigneOpen(false)}
        associations={associations}
        canaux={canaux}
        cdps={data?.cdps || []}
        perimetres={data?.perimetres || []}
        chefs={chefs}
        onCreateChef={createChef}
        weekMonday={monday}
        onDone={(res) => {
          setNewLigneOpen(false);
          load();
          if (res?.ligne_id) setTimeout(() => focusLigne(res.ligne_id), 400);
        }}
      />

      {/* Suppression d'une ligne (confirmation) */}
      <RemoveLigneDialog
        ligne={deleteLigneCtx}
        weekMonday={monday}
        weekNumber={data?.week_number}
        isAdmin={user?.role === "admin"}
        onClose={() => setDeleteLigneCtx(null)}
        onDone={load}
      />

      <EditPeriodDialog
        ligne={editPeriodLigne}
        weekMonday={monday}
        onClose={() => setEditPeriodLigne(null)}
        onDone={load}
      />

      {/* Bascule d'une ligne vers une autre association (+ périmètre / code / lieu) */}
      <ChangeAssocDialog
        ligne={changeAssocLigne}
        associations={associations}
        perimetres={data?.perimetres || []}
        bookingsCount={changeAssocLigne ? (data?.bookings || []).filter((b) => b.ligne_id === changeAssocLigne.id).length : 0}
        weekMonday={monday}
        onClose={() => setChangeAssocLigne(null)}
        onDone={() => { setChangeAssocLigne(null); load(); }}
      />


      {/* Rule conflict */}
      <AlertDialog open={confirmFerme} onOpenChange={(o) => !o && setConfirmFerme(false)}>
        <AlertDialogContent data-testid="confirm-ferme-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-[#1F9D55]" /> Confirmer la semaine</AlertDialogTitle>
            <AlertDialogDescription>
              Passer les <b>{crayonCount}</b> créneau(x) « Au crayon » de la semaine {data?.week_number} en <b>Ferme</b> (accord obtenu) ? Les créneaux historiques ne sont pas touchés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-ferme-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmFermeWeek} disabled={ferming}
              className="bg-[#1F9D55] text-white hover:bg-[#1A854A]" data-testid="confirm-ferme-confirm">
              {ferming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer en Ferme"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Garde-fou : suppression d'un créneau CONFIRMÉ / TROUVÉ */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#D64545]" />
              {confirmDelete?.blocked ? "Suppression réservée à la direction" : "Supprimer un créneau confirmé ?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.blocked ? (
                <>Le créneau <b>{confirmDelete?.booking?.spot_name || "sélectionné"}</b>
                  {confirmDelete?.booking?.date ? <> du <b>{frenchDate(confirmDelete.booking.date)}</b></> : null} est
                  <b> confirmé / trouvé</b>. Pour éviter toute suppression par mégarde, seule la direction
                  (administrateur) peut le supprimer. Il reste visible pour tout le monde. Contactez un
                  administrateur si une suppression est nécessaire.</>
              ) : (
                <>Vous êtes sur le point de supprimer un créneau <b>confirmé / trouvé</b> :
                  <b> {confirmDelete?.booking?.spot_name || "créneau"}</b>
                  {confirmDelete?.booking?.date ? <> du <b>{frenchDate(confirmDelete.booking.date)}</b></> : null}
                  {confirmDelete?.booking?.status_label ? <> (statut : {confirmDelete.booking.status_label})</> : null}.
                  Il sera placé dans la <b>corbeille</b> et restera <b>restaurable</b>. Confirmez-vous ?</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">
              {confirmDelete?.blocked ? "Fermer" : "Annuler"}
            </AlertDialogCancel>
            {!confirmDelete?.blocked && (
              <AlertDialogAction
                onClick={() => { const id = confirmDelete?.id; setConfirmDelete(null); if (id) deleteBooking(id); }}
                className="bg-[#D64545] text-white hover:bg-[#B93A3A]" data-testid="confirm-delete-confirm">
                Mettre à la corbeille
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Corbeille des créneaux (suppression douce, réversible) */}
      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="max-w-2xl" data-testid="trash-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-[#D64545]" /> Corbeille des créneaux</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-ink">
            Les créneaux supprimés sont conservés ici et peuvent être <b>restaurés</b> à tout moment.
            {role === "admin"
              ? " La suppression définitive (vider) est irréversible."
              : " Seule la direction (administrateur) peut supprimer définitivement."}
          </p>
          {trashData === null ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--shariti-orange)]" /></div>
          ) : (trashData.items || []).length === 0 ? (
            <div className="grid place-items-center rounded-2xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] py-10 text-center" data-testid="trash-empty">
              <Trash2 className="h-7 w-7 text-muted-ink" />
              <p className="mt-2 text-sm font-bold text-ink">La corbeille est vide</p>
              <p className="mt-1 text-xs text-muted-ink">Aucun créneau supprimé récemment.</p>
            </div>
          ) : (
            <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1" data-testid="trash-list">
              {trashData.items.map((it) => (
                <div key={it.id} className="flex items-start justify-between gap-3 rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`trash-row-${it.id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-bold text-ink">{it.spot_name}</span>
                      {it.status_label && <span className="rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold text-muted-ink">{it.status_label}</span>}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-ink">
                      {[it.ville, it.ligne_label, it.date ? frenchDate(it.date) : null].filter(Boolean).join(" · ")}
                    </div>
                    <div className="mt-0.5 text-[11px] text-muted-ink">
                      Supprimé par <span className="font-semibold text-ink">{it.deleted_by_name}</span>
                      {it.deleted_at ? <> le {frenchDateTime(it.deleted_at)}</> : null}
                      {it.affectations_count > 0 ? <> · {it.affectations_count} affectation(s) à rétablir</> : null}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button size="sm" variant="outline" disabled={trashBusyId === it.id}
                      onClick={() => restoreFromTrash(it.id)} data-testid={`trash-restore-${it.id}`}
                      className="h-8 gap-1 text-xs">
                      {trashBusyId === it.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                      Restaurer
                    </Button>
                    {role === "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" disabled={trashBusyId === it.id}
                            data-testid={`trash-delete-${it.id}`}
                            className="h-8 gap-1 text-xs text-[#D64545] hover:bg-[rgba(214,69,69,0.08)] hover:text-[#B93A3A]">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="trash-delete-confirm-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#D64545]" /> Supprimer définitivement ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Le créneau <b>{it.spot_name}</b>{it.date ? <> du <b>{frenchDate(it.date)}</b></> : null} sera
                              supprimé <b>définitivement</b> (action irréversible). Cette opération est tracée dans le journal.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => permanentDelete(it.id)}
                              className="bg-[#D64545] text-white hover:bg-[#B93A3A]" data-testid={`trash-delete-confirm-${it.id}`}>
                              Supprimer définitivement
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" data-testid="conflict-title"><AlertTriangle className="h-5 w-5 text-[#D64545]" /> {conflict?.conflict_type === "paris" ? "Contrainte Paris non respectée" : "Règle de rotation non respectée"}</AlertDialogTitle>
            <AlertDialogDescription>
              {conflict?.reason || "Cet emplacement ne peut pas être réservé à cette date."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[46vh] overflow-y-auto rounded-lg border border-[rgba(214,69,69,0.25)] bg-[rgba(214,69,69,0.04)] p-2.5" data-testid="conflict-detail">
            {conflict?.conflict_type === "paris"
              ? <ParisConstraintBlock reason={conflict?.reason} nextAvailable={conflict?.paris_next_available} />
              : <RotationHistoryBlocks conflictEvents={conflict?.conflict_events} history={conflict?.history} nextAvailable={conflict?.next} />}
          </div>
          {user?.role !== "admin" && (
            <div className="flex items-start gap-2 rounded-lg border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.07)] px-3 py-2 text-[12px] font-semibold text-[#9B2226]" data-testid="conflict-blocked-note">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{conflict?.conflict_type === "paris"
                ? "Impossible de réserver cet emplacement : la contrainte Paris (dernière semaine du mois) n'est pas respectée. Seul un administrateur peut forcer ce créneau — contactez-le pour valider l'exception."
                : "Impossible de réserver cet emplacement : la règle de rotation n'est pas respectée. Seul un administrateur peut forcer un rebooking — contactez-le pour valider l'exception."}</span>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conflict-cancel">{user?.role === "admin" ? "Annuler" : "Fermer"}</AlertDialogCancel>
            {user?.role === "admin" && (
              <AlertDialogAction data-testid="conflict-force" className="bg-[var(--shariti-orange)] hover:bg-[#E56622]"
                onClick={() => {
                  if (conflict?.type === "move") {
                    moveBooking(conflict.booking_id, conflict.ligne_id, conflict.date, true, conflict.lane);
                  } else {
                    doAssign({ ...conflict, force: true });
                  }
                  setConflict(null);
                }}>Forcer quand même (admin)</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!refuseCtx} onOpenChange={(o) => { if (!o) { setRefuseCtx(null); setRefuseReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-[#D64545]" /> Refuser ce rebooking forcé</AlertDialogTitle>
            <AlertDialogDescription>
              {refuseCtx ? `« ${refuseCtx.spot} » du ${frenchDate(refuseCtx.iso)}` : ""} sera retiré du planning et archivé dans le journal des exceptions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-[12px] font-semibold text-ink">Motif du refus (recommandé)</label>
            <Textarea value={refuseReason} onChange={(e) => setRefuseReason(e.target.value)} rows={3}
              placeholder="Ex : emplacement déjà exploité par AIF le 17/08 — rotation non respectée."
              data-testid="refuse-reason-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="refuse-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="refuse-confirm" disabled={refusing}
              className="bg-[#D64545] text-white hover:bg-[#C13A3A]"
              onClick={(e) => { e.preventDefault(); submitRefuse(); }}>
              {refusing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refuser et archiver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DiffusionDialog open={diffusionOpen} onClose={() => setDiffusionOpen(false)} week={monday} onSent={load} />
      <OptimizeAttributionSheet
        open={optimizeOpen}
        onOpenChange={setOptimizeOpen}
        week={monday}
        onApplied={(bookingIds) => {
          const hl = {};
          (bookingIds || []).forEach((id) => { hl[id] = true; });
          setHighlightBookings(hl);
          load();
          setTimeout(() => setHighlightBookings({}), 2500);
        }}
      />

      <CollabFitDialog
        open={fitOpen}
        onOpenChange={setFitOpen}
        weekDays={days.map((d) => d.date)}
        weekNumber={data?.week_number}
        onOpenSpot={(sid) => { setFitOpen(false); navigate(`/spots/${sid}`); }}
        onValidated={() => { load(); refreshTeamCounts(data?.bookings); }}
      />

      <AutoDistributeDialog
        open={autoDistOpen}
        onOpenChange={setAutoDistOpen}
        week={monday}
        onApplied={() => { load(); refreshTeamCounts(data?.bookings); }}
        onOpenHistory={() => { setAutoDistOpen(false); setIaJournalOpen(true); }}
      />

      <AutoDistributeHistoryDialog
        open={iaJournalOpen}
        onOpenChange={setIaJournalOpen}
        week={monday}
        onChanged={() => { load(); refreshTeamCounts(data?.bookings); }}
      />
    </>
  );
}

const WEEKDAY_LABELS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];

// Assistant de placement : on choisit un collaborateur + un jour de la semaine
// affichée, et on classe les spots DÉJÀ positionnés ce jour-là selon la performance
// historique du collaborateur, en priorisant son rendement sur CE jour de semaine.
function CollabFitDialog({ open, onOpenChange, weekDays = [], weekNumber, onOpenSpot, onValidated }) {
  const [mode, setMode] = useState("semaine");   // "semaine" (défaut) | "jour"
  const [query, setQuery] = useState("");
  const [rdResults, setRdResults] = useState([]);
  const [rdOpen, setRdOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rd, setRd] = useState(null);        // { id, name, code }
  const [day, setDay] = useState(null);      // date ISO
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Jour par défaut à l'ouverture : aujourd'hui s'il est dans la semaine, sinon le 1er jour.
  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setDay(weekDays.includes(today) ? today : (weekDays[0] || null));
    setMode("semaine");
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Réinitialisation à la fermeture.
  useEffect(() => {
    if (!open) { setQuery(""); setRdResults([]); setRd(null); setData(null); setRdOpen(false); }
  }, [open]);

  // Autocomplétion collaborateur (recruteur).
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2 || rd) { setRdResults([]); return; }
    let cancel = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data: res } = await api.get("/rds", { params: { search: q, limit: 8 } });
        if (!cancel) { setRdResults(res || []); setRdOpen(true); }
      } catch { if (!cancel) setRdResults([]); }
      finally { if (!cancel) setSearching(false); }
    }, 250);
    return () => { cancel = true; clearTimeout(t); };
  }, [query, open, rd]);

  // Chargement du classement (onglet « Jour par jour » uniquement).
  useEffect(() => {
    if (!open || mode !== "jour" || !rd || !day) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const { data: res } = await api.get("/planning/collaborator-fit", { params: { rd_id: rd.id, date: day } });
        if (!cancel) setData(res);
      } catch { if (!cancel) setData(null); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [rd, day, open, mode]);

  const wdLabel = data ? WEEKDAY_LABELS[data.weekday] : "";
  const candidates = data?.candidates || [];
  const withHistory = candidates.filter((c) => c.has_history);
  const topId = (withHistory.find((c) => c.weekday_days > 0) || withHistory[0] || {}).spot_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden" data-testid="collab-fit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5 text-[var(--shariti-orange)]" /> Où placer un collaborateur ?
          </DialogTitle>
        </DialogHeader>

        {/* Recherche du collaborateur (partagée par les deux onglets) */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
          <Input
            value={rd ? `${rd.name}${rd.code ? ` (${rd.code})` : ""}` : query}
            onChange={(e) => { setRd(null); setData(null); setQuery(e.target.value); }}
            onFocus={() => { if (rdResults.length && !rd) setRdOpen(true); }}
            placeholder="Tapez le nom d'un collaborateur (recruteur)…"
            className="h-11 bg-white pl-9 pr-9"
            data-testid="collab-fit-search"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--shariti-orange)]" />}
          {!searching && (rd || query) && (
            <button onClick={() => { setRd(null); setQuery(""); setRdResults([]); setData(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-ink hover:text-ink"
              data-testid="collab-fit-clear" aria-label="Effacer">
              <X className="h-4 w-4" />
            </button>
          )}
          {rdOpen && !rd && rdResults.length > 0 && (
            <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-[var(--border-soft)] bg-white shadow-lg" data-testid="collab-fit-suggestions">
              {rdResults.map((r) => (
                <button key={r.id} type="button" onClick={() => { setRd(r); setRdOpen(false); }}
                  data-testid={`collab-fit-rd-${r.id}`}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[rgba(238,114,46,0.08)]">
                  <span className="font-semibold text-ink">{r.name}</span>
                  <span className="text-[11px] text-muted-ink">{r.code || ""}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Tabs value={mode} onValueChange={setMode} className="mt-3 flex min-h-0 flex-1 flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="semaine" data-testid="collab-fit-tab-semaine" className="gap-1.5">
              <CalendarRange className="h-4 w-4" /> Semaine
            </TabsTrigger>
            <TabsTrigger value="groupe" data-testid="collab-fit-tab-groupe" className="gap-1.5">
              <Users className="h-4 w-4" /> Groupe
            </TabsTrigger>
            <TabsTrigger value="jour" data-testid="collab-fit-tab-jour" className="gap-1.5">
              <CalendarDays className="h-4 w-4" /> Par jour
            </TabsTrigger>
          </TabsList>

          {/* ===== ONGLET SEMAINE : proposition + validation en 1 clic ===== */}
          <TabsContent value="semaine" className="mt-3 min-h-0 flex-1 overflow-y-auto">
            {!rd ? (
              <div className="grid place-items-center py-10 text-center text-sm text-muted-ink" data-testid="collab-week-hint">
                <CalendarRange className="mb-2 h-8 w-8 opacity-40" />
                Choisissez un collaborateur : je vous propose un planning complet de la semaine, prêt à valider (meilleur spot chaque jour + horaires pré-remplis).
              </div>
            ) : (
              <CollabWeekView rd={rd} weekMonday={weekDays[0]} weekNumber={weekNumber}
                onOpenSpot={onOpenSpot} onValidated={onValidated} />
            )}
          </TabsContent>

          {/* ===== ONGLET GROUPE : place un binôme/trio historique ensemble ===== */}
          <TabsContent value="groupe" className="mt-3 min-h-0 flex-1 overflow-y-auto">
            {!rd ? (
              <div className="grid place-items-center py-10 text-center text-sm text-muted-ink" data-testid="collab-group-hint">
                <Users className="mb-2 h-8 w-8 opacity-40" />
                Choisissez un collaborateur « pilote » : je détecte ses binômes/trios habituels et place tout le groupe sur les meilleurs spots de la semaine, en un clic.
              </div>
            ) : (
              <CollabWeekView rd={rd} weekMonday={weekDays[0]} weekNumber={weekNumber}
                onOpenSpot={onOpenSpot} onValidated={onValidated} groupMode />
            )}
          </TabsContent>

          {/* ===== ONGLET JOUR PAR JOUR : classement d'un jour précis ===== */}
          <TabsContent value="jour" className="mt-3 min-h-0 flex-1 overflow-y-auto">
            <div className="mb-2">
              <div className="mb-1 text-[11px] font-semibold uppercase text-muted-ink">Jour{weekNumber ? ` · semaine ${weekNumber}` : ""}</div>
              <div className="flex flex-wrap gap-1.5" data-testid="collab-fit-days">
                {weekDays.map((d) => (
                  <button key={d} type="button" onClick={() => setDay(d)} data-testid={`collab-fit-day-${d}`}
                    className={`rounded-lg border px-2.5 py-1 text-[12px] font-semibold transition-colors ${day === d ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[#8A3A10]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:border-[var(--shariti-orange)]"}`}>
                    {frenchDayChip(d)}
                  </button>
                ))}
              </div>
            </div>

            {!rd ? (
              <div className="grid place-items-center py-10 text-center text-sm text-muted-ink" data-testid="collab-fit-hint">
                <UserRound className="mb-2 h-8 w-8 opacity-40" />
                Choisissez un collaborateur : on classe les spots positionnés ce jour-là selon là où il a le mieux performé (priorité au même jour de semaine).
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-muted-ink"><Loader2 className="h-4 w-4 animate-spin" /> Analyse de l'historique…</div>
            ) : (data && data.count === 0) ? (
              <div className="grid place-items-center py-10 text-center text-sm text-muted-ink" data-testid="collab-fit-noday">
                <CalendarClock className="mb-2 h-8 w-8 opacity-40" />
                {data.message || "Aucun spot positionné ce jour-là."}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-[12px] text-muted-ink" data-testid="collab-fit-summary">
                  Classement de <b className="text-ink">{data?.rd?.name}</b> sur les <b className="text-ink">{candidates.length}</b> spot(s) positionnés le <b className="text-ink">{frenchDate(day)}</b> — priorité au rendement du <b className="text-ink">{wdLabel}</b>.
                </div>
                <div className="flex flex-wrap items-center gap-1.5" data-testid="collab-fit-dispo-line">
                  <span className="text-[11px] font-semibold text-muted-ink">Disponibilité déclarée ce {wdLabel} :</span>
                  <DispoChip dispo={data?.rd?.disponibilites} date={day} testId="collab-fit-dispo" />
                </div>
                {candidates.map((c, i) => {
                  const isTop = c.spot_id === topId && c.has_history;
                  return (
                    <div key={c.spot_id} data-testid={`collab-fit-row-${c.spot_id}`}
                      className={`rounded-xl border p-3 ${isTop ? "border-[var(--shariti-orange)] bg-[#FFF7F1]" : c.has_history ? "border-[var(--border-soft)] bg-white" : "border-dashed border-[var(--border-soft)] bg-[var(--surface-2)]"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] font-bold text-muted-ink">{i + 1}.</span>
                            <span className="break-words text-sm font-bold text-ink">{c.spot_name}</span>
                            {isTop && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2 py-0.5 text-[10px] font-bold text-white"><Trophy className="h-3 w-3" /> Meilleur choix</span>}
                            {c.non_bookable && <span className="inline-flex items-center gap-1 rounded-full border border-[#D64545] bg-[rgba(214,69,69,0.12)] px-2 py-0.5 text-[10px] font-bold text-[#9B2226]"><Ban className="h-3 w-3" /> {c.status}</span>}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-ink">
                            <MapPin className="h-3 w-3" />{[c.ville, c.dept ? `(${c.dept})` : null].filter(Boolean).join(" ") || "—"}
                          </div>
                        </div>
                        {onOpenSpot && (
                          <button onClick={() => onOpenSpot(c.spot_id)} data-testid={`collab-fit-open-${c.spot_id}`}
                            className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline">
                            <ExternalLink className="h-3 w-3" /> Fiche
                          </button>
                        )}
                      </div>

                      {c.has_history ? (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {c.weekday_days > 0 ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.14)] px-2 py-0.5 text-[11px] font-bold text-[#0F5A2F]" data-testid={`collab-fit-wd-${c.spot_id}`}>
                              <TrendingUp className="h-3 w-3" /> {wdLabel} : {c.weekday_valid} validé{c.weekday_valid > 1 ? "s" : ""} · {c.weekday_days} jour{c.weekday_days > 1 ? "s" : ""} · moy {c.weekday_avg_valid_per_day}/j
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink">
                              Jamais un {wdLabel} ici
                            </span>
                          )}
                          <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 text-[11px] font-semibold text-ink">
                            Total : {c.valid_bulletins}/{c.total_bulletins} validés · {c.days_worked} jour{c.days_worked > 1 ? "s" : ""}
                          </span>
                          {c.best_day && (
                            <span className="rounded-full bg-[rgba(47,111,237,0.10)] px-2 py-0.5 text-[11px] font-semibold text-[#1D4ED8]">
                              Record : {c.best_day.valid} le {frenchDate(c.best_day.date)}
                            </span>
                          )}
                          {c.weekday_edge && (
                            <span className="rounded-full bg-[rgba(238,114,46,0.14)] px-2 py-0.5 text-[11px] font-bold text-[#8A3A10]">
                              Fort le {wdLabel}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2 text-[11px] text-muted-ink">Aucun historique de ce collaborateur sur ce spot.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Puce de RAPPEL de disponibilité déclarée d'un collaborateur pour un jour donné.
// Purement visuel — n'impacte aucun calcul de planning.
function DispoChip({ dispo, date, testId }) {
  const entry = dispoForDate(dispo, date);
  const { tone, label } = dispoBadge(entry);
  const cls = {
    ok: "bg-[rgba(31,122,68,0.12)] text-[#0F5A2F]",
    partial: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8]",
    off: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
    none: "bg-[var(--surface-2)] text-muted-ink",
  }[tone];
  const Icon = tone === "off" ? Ban : tone === "none" ? CalendarClock : tone === "ok" ? CheckCircle2 : Clock;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}
      title={`Disponibilité déclarée : ${label}`} data-testid={testId}>
      <Icon className="h-3 w-3" /> {label}
    </span>
  );
}


// ===================================================================
// Distribution IA de la semaine : on coche les collaborateurs qui travaillent,
// l'IA place chacun sur son meilleur spot chaque jour (rendement du même jour de
// semaine prioritaire + binômes habituels + disponibilités déclarées), puis
// application en 1 clic. Aperçu NON destructif avant validation.
// Accessible aux profils autorisés « Gérer les équipes » (dont la chargée des
// opérations en lecture seule) — le bouton d'ouverture est déjà protégé côté page.
// ===================================================================
function AutoDistributeDialog({ open, onOpenChange, week, onApplied, onOpenHistory }) {
  const [step, setStep] = useState("select");      // "select" | "preview"
  const [rds, setRds] = useState([]);
  const [loadingRds, setLoadingRds] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [replaceSelected, setReplaceSelected] = useState(false);
  const [sugMap, setSugMap] = useState({});          // id -> info « déjà positionné récemment »
  const [lookback, setLookback] = useState(3);       // fenêtre de suggestion (semaines)
  const [loadingSug, setLoadingSug] = useState(false);

  // Un collaborateur est « actif » s'il n'est ni archivé, ni en pause, ni sorti de
  // l'effectif, ni fiche de démonstration.
  const isActiveRd = (r) =>
    !r?.is_demo && !r?.off_roster && r?.active !== false &&
    !["paused", "archived"].includes(r?.statut_prerec || "active");

  // Signal « travaille cette semaine » : au moins un jour déclaré disponible.
  const hasWeekAvailability = (r) => {
    const jours = (r?.disponibilites && r.disponibilites.jours) || {};
    return Object.values(jours).some((v) => v && (v.mode === "journee" || v.mode === "creneau"));
  };

  // SÉLECTION ASSISTÉE : le serveur renvoie les collaborateurs déjà positionnés sur
  // les dernières semaines (encore actifs). On les pré-coche automatiquement pour
  // éviter la sélection manuelle un-à-un. À défaut, on retombe sur les dispos déclarées.
  const loadSuggested = async (lb, listForFallback) => {
    setLoadingSug(true);
    try {
      const { data } = await api.get("/planning/auto-distribute/suggested-rds",
        { params: { week, lookback_weeks: lb } });
      const map = {};
      (data.rds || []).forEach((s) => { map[s.id] = s; });
      setSugMap(map);
      const ids = Object.keys(map);
      if (ids.length > 0) {
        setSelected(new Set(ids));
      } else {
        const decl = (listForFallback || []).filter((r) => isActiveRd(r) && hasWeekAvailability(r));
        setSelected(new Set(decl.map((r) => r.id)));
      }
    } catch {
      setSugMap({});
    } finally {
      setLoadingSug(false);
    }
  };

  // Chargement de l'effectif + suggestions à l'ouverture.
  useEffect(() => {
    if (!open) return;
    setStep("select"); setQuery(""); setPreview(null); setReplaceSelected(false);
    setSugMap({}); setLookback(3);
    let cancel = false;
    setLoadingRds(true);
    (async () => {
      try {
        const { data } = await api.get("/rds", { params: { limit: 1000 } });
        if (cancel) return;
        const list = (data || []).filter((r) => r && r.id);
        list.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
        setRds(list);
        await loadSuggested(3, list);
      } catch {
        if (!cancel) { setRds([]); setSelected(new Set()); }
      } finally {
        if (!cancel) setLoadingRds(false);
      }
    })();
    return () => { cancel = true; };
  }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const changeLookback = (lb) => { setLookback(lb); loadSuggested(lb, rds); };

  // Collaborateurs suggérés affichés EN PREMIER (par positionnement le plus récent).
  const ordered = useMemo(() => {
    const sug = [], rest = [];
    rds.forEach((r) => (sugMap[r.id] ? sug : rest).push(r));
    sug.sort((a, b) => (sugMap[b.id]?.last_date || "").localeCompare(sugMap[a.id]?.last_date || ""));
    return [...sug, ...rest];
  }, [rds, sugMap]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter((r) =>
      (r.name || "").toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q));
  }, [ordered, query]);

  const sugCount = Object.keys(sugMap).length;
  const selectSuggested = () => setSelected(new Set(Object.keys(sugMap)));

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelected((prev) => {
    const next = new Set(prev);
    filtered.forEach((r) => next.add(r.id));
    return next;
  });
  const clearAll = () => setSelected(new Set());

  const weekEnd = week ? addDays(week, 6) : null;

  const generate = async () => {
    if (selected.size === 0) {
      toast.error("Cochez au moins un collaborateur qui travaille cette semaine.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/planning/auto-distribute", {
        week, rd_ids: Array.from(selected), apply: false, replace_selected: replaceSelected,
      });
      setPreview(data);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de générer la distribution.");
    } finally {
      setGenerating(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/planning/auto-distribute", {
        week, rd_ids: Array.from(selected), apply: true, replace_selected: replaceSelected,
      });
      toast.success(`${data.applied || 0} affectation(s) créée(s) « au crayon ».`);
      onApplied && onApplied();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'appliquer la distribution.");
    } finally {
      setApplying(false);
    }
  };

  const lvl = preview?.levels || { excellent: 0, bon: 0, nouveau: 0 };
  const daysWithSpots = (preview?.days || []).filter((d) => (d.spots || []).length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden" data-testid="auto-distribute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--shariti-orange)]" /> Distribution IA de la semaine
            {onOpenHistory && (
              <Button type="button" variant="ghost" size="sm" onClick={onOpenHistory}
                className="ml-auto h-8 gap-1 text-[12px] text-muted-ink hover:text-ink" data-testid="auto-dist-open-history">
                <History className="h-4 w-4" /> Journal IA
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="text-[12px] leading-relaxed text-muted-ink" data-testid="auto-dist-week">
          Semaine du <b className="text-ink">{week ? frenchDate(week) : "—"}</b>
          {weekEnd ? <> au <b className="text-ink">{frenchDate(weekEnd)}</b></> : null}
          {" · "}L'IA place chaque collaborateur coché sur son meilleur spot chaque jour
          (rendement du même jour prioritaire, binômes habituels, disponibilités déclarées respectées).
        </div>

        {/* ============ ÉTAPE 1 : sélection des collaborateurs ============ */}
        {step === "select" && (
          <>
            <div className="mt-1 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filtrer par nom ou matricule…"
                  className="h-10 bg-white pl-9" data-testid="auto-dist-search" />
              </div>
              <Button type="button" variant="secondary" size="sm" onClick={selectAll}
                className="border border-[var(--border-soft)] bg-white" data-testid="auto-dist-select-all">
                Tout cocher
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={clearAll}
                className="border border-[var(--border-soft)] bg-white" data-testid="auto-dist-clear-all">
                Tout décocher
              </Button>
            </div>

            {/* Sélection ASSISTÉE : pré-coche les collaborateurs déjà positionnés */}
            <div className="mt-1 flex flex-wrap items-center gap-2 rounded-lg border border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.06)] px-2.5 py-1.5 text-[12px]" data-testid="auto-dist-suggest-bar">
              <Sparkles className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
              <span className="text-ink">
                {loadingSug ? "Recherche des collaborateurs récemment positionnés…"
                  : sugCount > 0
                    ? <><b>{sugCount}</b> déjà positionné(s) → pré-coché(s)</>
                    : "Aucun positionné sur la période — cochez manuellement."}
              </span>
              <span className="ml-auto flex items-center gap-1 text-muted-ink">
                Sur les
                {[1, 2, 3, 4].map((n) => (
                  <button key={n} type="button" onClick={() => changeLookback(n)} disabled={loadingSug}
                    className={`h-6 w-6 rounded-md border text-[11px] font-semibold transition-colors ${lookback === n ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white text-ink hover:border-[var(--shariti-orange)]"}`}
                    data-testid={`auto-dist-lookback-${n}`}>{n}</button>
                ))}
                <span>dern. sem.</span>
              </span>
              {sugCount > 0 && (
                <Button type="button" variant="secondary" size="sm" onClick={selectSuggested}
                  className="h-7 border border-[var(--border-soft)] bg-white" data-testid="auto-dist-select-suggested">
                  Cocher les suggérés
                </Button>
              )}
            </div>

            <div className="mt-1 flex items-center justify-between text-[12px]">
              <span className="font-semibold text-ink" data-testid="auto-dist-selected-count">
                {selected.size} collaborateur(s) coché(s)
              </span>
              <span className="text-muted-ink">{filtered.length} affiché(s)</span>
            </div>

            {!loadingRds && selected.size === 0 && (
              <div className="rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[12px] text-muted-ink" data-testid="auto-dist-select-hint">
                Cochez les collaborateurs qui <b className="text-ink">travaillent cette semaine</b>. Astuce : la pré-sélection propose ceux <b className="text-ink">déjà positionnés</b> les dernières semaines — ajustez la fenêtre ci-dessus ou utilisez « Tout cocher ».
              </div>
            )}

            <div className="mt-1 min-h-0 flex-1 overflow-y-auto rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-1.5" data-testid="auto-dist-rd-grid">
              {loadingRds ? (
                <div className="flex items-center justify-center gap-2 py-10 text-muted-ink">
                  <Loader2 className="h-4 w-4 animate-spin" /> Chargement des collaborateurs…
                </div>
              ) : filtered.length === 0 ? (
                <div className="grid place-items-center py-10 text-center text-sm text-muted-ink">
                  <UserRound className="mb-2 h-8 w-8 opacity-40" /> Aucun collaborateur trouvé.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {filtered.map((r) => {
                    const on = selected.has(r.id);
                    return (
                      <button key={r.id} type="button" onClick={() => toggle(r.id)}
                        data-testid={`auto-dist-rd-${r.id}`}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.10)]" : "border-[var(--border-soft)] bg-white hover:border-[var(--shariti-orange)]"}`}>
                        <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-md border ${on ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white"}`}>
                          {on && <Check className="h-3.5 w-3.5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-ink">{r.name}</span>
                          <span className="block truncate text-[11px] text-muted-ink">{r.code || "—"}</span>
                        </span>
                        {sugMap[r.id] && (
                          <span className="shrink-0 rounded-full bg-[rgba(31,157,85,0.14)] px-1.5 py-0.5 text-[10px] font-semibold text-[#0F5A2F]" data-testid={`auto-dist-sug-badge-${r.id}`}>
                            {sugMap[r.id].last_week ? "sem. dernière" : `${sugMap[r.id].weeks} sem.`}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ============ ÉTAPE 2 : aperçu de la proposition ============ */}
        {step === "preview" && preview && (
          <div className="mt-1 min-h-0 flex-1 overflow-y-auto pr-0.5" data-testid="auto-dist-preview">
            {/* Bandeau récapitulatif */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(238,114,46,0.12)] px-2.5 py-1 text-[12px] font-bold text-[#8A3A10]">
                {preview.assignment_count} placement(s)
              </span>
              <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2.5 py-1 text-[12px] font-semibold text-ink">
                {preview.rd_count} collaborateur(s) · {preview.spot_day_count} emplacement(s)-jour
              </span>
              {lvl.excellent > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(31,157,85,0.14)] px-2.5 py-1 text-[12px] font-bold text-[#0F5A2F]">
                  <TrendingUp className="h-3.5 w-3.5" /> {lvl.excellent} excellent(s)
                </span>
              )}
              {lvl.bon > 0 && (
                <span className="rounded-full bg-[rgba(47,111,237,0.12)] px-2.5 py-1 text-[12px] font-semibold text-[#1D4ED8]">{lvl.bon} bon(s)</span>
              )}
              {lvl.nouveau > 0 && (
                <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2.5 py-1 text-[12px] font-semibold text-muted-ink">{lvl.nouveau} nouveau(x)</span>
              )}
            </div>

            {preview.message && (
              <div className="mt-2 rounded-xl border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-center text-sm text-muted-ink" data-testid="auto-dist-empty-msg">
                {preview.message}
              </div>
            )}

            {/* Jours empilés (lundi → samedi) — mise en page verticale, pas de débordement */}
            <div className="mt-2 space-y-2.5">
              {daysWithSpots.map((d) => (
                <div key={d.date} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`auto-dist-day-${d.date}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" />
                    <span className="text-sm font-bold capitalize text-ink">{d.weekday_label}</span>
                    <span className="text-[11px] text-muted-ink">{frenchDate(d.date)}</span>
                  </div>
                  <div className="space-y-2">
                    {d.spots.map((sp) => (
                      <div key={sp.spot_id} className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5" data-testid={`auto-dist-spot-${d.date}-${sp.spot_id}`}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Store className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
                          <span className="break-words text-[13px] font-bold text-ink">{sp.spot_name}</span>
                          <span className="text-[11px] text-muted-ink">{[sp.ville, sp.dept ? `(${sp.dept})` : null].filter(Boolean).join(" ")}</span>
                        </div>
                        {(sp.members || []).length === 0 ? (
                          <div className="mt-1 text-[11px] italic text-muted-ink">Aucun collaborateur proposé.</div>
                        ) : (
                          <div className="mt-1.5 space-y-1.5">
                            {sp.members.map((m) => (
                              <div key={m.rd_id} className="flex items-start gap-2 rounded-md bg-white p-2" data-testid={`auto-dist-member-${d.date}-${sp.spot_id}-${m.rd_id}`}>
                                <span className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${m.level === "excellent" ? "bg-[#1F9D55]" : m.level === "bon" ? "bg-[#2F6FED]" : "bg-[#9AA1A9]"}`} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className="text-[13px] font-semibold text-ink">{m.rd_name}</span>
                                    {m.code_rd && <span className="text-[10px] text-muted-ink">{m.code_rd}</span>}
                                    <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">
                                      <Clock className="h-3 w-3" /> {m.creneau_debut}–{m.creneau_fin}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 break-words text-[11px] text-muted-ink">{m.reason}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Collaborateurs non placés (jour indispo / pas d'emplacement, etc.) */}
            {(preview.unplaced || []).length > 0 && (
              <div className="mt-2.5 rounded-xl border border-[rgba(214,69,69,0.35)] bg-[rgba(214,69,69,0.06)] p-3" data-testid="auto-dist-unplaced">
                <div className="mb-1 flex items-center gap-1.5 text-[12px] font-bold text-[#9B2226]">
                  <AlertTriangle className="h-4 w-4" /> {preview.unplaced.length} non placé(s)
                </div>
                <ul className="space-y-0.5 text-[11px] text-[#9B2226]">
                  {preview.unplaced.slice(0, 60).map((u, i) => (
                    <li key={`${u.rd_id}-${u.date}-${i}`}>
                      <b>{u.rd_name}</b> · {frenchDate(u.date)} — {u.reason}
                    </li>
                  ))}
                  {preview.unplaced.length > 60 && <li>… et {preview.unplaced.length - 60} de plus.</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-[12px] text-muted-ink">
            <Switch checked={replaceSelected} onCheckedChange={setReplaceSelected}
              disabled={step === "preview"} data-testid="auto-dist-replace-switch" />
            Réattribuer proprement (effacer d'abord les affectations existantes des cochés)
          </label>
          <div className="flex items-center gap-2">
            {step === "preview" && (
              <Button type="button" variant="secondary" onClick={() => setStep("select")}
                className="border border-[var(--border-soft)] bg-white" data-testid="auto-dist-back">
                Retour
              </Button>
            )}
            {step === "select" ? (
              <Button type="button" onClick={generate} disabled={generating || selected.size === 0}
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" data-testid="auto-dist-generate">
                {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyse…</> : <><Sparkles className="mr-2 h-4 w-4" /> Générer l'aperçu</>}
              </Button>
            ) : (
              <Button type="button" onClick={apply} disabled={applying || (preview?.assignment_count || 0) === 0}
                className="bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" data-testid="auto-dist-apply">
                {applying ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Application…</> : <><CheckCircle2 className="mr-2 h-4 w-4" /> Appliquer ({preview?.assignment_count || 0})</>}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ===================================================================
// Journal IA : historique des distributions appliquées. On peut REVOIR le détail
// (jours → spots → collaborateurs, avec l'état actuel de chaque affectation) et
// ANNULER une distribution. L'annulation ne supprime que les affectations
// « au crayon » créées par le run et ENCORE non engagées ; elle protège celles
// qui ont été pointées, déclarées à la coordination ou déjà facturées (bulletin).
// ===================================================================
function AutoDistributeHistoryDialog({ open, onOpenChange, week, onChanged }) {
  const [mode, setMode] = useState("list");        // "list" | "detail"
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekOnly, setWeekOnly] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [undoingId, setUndoingId] = useState(null);
  const [confirmRun, setConfirmRun] = useState(null);   // run à annuler (confirmation)

  const fetchRuns = async () => {
    setLoading(true);
    try {
      const params = {};
      if (weekOnly && week) params.week = week;
      const { data } = await api.get("/planning/auto-distribute/history", { params });
      setRuns(data.runs || []);
    } catch {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setMode("list"); setDetail(null); setConfirmRun(null);
    fetchRuns();
  }, [open, weekOnly]);   // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (runId) => {
    setDetailLoading(true); setMode("detail"); setDetail(null);
    try {
      const { data } = await api.get(`/planning/auto-distribute/history/${runId}`);
      setDetail(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir cette distribution.");
      setMode("list");
    } finally {
      setDetailLoading(false);
    }
  };

  const doUndo = async (runId) => {
    setUndoingId(runId);
    try {
      const { data } = await api.post(`/planning/auto-distribute/${runId}/undo`);
      if (data.skipped > 0)
        toast.success(`${data.deleted} affectation(s) annulée(s) · ${data.skipped} protégée(s) (déjà engagées).`);
      else
        toast.success(`${data.deleted} affectation(s) annulée(s).`);
      setConfirmRun(null);
      onChanged && onChanged();
      await fetchRuns();
      if (mode === "detail" && detail?.id === runId) await openDetail(runId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'annuler cette distribution.");
    } finally {
      setUndoingId(null);
    }
  };

  const statusBadge = (s) => {
    if (s === "annule") return <span className="rounded-full bg-[rgba(24,24,24,0.08)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink">Annulée</span>;
    if (s === "annule_partiel") return <span className="rounded-full bg-[rgba(214,138,20,0.16)] px-2 py-0.5 text-[11px] font-semibold text-[#8A5A10]">Annulée en partie</span>;
    return <span className="rounded-full bg-[rgba(31,157,85,0.16)] px-2 py-0.5 text-[11px] font-semibold text-[#0F5A2F]">Appliquée</span>;
  };

  const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) + " à " +
             d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-3xl flex-col overflow-hidden" data-testid="auto-dist-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5 text-[var(--shariti-orange)]" />
            {mode === "detail" ? "Détail de la distribution" : "Journal IA — distributions appliquées"}
          </DialogTitle>
        </DialogHeader>

        {/* ============ LISTE ============ */}
        {mode === "list" && (
          <>
            <div className="flex items-center justify-between gap-2">
              <label className="flex cursor-pointer items-center gap-2 text-[12px] text-muted-ink">
                <Switch checked={weekOnly} onCheckedChange={setWeekOnly} data-testid="auto-dist-history-weekonly" />
                Semaine affichée uniquement
              </label>
              <Button type="button" variant="ghost" size="sm" onClick={fetchRuns}
                className="h-8 gap-1 text-[12px] text-muted-ink" data-testid="auto-dist-history-refresh">
                <RotateCcw className="h-3.5 w-3.5" /> Rafraîchir
              </Button>
            </div>

            <div className="mt-1 min-h-0 flex-1 overflow-y-auto" data-testid="auto-dist-history-list">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-muted-ink">
                  <Loader2 className="h-4 w-4 animate-spin" /> Chargement du journal…
                </div>
              ) : runs.length === 0 ? (
                <div className="grid place-items-center py-12 text-center text-sm text-muted-ink">
                  <History className="mb-2 h-8 w-8 opacity-40" />
                  Aucune distribution IA appliquée{weekOnly ? " sur cette semaine" : ""} pour l'instant.
                </div>
              ) : (
                <div className="space-y-2">
                  {runs.map((r) => (
                    <div key={r.id} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`auto-dist-history-run-${r.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[13px] font-bold text-ink">Semaine du {frenchDate(r.week_monday)}</span>
                        {statusBadge(r.status)}
                        <span className="ml-auto text-[11px] text-muted-ink">{fmtDateTime(r.applied_at)} · {r.applied_by_name}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="rounded-full bg-[rgba(238,114,46,0.12)] px-2 py-0.5 font-bold text-[#8A3A10]">{r.assignment_count} placement(s)</span>
                        <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 font-semibold text-ink">{r.rd_count} collaborateur(s)</span>
                        {r.levels?.excellent > 0 && <span className="rounded-full bg-[rgba(31,157,85,0.14)] px-2 py-0.5 font-semibold text-[#0F5A2F]">{r.levels.excellent} excellent(s)</span>}
                        {r.status !== "applique" && (r.deleted_count > 0 || r.skipped_count > 0) && (
                          <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 font-semibold text-muted-ink">
                            {r.deleted_count} annulée(s){r.skipped_count > 0 ? ` · ${r.skipped_count} protégée(s)` : ""}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Button type="button" variant="secondary" size="sm" onClick={() => openDetail(r.id)}
                          className="h-8 border border-[var(--border-soft)] bg-white" data-testid={`auto-dist-history-review-${r.id}`}>
                          <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Revoir
                        </Button>
                        {r.status === "applique" && (
                          <Button type="button" variant="secondary" size="sm" onClick={() => setConfirmRun(r)}
                            disabled={undoingId === r.id}
                            className="h-8 border border-[rgba(214,69,69,0.4)] bg-white text-[#9B2226] hover:bg-[rgba(214,69,69,0.06)]" data-testid={`auto-dist-history-undo-${r.id}`}>
                            {undoingId === r.id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Undo2 className="mr-1.5 h-3.5 w-3.5" />} Annuler
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ============ DÉTAIL ============ */}
        {mode === "detail" && (
          <div className="min-h-0 flex-1 overflow-y-auto pr-0.5" data-testid="auto-dist-history-detail">
            {detailLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-muted-ink">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement du détail…
              </div>
            ) : detail ? (
              <>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-bold text-ink">Semaine du {frenchDate(detail.week_monday)}</span>
                  {statusBadge(detail.status)}
                  <span className="ml-auto text-[11px] text-muted-ink">{fmtDateTime(detail.applied_at)} · {detail.applied_by_name}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                  <span className="rounded-full bg-[rgba(24,24,24,0.06)] px-2 py-0.5 font-semibold text-ink">{detail.state_counts?.present || 0} présente(s)</span>
                  {detail.state_counts?.protegee > 0 && <span className="rounded-full bg-[rgba(47,111,237,0.12)] px-2 py-0.5 font-semibold text-[#1D4ED8]">{detail.state_counts.protegee} engagée(s)</span>}
                  {detail.state_counts?.supprimee > 0 && <span className="rounded-full bg-[rgba(214,69,69,0.10)] px-2 py-0.5 font-semibold text-[#9B2226]">{detail.state_counts.supprimee} supprimée(s)</span>}
                </div>
                {detail.undone_by_name && (
                  <div className="mt-1 text-[11px] text-muted-ink">Annulée par {detail.undone_by_name} le {fmtDateTime(detail.undone_at)}.</div>
                )}
                {detail.replace_selected && (
                  <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] p-2 text-[11px] text-muted-ink">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Cette distribution a utilisé « réattribuer proprement » : l'annulation ne restaure pas les affectations effacées au préalable.
                  </div>
                )}

                <div className="mt-2 space-y-2.5">
                  {(detail.days || []).map((d) => (
                    <div key={d.date} className="rounded-xl border border-[var(--border-soft)] bg-white p-3">
                      <div className="mb-2 flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" />
                        <span className="text-sm font-bold capitalize text-ink">{d.weekday_label}</span>
                        <span className="text-[11px] text-muted-ink">{frenchDate(d.date)}</span>
                      </div>
                      <div className="space-y-2">
                        {(d.spots || []).map((sp) => (
                          <div key={sp.spot_id} className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <Store className="h-3.5 w-3.5 shrink-0 text-muted-ink" />
                              <span className="break-words text-[13px] font-bold text-ink">{sp.spot_name}</span>
                              <span className="text-[11px] text-muted-ink">{[sp.ville, sp.dept ? `(${sp.dept})` : null].filter(Boolean).join(" ")}</span>
                            </div>
                            <div className="mt-1.5 space-y-1.5">
                              {(sp.members || []).map((m) => (
                                <div key={m.affectation_id} className="flex items-start gap-2 rounded-md bg-white p-2">
                                  <span className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${m.state === "supprimee" ? "bg-[#9AA1A9]" : m.state === "protegee" ? "bg-[#2F6FED]" : "bg-[#1F9D55]"}`} />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span className={`text-[13px] font-semibold ${m.state === "supprimee" ? "text-muted-ink line-through" : "text-ink"}`}>{m.rd_name}</span>
                                      {m.code_rd && <span className="text-[10px] text-muted-ink">{m.code_rd}</span>}
                                      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">
                                        <Clock className="h-3 w-3" /> {m.creneau_debut}–{m.creneau_fin}
                                      </span>
                                      {m.state === "protegee" && <span className="rounded-full bg-[rgba(47,111,237,0.12)] px-1.5 py-0.5 text-[10px] font-semibold text-[#1D4ED8]">engagée</span>}
                                      {m.state === "supprimee" && <span className="rounded-full bg-[rgba(214,69,69,0.10)] px-1.5 py-0.5 text-[10px] font-semibold text-[#9B2226]">supprimée</span>}
                                    </div>
                                    <div className="mt-0.5 break-words text-[11px] text-muted-ink">{m.reason}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        )}

        <DialogFooter className="mt-2 flex-row items-center justify-between gap-2">
          {mode === "detail" ? (
            <Button type="button" variant="secondary" onClick={() => { setMode("list"); setDetail(null); }}
              className="border border-[var(--border-soft)] bg-white" data-testid="auto-dist-history-back">
              <ChevronLeft className="mr-1 h-4 w-4" /> Retour au journal
            </Button>
          ) : <span />}
          {mode === "detail" && detail?.status === "applique" && (
            <Button type="button" onClick={() => setConfirmRun(detail)} disabled={undoingId === detail.id}
              className="bg-[#9B2226] text-white hover:bg-[#7d1b1f]" data-testid="auto-dist-history-detail-undo">
              {undoingId === detail?.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />} Annuler cette distribution
            </Button>
          )}
        </DialogFooter>

        {/* Confirmation d'annulation */}
        <AlertDialog open={!!confirmRun} onOpenChange={(o) => !o && setConfirmRun(null)}>
          <AlertDialogContent data-testid="auto-dist-undo-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Annuler cette distribution IA ?</AlertDialogTitle>
              <AlertDialogDescription>
                Les affectations « au crayon » créées par cette distribution et <b>encore non engagées</b> seront supprimées.
                Celles qui ont été <b>pointées, déclarées à la coordination ou déjà facturées</b> seront conservées.
                {confirmRun?.replace_selected ? " Les affectations effacées par « réattribuer proprement » ne seront pas restaurées." : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="auto-dist-undo-cancel">Retour</AlertDialogCancel>
              <AlertDialogAction onClick={() => confirmRun && doUndo(confirmRun.id)}
                className="bg-[#9B2226] text-white hover:bg-[#7d1b1f]" data-testid="auto-dist-undo-confirm-btn">
                Annuler la distribution
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}


// Sous-vue « Semaine complète » / « Groupe » : propose tout le planning hebdo (meilleur
// spot par jour + horaires pré-remplis d'après l'historique) et permet de VALIDER en 1 clic.
// En mode groupe, on place ENSEMBLE le collaborateur pilote et ses binômes/trios habituels.
// La validation crée des Affectations (visibles Planning + Pointage).
function CollabWeekView({ rd, weekMonday, weekNumber, onOpenSpot, onValidated, groupMode = false }) {
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [selected, setSelected] = useState({});   // date -> spot_id
  const [included, setIncluded] = useState({});    // date -> bool
  const [hours, setHours] = useState({});          // date -> { debut, fin }
  const [glob, setGlob] = useState({ debut: "10:00", fin: "20:00" });
  const [submitting, setSubmitting] = useState(false);
  const [teammates, setTeammates] = useState([]);  // [{ id, name, code, shared_days }]
  const [members, setMembers] = useState({});      // rd_id -> bool (le pilote est toujours inclus)

  const fetchPlan = useCallback(async () => {
    if (!rd || !weekMonday) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/planning/collaborator-week-plan", { params: { rd_id: rd.id, week: weekMonday } });
      setPlan(data);
      const g = {
        debut: data?.suggested_creneau?.creneau_debut || "10:00",
        fin: data?.suggested_creneau?.creneau_fin || "20:00",
      };
      setGlob(g);
      const sel = {}, inc = {}, hrs = {};
      (data?.days || []).forEach((d) => {
        const ex = d.existing;
        // Spot pré-sélectionné : celui où la personne est DÉJÀ placée (pour repositionner),
        // sinon le spot recommandé, sinon le 1er candidat.
        sel[d.date] = ex?.spot_id || d.proposed_spot_id || (d.candidates[0]?.spot_id ?? null);
        // Tous les jours ayant des spots programmés sont ACTIFS par défaut (y compris ceux
        // déjà positionnés → on peut les repositionner ; décocher = retirer).
        inc[d.date] = d.scheduled_count > 0;
        hrs[d.date] = ex ? { debut: ex.creneau_debut || g.debut, fin: ex.creneau_fin || g.fin }
                         : { debut: g.debut, fin: g.fin };
      });
      setSelected(sel); setIncluded(inc); setHours(hrs);
    } catch { setPlan(null); }
    finally { setLoading(false); }
  }, [rd, weekMonday]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  // Détection des binômes/trios habituels (mode Groupe uniquement).
  useEffect(() => {
    if (!groupMode || !rd) { setTeammates([]); setMembers({}); return; }
    let cancel = false;
    setMembers({ [rd.id]: true });
    (async () => {
      try {
        const { data } = await api.get("/planning/collaborator-teammates", { params: { rd_id: rd.id, limit: 8 } });
        if (!cancel) setTeammates(data?.teammates || []);
      } catch { if (!cancel) setTeammates([]); }
    })();
    return () => { cancel = true; };
  }, [groupMode, rd]);   // eslint-disable-line react-hooks/exhaustive-deps

  const activeMembers = groupMode
    ? [{ id: rd.id, name: rd.name, code: rd.code }, ...teammates.filter((t) => members[t.id])]
    : [{ id: rd.id, name: rd.name, code: rd.code }];

  const applyGlobalHours = () => {
    setHours((prev) => {
      const next = { ...prev };
      (plan?.days || []).forEach((d) => { next[d.date] = { debut: glob.debut, fin: glob.fin }; });
      return next;
    });
    toast.success("Horaire appliqué à toute la semaine");
  };

  // Candidats du jour, en garantissant que le spot ACTUEL (si placé) est sélectionnable.
  const getDayCandidates = (d) => {
    const list = d.candidates || [];
    const ex = d.existing;
    if (ex?.spot_id && !list.some((c) => c.spot_id === ex.spot_id)) {
      return [{ spot_id: ex.spot_id, spot_name: ex.spot_name || ex.spot_id, booking_id: ex.booking_id,
                ville: null, dept: null, has_history: false, weekday_days: 0, affected_count: 0,
                affected_names: [] }, ...list];
    }
    return list;
  };

  // Explication CLAIRE de la recommandation (pourquoi ce spot ce jour-là).
  const prenom = (rd.name || "").split(" ")[0] || rd.name;
  const reasonFor = (cand, d) => {
    if (!cand) return null;
    if (cand.weekday_days > 0) {
      const rec = cand.weekday_best_day ? ` · record ${cand.weekday_best_day.valid} le ${frenchDate(cand.weekday_best_day.date)}` : "";
      return { tone: "good", title: `Excellent le ${d.weekday_label} ici`,
        detail: `${cand.weekday_valid} bulletin(s) validé(s) sur ${cand.weekday_days} ${d.weekday_label}(s) travaillé(s) — soit ${cand.weekday_avg_valid_per_day}/jour en moyenne${rec}.` };
    }
    if (cand.has_history) {
      return { tone: "ok", title: `Déjà à l'aise sur ce spot`,
        detail: `${cand.valid_bulletins} validé(s) sur ${cand.days_worked} jour(s) ici (${cand.avg_valid_per_day}/jour en moyenne)${cand.best_day ? `, meilleur jour : ${cand.best_day.valid} le ${frenchDate(cand.best_day.date)}` : ""} — mais jamais un ${d.weekday_label}.` };
    }
    return { tone: "neutral", title: `Sans historique ici`,
      detail: `${prenom} n'a encore jamais travaillé sur ce spot — proposé faute d'un meilleur choix parmi les spots programmés ce ${d.weekday_label}.` };
  };

  // Action calculée pour chaque jour : create | move | update | remove | group | none.
  const dayActions = (plan?.days || []).map((d) => {
    const isEmpty = d.scheduled_count === 0;
    const ex = d.existing;
    const inc = !!included[d.date] && !isEmpty;
    const selSpot = selected[d.date];
    const cand = getDayCandidates(d).find((c) => c.spot_id === selSpot) || null;
    const h = hours[d.date] || glob;
    let action = "none";
    if (isEmpty) action = "none";
    else if (groupMode) action = inc && cand?.booking_id ? "group" : "none";
    else if (!inc) action = ex ? "remove" : "none";
    else if (!ex) action = cand?.booking_id ? "create" : "none";
    else {
      const sameSpot = ex.spot_id === selSpot;
      const hoursChanged = (ex.creneau_debut || glob.debut) !== h.debut || (ex.creneau_fin || glob.fin) !== h.fin;
      action = !sameSpot ? (cand?.booking_id ? "move" : "none") : (hoursChanged ? "update" : "none");
    }
    return { date: d.date, action, booking_id: cand?.booking_id, existing: ex, h };
  });

  const changeCount = groupMode
    ? dayActions.filter((a) => a.action === "group").length
    : dayActions.filter((a) => a.action !== "none").length;
  const totalAffectations = groupMode ? changeCount * activeMembers.length : changeCount;
  const actionByDate = Object.fromEntries(dayActions.map((a) => [a.date, a.action]));

  const validate = async () => {
    if (!plan) return;
    const jobs = dayActions.filter((a) => a.action !== "none");
    if (!jobs.length) { toast.error("Aucun changement à enregistrer."); return; }
    setSubmitting(true);
    let created = 0, moved = 0, updated = 0, removed = 0, skipped = 0, failed = 0;
    for (const j of jobs) {
      try {
        if (j.action === "group") {
          for (const m of activeMembers) {
            try {
              await api.post("/pointage/affectations", { booking_id: j.booking_id, rd_id: m.id, creneau_debut: j.h.debut, creneau_fin: j.h.fin });
              created += 1;
            } catch (e) { if (e?.response?.status === 409) skipped += 1; else failed += 1; }
          }
        } else if (j.action === "create") {
          await api.post("/pointage/affectations", { booking_id: j.booking_id, rd_id: rd.id, creneau_debut: j.h.debut, creneau_fin: j.h.fin });
          created += 1;
        } else if (j.action === "move") {
          await api.delete(`/pointage/affectations/${j.existing.id}`);
          await api.post("/pointage/affectations", { booking_id: j.booking_id, rd_id: rd.id, creneau_debut: j.h.debut, creneau_fin: j.h.fin });
          moved += 1;
        } else if (j.action === "update") {
          await api.put(`/pointage/affectations/${j.existing.id}`, { creneau_debut: j.h.debut, creneau_fin: j.h.fin });
          updated += 1;
        } else if (j.action === "remove") {
          await api.delete(`/pointage/affectations/${j.existing.id}`);
          removed += 1;
        }
      } catch (e) { failed += 1; }
    }
    setSubmitting(false);
    const parts = [];
    if (created) parts.push(`${created} ajouté(s)`);
    if (moved) parts.push(`${moved} déplacé(s)`);
    if (updated) parts.push(`${updated} horaire(s) modifié(s)`);
    if (removed) parts.push(`${removed} retiré(s)`);
    if (skipped) parts.push(`${skipped} déjà en place`);
    if (failed) parts.push(`${failed} échec(s)`);
    if (created || moved || updated || removed) toast.success(`Planning mis à jour : ${parts.join(" · ")}`);
    else if (skipped) toast.info(`Rien à changer (${skipped} déjà en place).`);
    else toast.error("Aucun changement n'a pu être enregistré.");
    onValidated?.();
    fetchPlan();
  };

  if (loading) {
    return <div className="flex items-center justify-center gap-2 py-10 text-muted-ink" data-testid="collab-week-loading"><Loader2 className="h-4 w-4 animate-spin" /> Préparation du planning de la semaine…</div>;
  }
  if (!plan) {
    return <div className="py-10 text-center text-sm text-muted-ink">Impossible de charger la proposition de la semaine.</div>;
  }
  const anyScheduled = (plan.days || []).some((d) => d.scheduled_count > 0);

  return (
    <div className="space-y-3" data-testid="collab-week-view">
      {/* Horaire par défaut (pré-rempli d'après l'historique) */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-ink"><Clock className="h-4 w-4 text-[var(--shariti-orange)]" /> Horaire par défaut</div>
        <div className="flex items-center gap-1.5">
          <Input type="time" value={glob.debut} onChange={(e) => setGlob((g) => ({ ...g, debut: e.target.value }))} className="h-9 w-[130px] bg-white" data-testid="collab-week-global-debut" />
          <span className="text-muted-ink">→</span>
          <Input type="time" value={glob.fin} onChange={(e) => setGlob((g) => ({ ...g, fin: e.target.value }))} className="h-9 w-[130px] bg-white" data-testid="collab-week-global-fin" />
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={applyGlobalHours} className="h-9 shrink-0 border border-[var(--border-soft)] bg-white" data-testid="collab-week-apply-hours">
          Appliquer à la semaine
        </Button>
        {plan?.suggested_creneau?.source === "historique" && (
          <span className="w-full text-[11px] text-muted-ink sm:w-auto">D'après ses horaires habituels</span>
        )}
      </div>

      {/* Groupe à placer ensemble (mode Groupe) : pilote + binômes/trios historiques */}
      {groupMode && (
        <div className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid="collab-group-members">
          <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-ink"><Users className="h-4 w-4 text-[var(--shariti-orange)]" /> Groupe à placer ensemble</div>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-2.5 py-1 text-[11px] font-bold text-white" data-testid="collab-group-pilote">
              {rd.name} <span className="opacity-80">· pilote</span>
            </span>
            {teammates.length === 0 ? (
              <span className="self-center text-[11px] text-muted-ink">Aucun binôme historique détecté pour ce collaborateur.</span>
            ) : teammates.map((t) => {
              const on = !!members[t.id];
              return (
                <button key={t.id} type="button" onClick={() => setMembers((p) => ({ ...p, [t.id]: !p[t.id] }))}
                  data-testid={`collab-group-member-${t.id}`}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.12)] text-[#8A3A10]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:border-[var(--shariti-orange)]"}`}>
                  {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}{t.name} <span className="opacity-70">· {t.shared_days}j</span>
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 text-[11px] text-muted-ink">
            <b className="text-ink">{activeMembers.length}</b> personne(s) seront placées ensemble sur chaque spot retenu. Astuce : « {rd.name?.split(" ")[0]} · Nj » = nombre de jours déjà travaillés ensemble.
          </div>
        </div>
      )}

      {!anyScheduled ? (
        <div className="grid place-items-center py-8 text-center text-sm text-muted-ink" data-testid="collab-week-empty">
          <CalendarClock className="mb-2 h-8 w-8 opacity-40" />
          Aucun spot n'est positionné cette semaine dans le planning.
        </div>
      ) : (
        <div className="space-y-2" data-testid="collab-week-days">
          {plan.days.map((d) => {
            const isEmpty = d.scheduled_count === 0;
            const ex = d.existing;
            const inc = !!included[d.date] && !isEmpty;
            const dayCands = getDayCandidates(d);
            const cand = dayCands.find((c) => c.spot_id === selected[d.date]) || null;
            const action = actionByDate[d.date] || "none";
            const reason = reasonFor(cand, d);
            const reasonBox = reason && ({
              good: "border-l-2 border-[#1F9D55] bg-[rgba(31,157,85,0.08)] text-[#0F5A2F]",
              ok: "border-l-2 border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)] text-[#8A3A10]",
              neutral: "border-l-2 border-[var(--border-soft)] bg-[var(--surface-2)] text-muted-ink",
            }[reason.tone]);
            const cardCls = isEmpty
              ? "border-[var(--border-soft)] bg-[var(--surface-2)]"
              : (ex && !inc) ? "border-[#D64545]/50 bg-[rgba(214,69,69,0.05)]"
              : inc ? "border-[var(--shariti-orange)] bg-[#FFF7F1]"
              : "border-[var(--border-soft)] bg-white";
            return (
              <div key={d.date} data-testid={`collab-week-day-${d.date}`}
                className={`rounded-xl border p-3 ${cardCls}`}>
                {/* En-tête : jour + état actuel + interrupteur inclure/retirer */}
                <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-[12px] font-bold capitalize text-ink whitespace-nowrap">{d.weekday_label} <span className="font-normal text-muted-ink">{frenchDateShort(d.date)}</span></span>
                    <DispoChip dispo={plan?.rd?.disponibilites} date={d.date} testId={`collab-week-dispo-${d.date}`} />
                    {isEmpty && <span className="text-[11px] text-muted-ink">Aucun spot programmé ce jour</span>}
                    {!isEmpty && ex && (
                      <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-muted-ink">
                        <CheckCircle2 className="h-3 w-3 shrink-0 text-[#1F7A44]" /> Actuellement : <b className="truncate font-semibold text-ink">{ex.spot_name}</b> <span className="whitespace-nowrap">({ex.creneau_debut}–{ex.creneau_fin})</span>
                      </span>
                    )}
                  </div>
                  {!isEmpty && (
                    <Switch checked={inc} onCheckedChange={(v) => setIncluded((p) => ({ ...p, [d.date]: v }))} data-testid={`collab-week-toggle-${d.date}`} />
                  )}
                </div>

                {/* Jour retiré (était positionné, décoché) */}
                {!isEmpty && ex && !inc && (
                  <div className="mt-1.5 inline-flex items-center gap-1 rounded-lg bg-[rgba(214,69,69,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#9B2226]" data-testid={`collab-week-remove-${d.date}`}>
                    <Trash2 className="h-3 w-3" /> Sera retiré du planning ce jour
                  </div>
                )}

                {!isEmpty && inc && (
                  <div className="mt-2 space-y-2">
                    {/* Choix du spot — pleine largeur, tronqué proprement */}
                    <Select value={selected[d.date] || undefined} onValueChange={(v) => setSelected((p) => ({ ...p, [d.date]: v }))}>
                      <SelectTrigger className="h-10 w-full min-w-0 bg-white [&>span]:block [&>span]:truncate" data-testid={`collab-week-spot-${d.date}`}>
                        <SelectValue placeholder="Choisir un spot" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64 max-w-[min(90vw,32rem)]">
                        {dayCands.map((c) => (
                          <SelectItem key={c.spot_id} value={c.spot_id} className="pr-2">
                            <div className="flex w-full items-center gap-2">
                              <span className="min-w-0 flex-1 truncate">{c.spot_id === d.proposed_spot_id ? "★ " : ""}{c.spot_name}</span>
                              <span className={`shrink-0 text-[10px] font-semibold ${c.weekday_days > 0 ? "text-[#0F5A2F]" : "text-muted-ink"}`}>
                                {c.weekday_days > 0 ? `${c.weekday_avg_valid_per_day}/j le ${d.weekday_label}` : (c.has_history ? "déjà venu" : "jamais venu")}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* POURQUOI ce spot : explication claire avec chiffres */}
                    {reason && (
                      <div className={`rounded-lg px-2.5 py-1.5 text-[11px] ${reasonBox}`} data-testid={`collab-week-reason-${d.date}`}>
                        <span className="font-bold">{reason.title}.</span> {reason.detail}
                      </div>
                    )}

                    {/* Ville + occupation + fiche */}
                    {cand && (
                      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-muted-ink"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{[cand.ville, cand.dept ? `(${cand.dept})` : null].filter(Boolean).join(" ") || "—"}</span></span>
                        {cand.spot_id === d.proposed_spot_id && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--shariti-orange)] px-1.5 py-0.5 text-[10px] font-bold text-white"><Trophy className="h-3 w-3" /> Recommandé</span>}
                        {cand.affected_count > 0 && (
                          <span title={(cand.affected_names || []).join(", ")}
                            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(47,111,237,0.10)] px-1.5 py-0.5 text-[10px] font-semibold text-[#1D4ED8]"
                            data-testid={`collab-week-occupancy-${d.date}`}>
                            <Users className="h-3 w-3" /> {cand.affected_count} déjà sur place
                          </span>
                        )}
                        {cand.non_bookable && <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#D64545] bg-[rgba(214,69,69,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#9B2226]"><Ban className="h-3 w-3" /> {cand.status}</span>}
                        {onOpenSpot && <button type="button" onClick={() => onOpenSpot(cand.spot_id)} className="inline-flex shrink-0 items-center gap-1 text-[10px] font-semibold text-[var(--shariti-orange)] hover:underline"><ExternalLink className="h-3 w-3" /> Fiche</button>}
                      </div>
                    )}

                    {/* Ce qui va se passer (repositionnement) — mode simple uniquement */}
                    {!groupMode && ex && action === "move" && (
                      <div className="inline-flex items-center gap-1 rounded-lg bg-[rgba(238,114,46,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#8A3A10]" data-testid={`collab-week-move-${d.date}`}>
                        <ArrowRightLeft className="h-3 w-3" /> Sera déplacé de « {ex.spot_name} » vers « {cand?.spot_name} »
                      </div>
                    )}
                    {!groupMode && ex && action === "update" && (
                      <div className="inline-flex items-center gap-1 rounded-lg bg-[rgba(238,114,46,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#8A3A10]"><Clock className="h-3 w-3" /> Nouveaux horaires : {(hours[d.date] || glob).debut}–{(hours[d.date] || glob).fin}</div>
                    )}
                    {!groupMode && ex && action === "none" && (
                      <div className="inline-flex items-center gap-1 rounded-lg bg-[rgba(31,122,68,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#0F5A2F]"><CheckCircle2 className="h-3 w-3" /> Déjà en place ici — rien à changer</div>
                    )}

                    {/* Horaires du jour — ligne dédiée */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-ink"><Clock className="h-3.5 w-3.5" /> Horaires</span>
                      <div className="flex items-center gap-1.5">
                        <Input type="time" value={hours[d.date]?.debut || glob.debut} onChange={(e) => setHours((p) => ({ ...p, [d.date]: { ...(p[d.date] || glob), debut: e.target.value } }))} className="h-9 w-[130px] bg-white" data-testid={`collab-week-debut-${d.date}`} />
                        <span className="text-muted-ink">→</span>
                        <Input type="time" value={hours[d.date]?.fin || glob.fin} onChange={(e) => setHours((p) => ({ ...p, [d.date]: { ...(p[d.date] || glob), fin: e.target.value } }))} className="h-9 w-[130px] bg-white" data-testid={`collab-week-fin-${d.date}`} />
                      </div>
                    </div>
                    {(() => {
                      const entry = dispoForDate(plan?.rd?.disponibilites, d.date);
                      const deb = hours[d.date]?.debut || glob.debut;
                      const fin = hours[d.date]?.fin || glob.fin;
                      if (!creneauOutsideDispo(entry, deb, fin)) return null;
                      const { label } = dispoBadge(entry);
                      return (
                        <div className="inline-flex items-center gap-1 rounded-lg bg-[rgba(214,69,69,0.10)] px-2.5 py-1 text-[11px] font-semibold text-[#9B2226]" data-testid={`collab-week-dispo-warn-${d.date}`}>
                          <AlertTriangle className="h-3 w-3" /> Attention : hors de sa disponibilité déclarée ({label})
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Barre de validation */}
      <div className="sticky bottom-0 -mx-1 flex items-center justify-between gap-2 border-t border-[var(--border-soft)] bg-white/90 px-1 pt-3 backdrop-blur">
        <div className="text-[12px] text-muted-ink" data-testid="collab-week-summary">
          {changeCount > 0
            ? (groupMode
                ? <><b className="text-ink">{changeCount}</b> jour(s) × <b className="text-ink">{activeMembers.length}</b> pers. = <b className="text-ink">{totalAffectations}</b> affectation(s)</>
                : <><b className="text-ink">{changeCount}</b> changement(s) à enregistrer pour <b className="text-ink">{rd.name}</b></>)
            : "Rien à changer — ajustez un jour, un spot ou un horaire."}
        </div>
        <Button onClick={validate} disabled={submitting || changeCount === 0}
          className="bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" data-testid="collab-week-validate">
          {submitting ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Enregistrement…</> : <><Check className="mr-1.5 h-4 w-4" /> {groupMode ? "Valider le groupe" : "Enregistrer"}{changeCount ? ` (${groupMode ? totalAffectations : changeCount})` : ""}</>}
        </Button>
      </div>
    </div>
  );
}

function PlanningZoneBar({ data, user, navigate }) {
  if (!data) return null;
  const isAdmin = user?.role === "admin";
  const mine = (data.cdps || []).find((c) => c.id === data.my_cdp_id);

  const ZPill = ({ z }) => {
    const depts = z.departments || [];
    const title = z.is_split
      ? `${z.name} — départements de ce CDP : ${depts.join(", ") || "—"} · zone complète : ${(z.zone_departments || depts).join(", ")} — ${z.spot_count} spot(s)`
      : `${z.name} (${depts.join(", ") || "—"}) — ${z.spot_count} spot(s)`;
    return (
      <button
        onClick={() => navigate(`/spots?territoire=${z.id}`)}
        title={title}
        data-testid={`planning-zone-pill-${z.id}`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)] ${z.is_split ? "border-dashed border-[rgba(238,114,46,0.6)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] bg-white"}`}>
        <MapPin className="h-2.5 w-2.5 shrink-0 text-[var(--shariti-orange)]" />
        <span>{z.name}</span>
        {depts.length > 0 && <span className="font-medium text-muted-ink">· {depts.join(", ")}</span>}
        {z.is_split && <span className="ml-0.5 rounded-full bg-[rgba(238,114,46,0.15)] px-1 text-[8px] font-extrabold uppercase tracking-wide text-[#8A3A10]">partagée</span>}
      </button>
    );
  };

  return (
    <div className="sticky top-14 z-20 mb-3 lg:top-0" data-testid="planning-zone-bar">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-[var(--border-soft)] bg-white/90 px-3 py-2 shadow-[var(--shadow-sm)] backdrop-blur">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-muted-ink">
          <Map className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          {isAdmin ? "Zones par CDP actif" : "Votre périmètre"}
        </span>
        {!isAdmin ? (
          mine && mine.zones.length > 0
            ? <div className="flex flex-wrap gap-1.5">{mine.zones.map((z) => <ZPill key={z.id} z={z} />)}</div>
            : <span className="text-[11px] text-muted-ink">Aucune zone attribuée — à voir avec l'administrateur.</span>
        ) : (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {(data.cdps || []).map((c) => (
              <span key={c.id} className="inline-flex flex-wrap items-center gap-1.5" data-testid={`planning-zone-cdp-${c.id}`}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                <span className="text-[11px] font-semibold">{c.name}</span>
                {(c.associations || []).map((a) => (
                  <span key={a.id} data-testid={`planning-zone-assoc-${c.id}-${a.id}`} title={`Gère ${a.acronym}`}
                    className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white"
                    style={{ background: a.color || "#EE722E" }}>
                    {a.acronym}
                  </span>
                ))}
                {c.zones.length > 0
                  ? c.zones.map((z) => <ZPill key={z.id} z={z} />)
                  : <span className="text-[10px] text-muted-ink">—</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CdpTag({ ligne, cdps }) {
  const cdp = cdps.find((c) => c.id === ligne.cdp_id);
  if (!cdp) return <div className="mt-1 text-[11px] text-muted-ink">Titulaire : —</div>;
  const firstName = (cdp.name || "").split(" ")[0];
  const inactif = cdp.active === false;
  return (
    <div className="mt-1.5 flex items-center gap-1.5" data-testid={`ligne-cdp-${ligne.id}`}>
      <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: cdp.color }} />
      <span className="min-w-0 truncate text-[11px] font-bold leading-snug" title={cdp.name}>{firstName}</span>
      {inactif && (
        <span className="shrink-0 rounded-full bg-[#FBF1E3] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#B9770E]"
          title="CDP désactivé — emplacements trouvés conservés et toujours visibles" data-testid={`ligne-cdp-inactif-${ligne.id}`}>inactif</span>
      )}
    </div>
  );
}

// #4 — Réattribution d'une ligne à un(e) CDP directement depuis le planning.
function RemoveLigneDialog({ ligne, weekMonday, weekNumber, isAdmin, onClose, onDone }) {
  const [scope, setScope] = useState("this_week");
  const [pickState, setPickState] = useState({}); // monday ISO -> visible (bool)
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null); // {weeks:[{monday,label}], message}

  const open = !!ligne;
  const nom = ligne?.equipe_nom || ligne?.identifiant || "";
  const dd = ligne?.date_debut ? mondayOf(ligne.date_debut) : null;
  const df = ligne?.date_fin ? mondayOf(ligne.date_fin) : null;
  const excludedSet = useMemo(() => new Set(ligne?.weeks_excluded || []), [ligne]);

  const isVisibleWeek = (m) => {
    if (dd && m < dd) return false;
    if (df && m > df) return false;
    if (excludedSet.has(m)) return false;
    return true;
  };

  // Fenêtre du mode « Choisir » : englobe les trous connus + une portée future raisonnable.
  const pickWeeks = useMemo(() => {
    if (!ligne) return [];
    const anchors = [weekMonday, ...(ligne.weeks_excluded || [])];
    if (dd) anchors.push(dd);
    if (df) anchors.push(df);
    let start = anchors.reduce((a, b) => (b < a ? b : a), weekMonday);
    const floor = mondayOf(addDays(weekMonday, -7 * 8));
    if (start < floor) start = floor;
    let end = anchors.reduce((a, b) => (b > a ? b : a), mondayOf(addDays(weekMonday, 7 * 15)));
    const ceil = mondayOf(addDays(weekMonday, 7 * 30));
    if (end > ceil) end = ceil;
    const out = [];
    let cur = start;
    while (cur <= end && out.length < 60) { out.push(cur); cur = addDays(cur, 7); }
    return out;
  }, [ligne, weekMonday, dd, df]);

  useEffect(() => {
    if (!ligne) return;
    setScope("this_week");
    setConflict(null);
    const init = {};
    pickWeeks.forEach((m) => { init[m] = isVisibleWeek(m); });
    setPickState(init);
  }, [ligne]); // eslint-disable-line

  const computeNext = () => {
    const baseDebut = dd;
    const baseFin = df;
    if (scope === "delete_all") return { deleteAll: true };
    if (scope === "this_week") {
      const ex = new Set(excludedSet); ex.add(weekMonday);
      return { date_debut: baseDebut, date_fin: baseFin, weeks_excluded: [...ex] };
    }
    if (scope === "from_here") {
      const fin = mondayOf(addDays(weekMonday, -7)); // dernière semaine visible = précédente
      const ex = [...excludedSet].filter((m) => m < weekMonday);
      return { date_debut: baseDebut, date_fin: fin, weeks_excluded: ex };
    }
    // pick : appliquer chaque bascule sur l'enveloppe (début/fin) + trous
    let debut = baseDebut;
    let fin = baseFin;
    const ex = new Set(excludedSet);
    pickWeeks.forEach((m) => {
      const want = !!pickState[m];
      const wasVisible = isVisibleWeek(m);
      if (want && !wasVisible) {
        ex.delete(m);
        if (debut && m < debut) debut = m;
        if (fin && m > fin) fin = m;
      } else if (!want && wasVisible) {
        ex.add(m);
      }
    });
    return { date_debut: debut, date_fin: fin, weeks_excluded: [...ex] };
  };

  const submit = async () => {
    if (!ligne) return;
    setBusy(true); setConflict(null);
    const ligneId = ligne.id;
    // État à restaurer si l'utilisateur annule le retrait.
    const revert = {
      date_debut: ligne.date_debut || null,
      date_fin: ligne.date_fin || null,
      weeks_excluded: ligne.weeks_excluded || [],
    };
    try {
      const next = computeNext();
      if (next.deleteAll) {
        const { data: res } = await api.delete(`/admin/lignes/${ligneId}`);
        toast.success(res?.mode === "archived"
          ? `Ligne « ${nom} » archivée (${res.bookings} créneau(x) conservés)`
          : `Ligne « ${nom} » supprimée définitivement`);
      } else {
        await api.put(`/planning/lignes/${ligneId}/schedule`, next);
        const msg = scope === "this_week" ? `Ligne retirée de la semaine ${weekNumber ? `S${weekNumber}` : "affichée"}`
          : scope === "from_here" ? "Ligne arrêtée à partir de cette semaine"
            : "Semaines de la ligne mises à jour";
        // Retrait réversible : bouton « Rétablir » dans le toast.
        const undo = async () => {
          try {
            await api.put(`/planning/lignes/${ligneId}/schedule`, revert);
            toast.success("Retrait annulé — la ligne est rétablie");
            onDone();
          } catch { toast.error("Impossible de rétablir la ligne"); }
        };
        toast.success(msg, { action: { label: "Rétablir", onClick: undo }, duration: 10000 });
      }
      onClose();
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.bookings_conflict) {
        setConflict(d);
      } else {
        toast.error(typeof d === "string" ? d : "Opération impossible");
      }
    } finally { setBusy(false); }
  };

  const OPTIONS = [
    { v: "this_week", icon: CalendarDays, title: `Cette semaine uniquement${weekNumber ? ` (S${weekNumber})` : ""}`,
      desc: "Retire la ligne juste de cette semaine. Les autres semaines ne changent pas." },
    { v: "from_here", icon: History, title: "À partir de cette semaine",
      desc: "La ligne s'arrête : visible jusqu'à la semaine précédente, plus rien après." },
    { v: "pick", icon: ClipboardList, title: "Choisir les semaines…",
      desc: "Cochez / décochez précisément les semaines où la ligne apparaît." },
  ];
  if (isAdmin) OPTIONS.push({ v: "delete_all", icon: Trash2, title: "Supprimer complètement la ligne",
    desc: "Retire la ligne de TOUTES les semaines (archivée si des créneaux existent).", danger: true });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="remove-ligne-dialog">
        <DialogHeader>
          <DialogTitle>Retirer la ligne « {nom} »</DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-muted-ink">
          Chaque semaine est indépendante : choisissez où la suppression doit s'appliquer.
        </p>

        <div className="space-y-2" data-testid="remove-ligne-options">
          {OPTIONS.map((o) => {
            const active = scope === o.v;
            const Icon = o.icon;
            return (
              <button key={o.v} type="button" onClick={() => setScope(o.v)}
                data-testid={`remove-scope-${o.v}`}
                className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                  active
                    ? (o.danger ? "border-[#D64545] bg-[rgba(214,69,69,0.06)]" : "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]")
                    : "border-[var(--border-soft)] bg-white hover:bg-[var(--surface-2)]"}`}>
                <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border ${
                  active ? (o.danger ? "border-[#D64545] bg-[#D64545] text-white" : "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white") : "border-[var(--border-soft)]"}`}>
                  {active && <Check className="h-3 w-3" />}
                </span>
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${o.danger ? "text-[#D64545]" : "text-[var(--shariti-orange)]"}`} />
                <span className="min-w-0">
                  <span className={`block text-sm font-bold ${o.danger ? "text-[#9B2226]" : "text-ink"}`}>{o.title}</span>
                  <span className="block text-[11px] leading-snug text-muted-ink">{o.desc}</span>
                </span>
              </button>
            );
          })}
        </div>

        {scope === "pick" && (
          <div className="mt-1 max-h-56 space-y-0.5 overflow-y-auto rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-2 no-scrollbar" data-testid="remove-pick-weeks">
            {pickWeeks.map((m) => {
              const on = !!pickState[m];
              const isCurrent = m === weekMonday;
              return (
                <button key={m} type="button" onClick={() => setPickState((s) => ({ ...s, [m]: !s[m] }))}
                  data-testid={`remove-pick-${m}`}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-white ${isCurrent ? "ring-1 ring-[var(--shariti-orange)]" : ""}`}>
                  <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)] bg-white"}`}>
                    {on && <Check className="h-3 w-3" />}
                  </span>
                  <span className="font-semibold text-ink">S{isoWeekNumber(m)}</span>
                  <span className="text-muted-ink">{frenchDateShort(m)} – {frenchDateShort(addDays(m, 6))}</span>
                  {isCurrent && <span className="ml-auto rounded-full bg-[rgba(238,114,46,0.12)] px-1.5 text-[9px] font-bold uppercase text-[var(--shariti-orange)]">Semaine affichée</span>}
                  {!on && <span className="ml-auto text-[10px] font-semibold text-muted-ink">masquée</span>}
                </button>
              );
            })}
          </div>
        )}

        {conflict && (
          <div className="rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.07)] p-3 text-xs" data-testid="remove-ligne-conflict">
            <div className="mb-1 flex items-center gap-1.5 font-bold text-[#9B2226]">
              <AlertTriangle className="h-4 w-4" /> Créneaux réservés sur ces semaines
            </div>
            <p className="mb-1.5 text-[#7F1D1D]">{conflict.message}</p>
            <div className="flex flex-wrap gap-1.5">
              {(conflict.weeks || []).map((w) => (
                <span key={w.monday} className="rounded-full bg-white px-2 py-0.5 font-semibold text-[#9B2226]">{w.label}</span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="remove-ligne-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="remove-ligne-confirm"
            className={scope === "delete_all" ? "bg-[#D64545] text-white hover:bg-[#c23b3b]" : "bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {scope === "delete_all" ? "Supprimer définitivement" : "Appliquer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditPeriodDialog({ ligne, weekMonday, onClose, onDone }) {
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);
  const open = !!ligne;
  const nom = ligne?.equipe_nom || ligne?.identifiant || "";

  useEffect(() => {
    if (!ligne) return;
    setDateDebut(ligne.date_debut || "");
    setDateFin(ligne.date_fin || "");
    setConflict(null);
  }, [ligne]);

  const submit = async () => {
    if (!ligne) return;
    if (dateDebut && dateFin && mondayOf(dateFin) < mondayOf(dateDebut)) {
      toast.error("La semaine de fin doit être après (ou égale à) la semaine de début.");
      return;
    }
    setBusy(true); setConflict(null);
    try {
      await api.put(`/planning/lignes/${ligne.id}/schedule`, {
        date_debut: dateDebut ? mondayOf(dateDebut) : null,
        date_fin: dateFin ? mondayOf(dateFin) : null,
        weeks_excluded: ligne.weeks_excluded || [],
      });
      toast.success("Période de la ligne mise à jour");
      onClose();
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.bookings_conflict) setConflict(d);
      else toast.error(typeof d === "string" ? d : "Modification impossible");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="edit-period-dialog">
        <DialogHeader>
          <DialogTitle>Période de « {nom} »</DialogTitle>
        </DialogHeader>
        <p className="-mt-1 text-xs text-muted-ink">De quelle semaine à quelle semaine cette ligne apparaît dans le planning.</p>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Semaine de début</label>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="edit-period-debut" />
              {dateDebut && (
                <button type="button" onClick={() => setDateDebut("")} data-testid="edit-period-debut-clear"
                  className="shrink-0 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline">Dès le début</button>
              )}
            </div>
            <p className="mt-1 text-[10px] text-muted-ink">
              {dateDebut ? `Démarre en S${isoWeekNumber(mondayOf(dateDebut))} · lundi ${frenchDateShort(mondayOf(dateDebut))}` : "Visible dès les premières semaines"}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Semaine de fin <span className="font-normal">(facultatif)</span></label>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFin} min={dateDebut || undefined} onChange={(e) => setDateFin(e.target.value)} data-testid="edit-period-fin" />
              {dateFin && (
                <button type="button" onClick={() => setDateFin("")} data-testid="edit-period-fin-clear"
                  className="shrink-0 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline">Sans fin</button>
              )}
            </div>
            <p className="mt-1 text-[10px] text-muted-ink">
              {dateFin ? `S'arrête après S${isoWeekNumber(mondayOf(dateFin))} · lundi ${frenchDateShort(mondayOf(dateFin))}` : "Durée indéterminée"}
            </p>
          </div>
        </div>

        {conflict && (
          <div className="rounded-xl border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.07)] p-3 text-xs" data-testid="edit-period-conflict">
            <div className="mb-1 flex items-center gap-1.5 font-bold text-[#9B2226]"><AlertTriangle className="h-4 w-4" /> Créneaux réservés</div>
            <p className="mb-1.5 text-[#7F1D1D]">{conflict.message}</p>
            <div className="flex flex-wrap gap-1.5">
              {(conflict.weeks || []).map((w) => (
                <span key={w.monday} className="rounded-full bg-white px-2 py-0.5 font-semibold text-[#9B2226]">{w.label}</span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="edit-period-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="edit-period-confirm"
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignLigneDialog({ ligne, cdps, weekMonday, onClose, onDone }) {
  const [cdpId, setCdpId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ligne) {
      setCdpId("");
      // weekMonday est déjà une date ISO (YYYY-MM-DD) : point de bascule par défaut.
      setFromDate(weekMonday || new Date().toISOString().slice(0, 10));
    }
  }, [ligne, weekMonday]);

  const currentCdp = cdps.find((c) => c.id === ligne?.cdp_id);
  const targets = cdps.filter((c) => c.id !== ligne?.cdp_id);

  const submit = async () => {
    if (!cdpId || !fromDate) { toast.error("Choisissez un CDP et une date de bascule."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/lignes/${ligne.id}/reassign`, { cdp_id: cdpId, from_date: fromDate });
      toast.success(`Ligne réattribuée à ${data?.new_cdp_name || "un CDP"} à partir du ${frenchDate(fromDate)}`
        + (data?.moved ? ` · ${data.moved} créneau(x) déplacé(s)` : "")
        + (data?.kept ? ` · ${data.kept} conservé(s) au titulaire d'origine` : ""));
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la réattribution.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!ligne} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reassign-ligne-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 text-[var(--shariti-orange)]" /> Réattribuer la ligne
          </DialogTitle>
        </DialogHeader>
        {ligne && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-sm">
              <div className="font-bold text-ink">{ligne.identifiant}</div>
              <div className="text-xs text-muted-ink">Titulaire actuel : {currentCdp?.name || "—"}</div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">Nouveau CDP</label>
              <Select value={cdpId} onValueChange={setCdpId}>
                <SelectTrigger data-testid="reassign-cdp-select"><SelectValue placeholder="Choisir un(e) CDP…" /></SelectTrigger>
                <SelectContent>
                  {targets.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`reassign-cdp-${c.id}`}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">À partir du</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                className="w-full rounded-lg border border-[var(--border-soft)] bg-white px-3 py-2 text-sm outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
                data-testid="reassign-from-date" />
              <p className="mt-1 text-[11px] text-muted-ink">
                Les créneaux <strong>à partir de cette date</strong> passent au nouveau CDP. Le <strong>passé reste inchangé</strong> (paie / stats préservées).
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose}
            className="rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[var(--surface-2)]"
            data-testid="reassign-cancel">Annuler</button>
          <button onClick={submit} disabled={saving || !cdpId}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--shariti-orange)] px-3 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-50"
            data-testid="reassign-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Réattribuer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Bascule d'une ligne vers une AUTRE association (admin), avec une PORTÉE maîtrisée
// dans le temps — sans jamais réécrire l'historique par erreur :
//  - Cette semaine uniquement / Cette semaine + suivantes / Toutes les semaines.
// Le passé est préservé (et restauré automatiquement si la base avait été écrasée).
// Le code campagne et l'intitulé sont recalculés avec le nouvel acronyme.
function ChangeAssocDialog({ ligne, associations = [], perimetres = [], bookingsCount = 0, weekMonday, onClose, onDone }) {
  const [assocId, setAssocId] = useState("");
  const [scope, setScope] = useState("future");            // single | future | all
  const [fromWeek, setFromWeek] = useState("");
  const [previewCode, setPreviewCode] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ctx, setCtx] = useState(null);                    // { base, overrides, effective, detected_original }
  const [removingWeek, setRemovingWeek] = useState(null);

  const refetchCtx = useCallback(async () => {
    if (!ligne) return;
    try {
      const { data } = await api.get(`/planning/lignes/${ligne.id}/association-overrides`,
        { params: { week: fromWeek || weekMonday } });
      setCtx(data);
    } catch { setCtx(null); }
  }, [ligne, fromWeek, weekMonday]);

  // Réinitialisation à chaque ouverture.
  useEffect(() => {
    if (ligne) {
      setAssocId("");
      setScope("future");
      setFromWeek(mondayOf(weekMonday || new Date()));
      setPreviewCode("");
      setCtx(null);
    }
  }, [ligne, weekMonday]);

  useEffect(() => { if (ligne) refetchCtx(); }, [ligne, fromWeek, refetchCtx]);

  const targetAssoc = associations.find((a) => a.id === assocId);
  // On propose TOUTES les associations (y compris l'actuelle) : utile pour ré-appliquer
  // la même association sur une portée différente (ex. restaurer le passé + garder le futur).
  const targets = associations;

  // Aperçu du code campagne sur la semaine effectivement visée (bascule).
  useEffect(() => {
    if (!ligne || !assocId) { setPreviewCode(""); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const wk = scope === "all" ? weekMonday : (fromWeek || weekMonday);
    api.get("/planning/code-preview", { params: { association_id: assocId, week: wk } })
      .then((r) => { if (!cancelled) setPreviewCode(r.data?.code || ""); })
      .catch(() => { if (!cancelled) setPreviewCode(""); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [ligne, assocId, fromWeek, scope, weekMonday]);

  const nextWeekIso = (iso) => { if (!iso) return ""; const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + 7); return toISODate(d); };

  // Association des semaines PASSÉES : origine détectée (si réparation) sinon base.
  const detected = ctx?.detected_original?.acronym || null;
  const pastLabel = detected || ctx?.base?.association?.acronym || "—";
  const afterLabel = targetAssoc?.acronym || targetAssoc?.name || "—";
  const overrides = ctx?.overrides || [];

  const undoOverride = async (fw) => {
    setRemovingWeek(fw);
    try {
      await api.delete(`/planning/lignes/${ligne.id}/association-overrides/${fw}`);
      toast.success(`Changement d'association du ${frenchDate(fw)} annulé`);
      await refetchCtx();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Annulation impossible.");
    } finally { setRemovingWeek(null); }
  };

  const submit = async () => {
    if (!assocId) { toast.error("Choisissez une association de destination."); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/planning/lignes/${ligne.id}/association`, {
        association_id: assocId,
        perimetre_id: null,
        code_campagne: "",
        regenerate_identifiant: true,
        scope,
        week: scope === "all" ? weekMonday : fromWeek,
      });
      const when = scope === "all" ? "toutes les semaines"
        : scope === "single" ? `semaine du ${frenchDate(data?.from_week)} uniquement`
        : `à partir du ${frenchDate(data?.from_week)}`;
      toast.success(`Ligne basculée vers « ${afterLabel} » (${when})`
        + (data?.restored_original ? ` · passé restauré sur ${data.restored_original}` : "")
        + (data?.code_campagne ? ` · code ${data.code_campagne}` : ""));
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la bascule d'association.");
    } finally {
      setSaving(false);
    }
  };

  const Seg = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`flex-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${active ? "border-[var(--shariti-orange)] bg-[var(--surface-2)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
      {children}
    </button>
  );

  return (
    <Dialog open={!!ligne} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="change-assoc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[var(--shariti-orange)]" /> Changer d'association
          </DialogTitle>
          <p className="text-xs text-muted-ink">
            Basculez cette ligne vers une autre association, avec une portée au choix. <strong>Les semaines passées ne sont jamais modifiées</strong> (sauf mode « toutes les semaines »).
          </p>
        </DialogHeader>
        {ligne && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-sm" data-testid="change-assoc-current">
              <div className="font-bold text-ink">{ligne.identifiant}</div>
              <div className="text-xs text-muted-ink">
                Association affichée cette semaine : <strong>{ctx?.effective?.acronym || ligne.assoc?.acronym || "—"}</strong>
              </div>
              {bookingsCount > 0 && (
                <div className="mt-0.5 text-[11px] text-muted-ink">{bookingsCount} créneau(x) posé(s) sur cette ligne (conservés).</div>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">Nouvelle association</label>
              <Select value={assocId} onValueChange={setAssocId}>
                <SelectTrigger data-testid="change-assoc-select"><SelectValue placeholder="Choisir une association…" /></SelectTrigger>
                <SelectContent>
                  {targets.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`change-assoc-option-${a.id}`}>
                      {a.acronym ? `${a.acronym} — ${a.name}` : a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Portée dans le temps */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">À partir de quand ?</label>
              <div className="flex gap-1.5">
                <Seg active={scope === "single"} onClick={() => setScope("single")} testId="change-assoc-scope-single">Cette semaine</Seg>
                <Seg active={scope === "future"} onClick={() => setScope("future")} testId="change-assoc-scope-future">Cette semaine + suivantes</Seg>
                <Seg active={scope === "all"} onClick={() => setScope("all")} testId="change-assoc-scope-all">Toutes</Seg>
              </div>

              {scope !== "all" ? (
                <div className="mt-2 space-y-2">
                  <Input type="date" value={fromWeek}
                    onChange={(e) => e.target.value && setFromWeek(mondayOf(e.target.value))}
                    className="h-9" data-testid="change-assoc-fromweek-date" />
                  <div className="rounded-lg border border-[var(--border-soft)] bg-white p-2.5 text-[11px] text-muted-ink" data-testid="change-assoc-scope-info">
                    {scope === "single" ? (
                      <>Appliqué à la <strong>semaine du {fromWeek ? frenchDate(fromWeek) : "—"}</strong> uniquement.
                        La semaine suivante ({fromWeek ? frenchDate(nextWeekIso(fromWeek)) : "—"}) revient automatiquement à « {pastLabel} ».</>
                    ) : (
                      <>Appliqué à la <strong>semaine du {fromWeek ? frenchDate(fromWeek) : "—"}</strong> et aux suivantes.
                        Les semaines précédentes restent sur « {pastLabel} ».</>
                    )}
                  </div>
                  {detected && (
                    <div className="flex items-start gap-2 rounded-lg border border-[rgba(16,124,16,0.25)] bg-[rgba(16,124,16,0.06)] p-2.5 text-[11px] text-[#0b6b0b]" data-testid="change-assoc-restore-note">
                      <RotateCcw className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>L'historique semble avoir été écrasé. Les semaines passées seront <strong>restaurées sur « {detected} »</strong> (association d'origine détectée).</span>
                    </div>
                  )}
                  {/* Aperçu Avant / Après */}
                  <div className="grid grid-cols-2 gap-2" data-testid="change-assoc-preview-beforeafter">
                    <div className="rounded-lg border border-[var(--border-soft)] bg-white p-2">
                      <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Semaines passées</div>
                      <div className="mt-0.5 truncate text-xs font-bold text-ink" data-testid="change-assoc-before">{pastLabel}</div>
                    </div>
                    <div className="rounded-lg border border-[var(--shariti-orange)] bg-[var(--surface-2)] p-2">
                      <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">{scope === "single" ? "Cette semaine" : "À partir du " + (fromWeek ? frenchDate(fromWeek) : "—")}</div>
                      <div className="mt-0.5 truncate text-xs font-bold text-ink" data-testid="change-assoc-after">{afterLabel}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-1 flex items-start gap-1.5 text-[11px] font-semibold text-[#9B2226]" data-testid="change-assoc-all-warning">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  Modifie AUSSI les semaines passées (uniformise tout l'historique).
                </p>
              )}
            </div>

            {assocId && (
              <div className="rounded-xl border border-[var(--border-soft)] bg-white p-3 text-sm" data-testid="change-assoc-preview">
                <div className="text-xs font-semibold text-muted-ink">Nouveau code campagne (automatique)</div>
                {previewLoading ? (
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-ink">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Calcul en cours…
                  </div>
                ) : (
                  <div className="mt-1 font-mono text-sm font-bold text-ink" data-testid="change-assoc-preview-code">
                    {previewCode || "Aucun code disponible pour cette association cette semaine"}
                  </div>
                )}
                <p className="mt-2 text-[11px] text-muted-ink">
                  L'intitulé de la ligne sera mis à jour avec le nouvel acronyme.
                </p>
              </div>
            )}

            {/* Historique des changements datés (avec annulation 1 clic) */}
            {overrides.length > 0 && (
              <div className="rounded-xl border border-[var(--border-soft)] p-2.5" data-testid="change-assoc-history">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-muted-ink">
                  <Clock className="h-3.5 w-3.5" /> Historique des changements
                </div>
                <ul className="max-h-40 space-y-1.5 overflow-auto pr-1">
                  <li className="flex items-start gap-2 text-[11px]">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-black/25" />
                    <div className="min-w-0"><span className="font-semibold">Au départ</span> : {ctx?.base?.association?.acronym || "—"}</div>
                  </li>
                  {overrides.map((o) => (
                    <li key={o.from_week} className="flex items-start gap-2 text-[11px]" data-testid={`change-assoc-history-${o.from_week}`}>
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold">Dès le {frenchDate(o.from_week)}</span> : {o.association?.acronym || "—"}
                        <div className="text-[10px] text-muted-ink">{o.by ? `par ${o.by}` : ""}{o.at ? ` · ${frenchDate(String(o.at).slice(0, 10))}` : ""}</div>
                      </div>
                      <button type="button" onClick={() => undoOverride(o.from_week)} disabled={removingWeek === o.from_week}
                        className="shrink-0 rounded-md p-1 text-muted-ink transition-colors hover:bg-[rgba(214,69,69,0.12)] hover:text-[#9B2226] disabled:opacity-50"
                        title="Annuler ce changement (revenir au réglage précédent)" data-testid={`change-assoc-history-undo-${o.from_week}`}>
                        {removingWeek === o.from_week ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose}
            className="rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[var(--surface-2)]"
            data-testid="change-assoc-cancel">Annuler</button>
          <button onClick={submit} disabled={saving || !assocId}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--shariti-orange)] px-3 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-50"
            data-testid="change-assoc-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />} Basculer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Nommer / renommer l'équipe d'une ligne directement depuis le planning.
// Le nom se propage automatiquement à toutes les lignes du même chef d'équipe.
// Change le NOM d'équipe et/ou le RESPONSABLE d'une ligne, avec portée maîtrisée :
//  - dans le temps : « à partir d'une semaine » (défaut, sans toucher au passé) OU toutes les semaines ;
//  - sur les lignes : cette ligne seule (défaut) OU toutes les lignes du responsable.
function RenameEquipeDialog({ ligne, chefs = [], onCreateChef, weekMonday, onChanged, onClose, onDone }) {
  const [nom, setNom] = useState("");
  const [chefId, setChefId] = useState("");
  const [chefQuery, setChefQuery] = useState("");
  const [chefOpen, setChefOpen] = useState(false);
  const [creatingChef, setCreatingChef] = useState(false);
  const [applyScope, setApplyScope] = useState("line");    // line | chef
  const [timeScope, setTimeScope] = useState("from_week");  // from_week | all
  const [fromWeek, setFromWeek] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);            // { base, overrides }
  const [removingWeek, setRemovingWeek] = useState(null);   // from_week en cours d'annulation

  const refetchHistory = useCallback(async () => {
    if (!ligne) return;
    try {
      const { data } = await api.get(`/admin/lignes/${ligne.id}/team-overrides`);
      setHistory(data);
    } catch { setHistory(null); }
  }, [ligne]);

  useEffect(() => {
    if (ligne) {
      setNom((ligne.equipe_nom || ligne.team_name || "").trim());
      setChefId(ligne.chef_equipe_id_effective || ligne.chef_equipe_id || "");
      setChefQuery(ligne.chef_nom || "");
      setApplyScope("line");
      setTimeScope("from_week");
      setFromWeek(mondayOf(weekMonday || new Date()));
      setChefOpen(false);
      setHistory(null);
      refetchHistory();
    }
  }, [ligne, weekMonday, refetchHistory]);

  // Valeur d'équipe/chef effective pour une semaine donnée (base + overrides).
  const resolveAt = (weekIso) => {
    const base = history?.base || { team_name: ligne?.team_name, chef_nom: ligne?.chef_nom };
    const applicable = (history?.overrides || [])
      .filter((o) => (o.from_week || "") <= weekIso)
      .sort((a, b) => (a.from_week > b.from_week ? 1 : -1));
    const eff = applicable[applicable.length - 1];
    return {
      team: (eff && eff.team_name) || base.team_name || "—",
      chef: (eff && eff.chef_nom) || base.chef_nom || null,
    };
  };
  const prevDayIso = (mondayIso) => {
    if (!mondayIso) return "";
    const d = new Date(`${mondayIso}T00:00:00`);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };
  const before = resolveAt(prevDayIso(fromWeek));
  const afterTeam = nom.trim() || before.team;
  const afterChef = chefQuery.trim() || before.chef || null;

  const undoOverride = async (fw) => {
    setRemovingWeek(fw);
    try {
      await api.delete(`/admin/lignes/${ligne.id}/team-overrides/${fw}`);
      toast.success(`Changement du ${frenchDate(fw)} annulé`);
      await refetchHistory();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Annulation impossible.");
    } finally { setRemovingWeek(null); }
  };

  const q = chefQuery.trim().toLowerCase();
  const chefMatches = (chefs || []).filter((c) => (c.nom || "").toLowerCase().includes(q));
  const exactChef = (chefs || []).find((c) => (c.nom || "").toLowerCase() === q);

  const pickChef = (c) => { setChefId(c.id); setChefQuery(c.nom || ""); setChefOpen(false); };
  const doCreateChef = async () => {
    const name = chefQuery.trim();
    if (!name || !onCreateChef) return;
    setCreatingChef(true);
    try { const c = await onCreateChef(name); pickChef(c); toast.success(`Responsable « ${c.nom || name} » créé`); }
    catch { toast.error("Création du responsable impossible."); }
    finally { setCreatingChef(false); }
  };

  const submit = async () => {
    const val = nom.trim();
    if (!val && !chefId) { toast.error("Saisissez un nom d'équipe ou un responsable."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/lignes/${ligne.id}/set-team`, {
        team_name: val || undefined,
        chef_equipe_id: chefId || undefined,
        apply_scope: applyScope,
        time_scope: timeScope,
        from_week: timeScope === "from_week" ? fromWeek : undefined,
      });
      const n = data?.updated_count || 0;
      const when = data?.time_scope === "all" ? "toutes les semaines" : `à partir du ${frenchDate(data?.from_week)}`;
      toast.success(`Équipe mise à jour (${when}) · ${n} ligne${n > 1 ? "s" : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement.");
    } finally { setSaving(false); }
  };

  const Seg = ({ active, onClick, children, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`flex-1 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${active ? "border-[var(--shariti-orange)] bg-[var(--surface-2)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
      {children}
    </button>
  );

  return (
    <Dialog open={!!ligne} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rename-equipe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-[var(--shariti-orange)]" /> Équipe &amp; responsable
          </DialogTitle>
        </DialogHeader>
        {ligne && (
          <div className="space-y-4">
            <div className="rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3 text-sm">
              <div className="font-bold text-ink">{ligne.identifiant}</div>
              <div className="text-xs text-muted-ink">
                Actuel : {ligne.equipe_nom || ligne.team_name || "—"}{ligne.chef_nom ? ` · responsable ${ligne.chef_nom}` : ""}
              </div>
            </div>

            {/* Nom d'équipe */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">Nom de l'équipe</label>
              <Input value={nom} onChange={(e) => setNom(e.target.value)} maxLength={60}
                placeholder="Ex : WARRIORS" autoFocus data-testid="rename-equipe-input" />
            </div>

            {/* Responsable (chef) */}
            <div className="relative">
              <label className="mb-1 block text-xs font-semibold text-muted-ink">Responsable d'équipe (chef)</label>
              <Input value={chefQuery}
                onChange={(e) => { setChefQuery(e.target.value); setChefId(""); setChefOpen(true); }}
                onFocus={() => setChefOpen(true)}
                placeholder="Rechercher ou créer un responsable…" data-testid="rename-chef-input" />
              {chefOpen && chefQuery.trim() && (
                <div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-[var(--border-soft)] bg-white p-1 shadow-lg" data-testid="rename-chef-dropdown">
                  {chefMatches.slice(0, 8).map((c) => (
                    <button key={c.id} type="button" onClick={() => pickChef(c)}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-[var(--surface-2)]" data-testid={`rename-chef-opt-${c.id}`}>
                      <Users className="h-3.5 w-3.5 text-muted-ink" />{c.nom}
                    </button>
                  ))}
                  {!exactChef && (
                    <button type="button" onClick={doCreateChef} disabled={creatingChef}
                      className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-sm font-semibold text-[var(--shariti-orange)] hover:bg-[var(--surface-2)]" data-testid="rename-chef-create">
                      {creatingChef ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Créer « {chefQuery.trim()} »
                    </button>
                  )}
                </div>
              )}
              <p className="mt-1 text-[11px] text-muted-ink">Laissez tel quel pour conserver le responsable actuel.</p>
            </div>

            {/* Portée dans le temps */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">À partir de quand ?</label>
              <div className="flex gap-1.5">
                <Seg active={timeScope === "from_week"} onClick={() => setTimeScope("from_week")} testId="rename-time-fromweek">À partir d'une semaine</Seg>
                <Seg active={timeScope === "all"} onClick={() => setTimeScope("all")} testId="rename-time-all">Toutes les semaines</Seg>
              </div>
              {timeScope === "from_week" ? (
                <div className="mt-2 space-y-2">
                  <Input type="date" value={fromWeek}
                    onChange={(e) => e.target.value && setFromWeek(mondayOf(e.target.value))}
                    className="h-9" data-testid="rename-fromweek-date" />
                  <p className="text-[11px] text-muted-ink">Semaine du <strong>{fromWeek ? frenchDate(fromWeek) : "—"}</strong> et suivantes. Les semaines précédentes ne changent pas.</p>
                  {/* Aperçu Avant / Après */}
                  <div className="grid grid-cols-2 gap-2" data-testid="rename-preview">
                    <div className="rounded-lg border border-[var(--border-soft)] bg-white p-2">
                      <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Avant (jusqu'au {frenchDate(prevDayIso(fromWeek))})</div>
                      <div className="mt-0.5 truncate text-xs font-bold text-ink" data-testid="rename-preview-before">{before.team}</div>
                      {before.chef && <div className="truncate text-[10px] text-muted-ink">{before.chef}</div>}
                    </div>
                    <div className="rounded-lg border border-[var(--shariti-orange)] bg-[var(--surface-2)] p-2">
                      <div className="text-[9px] font-bold uppercase tracking-wide text-[var(--shariti-orange)]">À partir du {frenchDate(fromWeek)}</div>
                      <div className="mt-0.5 truncate text-xs font-bold text-ink" data-testid="rename-preview-after">{afterTeam}</div>
                      {afterChef && <div className="truncate text-[10px] text-muted-ink">{afterChef}</div>}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="mt-1 text-[11px] font-semibold text-[#9B2226]">Modifie aussi les semaines passées (uniformise tout l'historique).</p>
              )}
            </div>

            {/* Portée sur les lignes */}
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-ink">Sur quelles lignes ?</label>
              <div className="flex gap-1.5">
                <Seg active={applyScope === "line"} onClick={() => setApplyScope("line")} testId="rename-scope-line">Cette ligne uniquement</Seg>
                <Seg active={applyScope === "chef"} onClick={() => setApplyScope("chef")} testId="rename-scope-chef">Toutes les lignes du responsable</Seg>
              </div>
            </div>

            {/* Historique des changements datés (avec annulation 1 clic) */}
            {history && (history.overrides?.length > 0) && (
              <div className="rounded-xl border border-[var(--border-soft)] p-2.5" data-testid="rename-history">
                <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-muted-ink">
                  <Clock className="h-3.5 w-3.5" /> Historique des changements
                </div>
                <ul className="max-h-40 space-y-1.5 overflow-auto pr-1">
                  <li className="flex items-start gap-2 text-[11px]">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-black/25" />
                    <div className="min-w-0">
                      <span className="font-semibold">Au départ</span> : {history.base.team_name || "—"}{history.base.chef_nom ? ` · ${history.base.chef_nom}` : ""}
                    </div>
                  </li>
                  {history.overrides.map((o) => (
                    <li key={o.from_week} className="flex items-start gap-2 text-[11px]" data-testid={`rename-history-${o.from_week}`}>
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--shariti-orange)]" />
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold">Dès le {frenchDate(o.from_week)}</span> : {o.team_name || history.base.team_name || "—"}{o.chef_nom ? ` · ${o.chef_nom}` : ""}
                        <div className="text-[10px] text-muted-ink">{o.by ? `par ${o.by}` : ""}{o.at ? ` · ${frenchDate(String(o.at).slice(0, 10))}` : ""}</div>
                      </div>
                      <button type="button" onClick={() => undoOverride(o.from_week)} disabled={removingWeek === o.from_week}
                        className="shrink-0 rounded-md p-1 text-muted-ink transition-colors hover:bg-[rgba(214,69,69,0.12)] hover:text-[#9B2226] disabled:opacity-50"
                        title="Annuler ce changement (revenir au réglage précédent)" data-testid={`rename-history-undo-${o.from_week}`}>
                        {removingWeek === o.from_week ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <button onClick={onClose}
            className="rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[var(--surface-2)]"
            data-testid="rename-equipe-cancel">Annuler</button>
          <button onClick={submit} disabled={saving || (!nom.trim() && !chefId)}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--shariti-orange)] px-3 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-50"
            data-testid="rename-equipe-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Créer une NOUVELLE ligne de planning directement depuis le planning (sans passer
// par l'Administration) : association, CDP, canal, périmètre, équipe, responsable et
// code campagne (auto-résolu, modifiable). La ligne apparaît immédiatement.
function NewLigneDialog({ open, onClose, associations = [], canaux = [], cdps = [], perimetres = [], chefs = [], onCreateChef, weekMonday, onDone }) {
  const spCanal = canaux.find((c) => c.code === "SP") || canaux[0] || null;
  const [associationId, setAssociationId] = useState("");
  const [cdpId, setCdpId] = useState("");
  const [canalId, setCanalId] = useState("");
  const [perimetreId, setPerimetreId] = useState("");
  const [teamName, setTeamName] = useState("");
  const [chefQuery, setChefQuery] = useState("");
  const [code, setCode] = useState("");
  const [codeEdited, setCodeEdited] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [zonesLabel, setZonesLabel] = useState("");
  const [dateDebut, setDateDebut] = useState(weekMonday || "");
  const [dateFin, setDateFin] = useState("");
  const [saving, setSaving] = useState(false);

  // Réinitialisation à l'ouverture.
  useEffect(() => {
    if (open) {
      setAssociationId(""); setCdpId(""); setCanalId(spCanal?.id || "");
      setPerimetreId(""); setTeamName(""); setChefQuery(""); setCode("");
      setCodeEdited(false); setZonesLabel("");
      setDateDebut(weekMonday || ""); setDateFin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const assocPerimetres = useMemo(
    () => perimetres.filter((p) => !associationId || p.association_id === associationId),
    [perimetres, associationId]);
  const sortedCdps = useMemo(
    () => [...cdps].sort((a, b) => (a.active === false) - (b.active === false) || (a.name || "").localeCompare(b.name || "")),
    [cdps]);

  // Aperçu du code campagne (association + périmètre + semaine).
  useEffect(() => {
    if (!open || !associationId) { return; }
    let cancelled = false;
    setCodeLoading(true);
    api.get("/planning/code-preview", { params: { association_id: associationId, perimetre_id: perimetreId || undefined, week: weekMonday } })
      .then((r) => { if (!cancelled && !codeEdited) setCode(r.data?.code || ""); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCodeLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, associationId, perimetreId, weekMonday]);

  const canSubmit = !!associationId && !!cdpId && !saving;

  const resolveChefId = async () => {
    const q = chefQuery.trim();
    if (!q) return null;
    const existing = chefs.find((c) => (c.nom || "").toLowerCase() === q.toLowerCase());
    if (existing) return existing.id;
    if (onCreateChef) {
      try { const created = await onCreateChef(q); return created?.id || null; }
      catch { return null; }
    }
    return null;
  };

  const submit = async () => {
    if (!canSubmit) return;
    if (dateDebut && dateFin && mondayOf(dateFin) < mondayOf(dateDebut)) {
      toast.error("La semaine de fin doit être après (ou égale à) la semaine de début.");
      return;
    }
    setSaving(true);
    try {
      const chef_equipe_id = await resolveChefId();
      const { data: res } = await api.post("/planning/lignes", {
        association_id: associationId,
        cdp_id: cdpId,
        canal_id: canalId || undefined,
        perimetre_id: perimetreId || undefined,
        team_name: teamName.trim() || undefined,
        chef_equipe_id: chef_equipe_id || undefined,
        code_campagne: code.trim() || undefined,
        zones_label: zonesLabel.trim() || undefined,
        // Période d'activité : la ligne apparaît de la semaine de début à la semaine de
        // fin (facultative). Par défaut, elle démarre à la semaine affichée.
        date_debut: mondayOf(dateDebut || weekMonday),
        date_fin: dateFin ? mondayOf(dateFin) : undefined,
      });
      toast.success(`Ligne « ${res?.identifiant || "créée"} » ajoutée au planning`
        + (res?.code_campagne ? ` · code ${res.code_campagne}` : ""));
      onDone?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création de la ligne impossible.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-ligne-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-[var(--shariti-orange)]" /> Nouvelle ligne
          </DialogTitle>
          <p className="text-xs text-muted-ink">Créez une ligne directement depuis le planning et définissez sa <strong>période d'activité</strong> (semaine de début → semaine de fin) plus bas.</p>
        </DialogHeader>
        <div className="grid max-h-[62vh] grid-cols-1 gap-3 overflow-y-auto no-scrollbar pr-0.5 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Association <span className="text-[#D64545]">*</span></label>
            <Select value={associationId} onValueChange={(v) => { setAssociationId(v); setPerimetreId(""); setCodeEdited(false); }}>
              <SelectTrigger data-testid="new-ligne-association"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {associations.map((a) => (
                  <SelectItem key={a.id} value={a.id} data-testid={`new-ligne-association-${a.id}`}>{a.acronym || a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">CDP responsable <span className="text-[#D64545]">*</span></label>
            <Select value={cdpId} onValueChange={setCdpId}>
              <SelectTrigger data-testid="new-ligne-cdp"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>
                {sortedCdps.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`new-ligne-cdp-${c.id}`}>
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full border border-black/20" style={{ background: c.color }} />
                      {c.name}{c.active === false ? " (inactif)" : ""}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Canal</label>
            <Select value={canalId} onValueChange={setCanalId}>
              <SelectTrigger data-testid="new-ligne-canal"><SelectValue placeholder="Sites privés" /></SelectTrigger>
              <SelectContent>
                {canaux.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`new-ligne-canal-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Périmètre <span className="font-normal">(pour le code)</span></label>
            <Select value={perimetreId} onValueChange={(v) => { setPerimetreId(v); setCodeEdited(false); }} disabled={!associationId}>
              <SelectTrigger data-testid="new-ligne-perimetre"><SelectValue placeholder={associationId ? "Choisir…" : "Association d'abord"} /></SelectTrigger>
              <SelectContent>
                {assocPerimetres.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`new-ligne-perimetre-${p.id}`}>{p.nom}</SelectItem>
                ))}
                {assocPerimetres.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-ink">Aucun périmètre</div>}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Nom d'équipe</label>
            <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} maxLength={60}
              placeholder="Ex : PERFORMERS" data-testid="new-ligne-team" />
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Responsable d'équipe</label>
            <Input value={chefQuery} onChange={(e) => setChefQuery(e.target.value)} list={`new-ligne-chefs-${open}`}
              maxLength={60} placeholder="Choisir ou saisir…" data-testid="new-ligne-chef" autoComplete="off" />
            <datalist id={`new-ligne-chefs-${open}`}>
              {chefs.map((c) => (<option key={c.id} value={c.nom} />))}
            </datalist>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Code campagne</label>
            <div className="relative">
              <Input value={code} onChange={(e) => { setCode(e.target.value); setCodeEdited(true); }}
                placeholder={codeLoading ? "Résolution…" : "Auto"} data-testid="new-ligne-code" className="font-mono uppercase" />
              {codeLoading && <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-ink" />}
            </div>
            <p className="mt-1 text-[10px] text-muted-ink">Proposé automatiquement selon l'association, le périmètre et la semaine. Modifiable.</p>
          </div>
          <div className="sm:col-span-1">
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Lieu affiché <span className="font-normal">(facultatif)</span></label>
            <Input value={zonesLabel} onChange={(e) => setZonesLabel(e.target.value)} maxLength={80}
              placeholder="Ex : Paris et Île-de-France" data-testid="new-ligne-lieu" />
          </div>
          <div className="sm:col-span-2 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-3" data-testid="new-ligne-period">
            <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" />
              <span className="text-xs font-bold text-ink">Période d'activité</span>
              <span className="text-[10px] text-muted-ink">— de quelle semaine à quelle semaine cette ligne tourne</span>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold text-muted-ink">Semaine de début</label>
                <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                  data-testid="new-ligne-date-debut" />
                <p className="mt-1 text-[10px] text-muted-ink">
                  {dateDebut ? `Démarre en S${isoWeekNumber(mondayOf(dateDebut))} · lundi ${frenchDateShort(mondayOf(dateDebut))}` : "Par défaut : la semaine affichée"}
                </p>
              </div>
              <div>
                <label className="mb-1 flex items-center justify-between text-xs font-semibold text-muted-ink">
                  <span>Semaine de fin <span className="font-normal">(facultatif)</span></span>
                  {dateFin && (
                    <button type="button" onClick={() => setDateFin("")} data-testid="new-ligne-date-fin-clear"
                      className="text-[10px] font-semibold text-[var(--shariti-orange)] hover:underline">Sans fin</button>
                  )}
                </label>
                <Input type="date" value={dateFin} min={dateDebut || undefined} onChange={(e) => setDateFin(e.target.value)}
                  data-testid="new-ligne-date-fin" />
                <p className="mt-1 text-[10px] text-muted-ink">
                  {dateFin ? `S'arrête après S${isoWeekNumber(mondayOf(dateFin))} · lundi ${frenchDateShort(mondayOf(dateFin))}` : "Laisser vide = durée indéterminée"}
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose}
            className="rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[var(--surface-2)]"
            data-testid="new-ligne-cancel">Annuler</button>
          <button onClick={submit} disabled={!canSubmit}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--shariti-orange)] px-3 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-50"
            data-testid="new-ligne-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer la ligne
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaskedBookingCard({ b }) {
  // Vue RESTREINTE (prospecteur terrain) : la case est occupée par une autre équipe.
  // On n'affiche AUCUNE information identifiante (magasin, adresse, contact, statut) —
  // uniquement le fait que le créneau est pris, non réservable et non cliquable.
  return (
    <div
      data-testid={`booking-masked-${b.id}`}
      title="Créneau réservé par une autre équipe (détails masqués)"
      className="relative flex min-h-[64px] w-full cursor-not-allowed select-none flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[var(--border-soft)] bg-[repeating-linear-gradient(45deg,rgba(24,24,24,0.035)_0,rgba(24,24,24,0.035)_6px,transparent_6px,transparent_12px)] p-1.5 text-center">
      <Lock className="h-3.5 w-3.5 text-[rgba(24,24,24,0.4)]" aria-hidden />
      <span className="text-[10px] font-bold uppercase tracking-wide text-[rgba(24,24,24,0.5)]">Créneau occupé</span>
    </div>
  );
}

// Badge « bulletins du jour » CLIQUABLE : ouvre le détail par recruteur (admin/CDP).
// Signale aussi, le cas échéant, les bulletins rattachés à un magasin EN DOUBLE (fusion).
function BulletinsBadge({ count, spotId, date, dup }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const navigate = useNavigate();
  const hasDup = !!(dup && dup.count > 0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/planning/bulletins-detail", { params: { spot_id: spotId, date } });
      setDetail(data);
    } catch { setDetail({ recruteurs: [], total: count }); }
    finally { setLoading(false); }
  };
  const loadAudit = async () => {
    if (showAudit) { setShowAudit(false); return; }
    setShowAudit(true);
    if (detail?.audit) return;               // déjà chargé
    setAuditLoading(true);
    try {
      const { data } = await api.get("/planning/bulletins-detail", { params: { spot_id: spotId, date, audit: 1 } });
      setDetail(data);
    } catch { /* on garde le détail déjà affiché */ }
    finally { setAuditLoading(false); }
  };
  const onOpenChange = (v) => { setOpen(v); if (v && !detail) load(); };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button" draggable={false}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); }}
          data-testid={`booking-bulletins-${spotId}-${date}`}
          title={`${count} bulletin${count > 1 ? "s" : ""} — voir le détail par recruteur`}
          className="relative grid h-[19px] min-w-[19px] shrink-0 cursor-pointer place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white shadow-[0_1px_3px_rgba(180,83,28,0.5)] ring-2 ring-white/75 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-white"
          style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
          {count}
          {hasDup && (
            <span aria-hidden
              className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#D64545] ring-1 ring-white" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={6} className={`overflow-hidden p-0 transition-[width] ${showAudit ? "w-80" : "w-64"}`}
        onClick={(e) => e.stopPropagation()} data-testid="bulletins-detail-popover">
        <div className="flex items-center gap-2 border-b border-[var(--border-soft)] bg-[#FBF7F2] px-3 py-2">
          <Users className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          <span className="text-xs font-bold text-[#181818]">Bulletins par recruteur</span>
          <span className="ml-auto grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-extrabold leading-none text-white"
            style={{ background: "linear-gradient(135deg,#F6903E 0%,#EE722E 55%,#D9601F 100%)" }}>
            {detail?.total ?? count}
          </span>
        </div>
        <div className="max-h-56 overflow-y-auto p-1.5">
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-ink">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement…
            </div>
          ) : (detail?.recruteurs?.length ? (
            detail.recruteurs.map((r) => (
              <div key={r.rd_id || r.code || r.name}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-black/5"
                data-testid={`bulletins-rd-${r.rd_id || r.code}`}>
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-bold text-[var(--shariti-orange)]"
                  style={{ background: "rgba(238,114,46,0.14)" }}>
                  {(r.name || "?").trim().slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs font-semibold text-[#181818]" title={r.name}>{r.name}</span>
                <span className="shrink-0 rounded-full bg-black/5 px-1.5 text-[11px] font-bold tabular-nums text-[#181818]">{r.count}</span>
              </div>
            ))
          ) : (
            <div className="px-2 py-3 text-xs text-muted-ink">Aucun détail disponible.</div>
          ))}
        </div>
        {/* AUDIT bulletin par bulletin : traçabilité complète pour réconcilier avec Evergiving. */}
        <button type="button" onClick={loadAudit}
          data-testid="bulletins-audit-toggle"
          className="flex w-full items-center gap-2 border-t border-[var(--border-soft)] bg-[#FBF7F2] px-3 py-2 text-left text-[11px] font-bold text-[var(--shariti-orange)] transition-colors hover:bg-[#F5EEE5]">
          <Search className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1">Auditer bulletin par bulletin (source Evergiving)</span>
          {showAudit ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        {showAudit && (
          <div className="max-h-64 overflow-y-auto border-t border-[var(--border-soft)] p-1.5" data-testid="bulletins-audit-list">
            {auditLoading ? (
              <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-ink">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse…
              </div>
            ) : (detail?.audit?.length ? (
              <>
                {detail.audit_alerte > 0 && (
                  <div className="mb-1.5 flex items-start gap-1.5 rounded-md bg-[rgba(214,69,69,0.08)] px-2 py-1.5 text-[11px] leading-snug text-[#8f2f2f]">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D64545]" />
                    <span><b>{detail.audit_alerte}</b> bulletin{detail.audit_alerte > 1 ? "s" : ""} rattaché{detail.audit_alerte > 1 ? "s" : ""} ici par le <b>dispatch</b> alors que leur <b>lieu Evergiving</b> désigne un AUTRE magasin.</span>
                  </div>
                )}
                {detail.audit.map((a, i) => (
                  <div key={(a.id_externe || i) + "-" + i}
                    className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-[11px] ${a.lieu_autre_spot ? "bg-[rgba(214,69,69,0.06)]" : "hover:bg-black/5"}`}
                    data-testid={`bulletins-audit-row-${i}`}>
                    <span className="mt-0.5 shrink-0 rounded px-1 text-[9px] font-bold uppercase tracking-wide"
                      style={{
                        background: a.raison === "direct" ? "rgba(34,139,84,0.14)" : a.raison === "lieu" ? "rgba(59,130,246,0.14)" : a.raison === "dispatch" ? "rgba(238,114,46,0.16)" : "rgba(120,120,120,0.14)",
                        color: a.raison === "direct" ? "#1b7a47" : a.raison === "lieu" ? "#2563eb" : a.raison === "dispatch" ? "#c25410" : "#555",
                      }}
                      title={
                        a.raison === "direct" ? "Rattaché par identifiant de spot (Evergiving)"
                        : a.raison === "lieu" ? "Rattaché par le lieu Evergiving"
                        : a.raison === "dispatch" ? "Rattaché par le dispatch (affectation du jour)"
                        : "Rattaché via l'équipe affectée"}>
                      {a.raison}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="min-w-0 flex-1 truncate font-semibold text-[#181818]" title={a.rd_nom}>{a.rd_nom}</span>
                        <span className="shrink-0 rounded-full bg-black/5 px-1 text-[10px] font-semibold text-muted-ink">{a.statut || "?"}</span>
                      </div>
                      <div className="truncate text-[10px] text-muted-ink" title={a.lieu || ""}>
                        <span className="font-mono text-[9px] opacity-70">{a.id_externe || "—"}</span>
                        {a.lieu ? ` · ${a.lieu}` : ""}
                      </div>
                      {a.lieu_autre_spot && (
                        <div className="text-[10px] font-semibold text-[#b23b3b]">→ lieu Evergiving : {a.lieu_spot_nom}</div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <div className="px-2 py-3 text-xs text-muted-ink">Aucun bulletin à auditer.</div>
            ))}
          </div>
        )}
        {hasDup && (
          <button type="button"
            onClick={() => navigate(`/doublons-spots${dup.spots?.[0]?.name ? `?q=${encodeURIComponent(dup.spots[0].name)}` : ""}`)}
            data-testid="bulletins-dup-warning"
            className="flex w-full items-start gap-2 border-t border-[var(--border-soft)] bg-[rgba(214,69,69,0.06)] px-3 py-2 text-left transition-colors hover:bg-[rgba(214,69,69,0.11)]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#D64545]" />
            <span className="text-[11px] leading-snug text-[#8f2f2f]">
              <b>{dup.count} bulletin{dup.count > 1 ? "s" : ""}</b> rattaché{dup.count > 1 ? "s" : ""} à un magasin en double
              {dup.spots?.length ? ` : ${dup.spots.map((s) => s.name).join(", ")}` : ""}. Cliquez pour fusionner.
            </span>
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}


function BookingCard({ b, onView, onDelete, onStatus, onBulletins, onFieldToggle, highlight, days, ligneOccupiedDates, onDuplicate, onSetContact, onCreateContact, cdps, onSetCdp, onSetConsignes, onChangeSpot, canBlacklist = false, onBlacklist, canEdit = true, canDrag = true, canDeclare = false, onDeclare, canManageTeam = false, teamCount, onTeam, canCancel = false, onCancel, onRestore, onThank, thankBusy = false }) {
  const isHistorical = !!b.readonly;             // créneau historique (import) = lecture seule stricte
  const isCancelled = b.status === "annule";     // créneau ANNULÉ (conservé, barré) — admin peut rétablir
  const readonly = isHistorical || !canEdit;      // édition verrouillée (menu, statut, suppression…)
  const dragLocked = isHistorical || !canDrag || isCancelled;    // déplacement verrouillé (glisser-déposer)
  const forced = !!b.rebooking_forced;
  // Nombre de bulletins VALIDES saisis ce jour-là sur ce spot (perf en un coup d'œil).
  // Le backend ne renseigne ce champ que pour les admins & CDP (jamais les RD).
  const bulletinsCount = Number(b.bulletins_jour) || 0;
  // Type de forçage : rotation (défaut) ou contrainte Paris. Legacy (sans forced_type) : dérivé du motif.
  const forcedType = b.forced_type || (String(b.forced_reason || "").startsWith("Contrainte Paris") ? "paris" : "rotation");
  const forcedIsParis = forcedType === "paris";
  const isField = !!b.field_rebooking;
  const declared = !!b.declared_to_coordination;  // déclaré à la coordination (marqueur visible de tous)
  // Remerciement : proposé sur les créneaux PASSÉS (visite réalisée), hors annulé.
  const bookingISO = String(b.date || "").slice(0, 10);
  const isPast = !!bookingISO && bookingISO < toISODate(new Date());
  // Remerciement proposé sur les créneaux CONFIRMÉS (ferme) ou déjà passés — hors annulé.
  // (On ne dépend pas uniquement de l'horloge du navigateur, souvent décalée.)
  const canThank = !!onThank && !isCancelled && (isPast || b.status === "ferme");
  // --- Duplication multi-jours (même semaine) ---
  const [open, setOpen] = useState(false);
  // --- « Écouter l'appel » : retrouve l'enregistrement de l'appel qui a permis de
  // closer ce créneau (même spot + même CDP, appel enregistré le plus récent) et
  // redirige vers la page Appels sur cet audio, pour vérifier l'accord oral en 1 clic.
  const navigateToCall = useNavigate();
  const [callBusy, setCallBusy] = useState(false);
  const listenToSourceCall = async () => {
    setCallBusy(true);
    try {
      const { data } = await api.get(`/planning/booking/${b.id}/call`);
      if (data?.found && data.call_id) {
        setOpen(false);
        navigateToCall(`/appels?call=${data.call_id}`);
      } else {
        toast.info("Aucun appel enregistré n'a été trouvé pour cet emplacement.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de retrouver l'appel.");
    } finally {
      setCallBusy(false);
    }
  };
  const [dupMode, setDupMode] = useState(false);
  const [dupSel, setDupSel] = useState(() => new Set());
  // --- Contact qui a autorisé la venue (par créneau) ---
  const [contactMode, setContactMode] = useState(false);
  // Sous-mode : création d'un nouveau contact directement depuis le planning.
  const [newContactMode, setNewContactMode] = useState(false);
  const emptyContact = { civilite: "", prenom: "", nom: "", fonction: "", telephone: "", email: "" };
  const [newContact, setNewContact] = useState(emptyContact);
  const [savingContact, setSavingContact] = useState(false);
  const spotContacts = b.spot_contacts || [];
  const contactLabel = (c) => (c ? [c.civilite, c.prenom, c.nom].filter(Boolean).join(" ").trim() : "");
  const hasOverride = !!(b.authorized_contact && contactLabel(b.authorized_contact));
  // --- CDP « réservé par » (choix explicite + couleur) ---
  const [cdpMode, setCdpMode] = useState(false);
  const cdpList = (cdps || []).filter((c) => c.active !== false);
  // --- Consignes éditables ---
  const [consignesMode, setConsignesMode] = useState(false);
  const [consignesDraft, setConsignesDraft] = useState("");
  // --- Annulation (admin) : sous-panneau de choix du motif avant confirmation ---
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelMotif, setCancelMotif] = useState("autre");
  const [cancelNote, setCancelNote] = useState("");
  // --- Blacklister le spot : sous-panneau de confirmation + envoi ---
  const [blacklistMode, setBlacklistMode] = useState(false);
  const [blacklistBusy, setBlacklistBusy] = useState(false);
  // --- Changer le MAGASIN (correction) : recherche + sélection d'un autre spot ---
  const [spotMode, setSpotMode] = useState(false);
  const [spotQuery, setSpotQuery] = useState("");
  const [spotResults, setSpotResults] = useState([]);
  const [spotSearching, setSpotSearching] = useState(false);
  const [spotSaving, setSpotSaving] = useState(false);
  // Jours candidats : tous les jours de la semaine affichée SAUF celui du créneau.
  const dupDays = (days || []).filter((d) => d.date !== b.date);
  const dupAvailable = dupDays.filter((d) => !(ligneOccupiedDates && ligneOccupiedDates.has(d.date)));
  // Recherche de magasin (debounce 300ms) pour l'action « Changer le magasin ».
  useEffect(() => {
    if (!spotMode) return;
    const q = spotQuery.trim();
    if (q.length < 2) { setSpotResults([]); return; }
    let alive = true;
    setSpotSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/planning/spot-search", { params: { q, limit: 8 } });
        if (alive) setSpotResults(data?.results || []);
      } catch { if (alive) setSpotResults([]); }
      finally { if (alive) setSpotSearching(false); }
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [spotMode, spotQuery]);
  const toggleDupDay = (date) => setDupSel((s) => {
    const n = new Set(s);
    if (n.has(date)) n.delete(date); else n.add(date);
    return n;
  });
  const allAvailSelected = dupAvailable.length > 0 && dupAvailable.every((d) => dupSel.has(d.date));
  const toggleSelectAll = () => setDupSel(() => (allAvailSelected ? new Set() : new Set(dupAvailable.map((d) => d.date))));
  const resetDup = () => { setDupMode(false); setDupSel(new Set()); setContactMode(false); setNewContactMode(false); setNewContact(emptyContact); setCdpMode(false); setConsignesMode(false); setCancelMode(false); setSpotMode(false); setSpotQuery(""); setSpotResults([]); };
  const confirmDup = () => {
    const dates = Array.from(dupSel);
    if (!dates.length) return;
    onDuplicate && onDuplicate(b, dates);
    setOpen(false); resetDup();
  };
  // Copie discrète « nom du magasin + adresse exacte » (pour re-déclaration ailleurs).
  const [copied, setCopied] = useState(false);
  const copyNameAddress = async (e) => {
    e?.stopPropagation?.();
    const text = [b.spot_name, b.spot_address].map((x) => (x || "").trim()).filter(Boolean).join("\n");
    if (!text) { toast.error("Aucune information à copier"); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Nom et adresse copiés");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copie impossible");
    }
  };
  let ringCls = "";
  if (highlight && forced) ringCls = "ring-4 ring-[#D64545] ring-offset-1";
  else if (highlight) ringCls = "ring-2 ring-[var(--shariti-orange)] ring-offset-1";
  else if (forced) ringCls = "ring-2 ring-[#D64545]";
  else if (isField) ringCls = "ring-2 ring-[#7C3AED]";
  else if (declared) ringCls = "ring-2 ring-[#1F9D55]";
  return (
    <>
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDup(); }}>
      <PopoverTrigger asChild>
        <motion.button
          layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
          data-testid={`booking-${b.id}`}
          draggable={!dragLocked}
          onDragStart={(e) => {
            if (dragLocked) { e.preventDefault(); return; }
            e.dataTransfer.setData("booking_id", b.id);
            e.dataTransfer.effectAllowed = "move";
          }}
          className={`group/card relative block w-full overflow-hidden rounded-lg border p-1.5 text-left shadow-[var(--shadow-sm)] transition-transform hover:-translate-y-0.5 ${dragLocked ? "cursor-default" : "cursor-grab active:cursor-grabbing"} ${readonly ? "opacity-90" : ""} ${isCancelled ? "opacity-70 saturate-[0.45]" : ""} ${ringCls}`}
          data-cancelled={isCancelled ? "true" : undefined}
          style={isCancelled ? cancelledCellStyle(b.cdp?.color) : (isField ? fieldRebookCellStyle() : cdpCellStyle(b.cdp?.color))}>
          {isCancelled && <span className="absolute inset-y-0 left-0 w-1 bg-[#D64545]" aria-hidden />}
          {!isCancelled && forced && <span className="absolute inset-y-0 left-0 w-1 bg-[#D64545]" aria-hidden />}
          {!isCancelled && !forced && isField && <span className="absolute inset-y-0 left-0 w-1" style={{ background: FIELD_REBOOK_COLOR }} aria-hidden />}
          {!isCancelled && !forced && !isField && declared && <span className="absolute inset-y-0 left-0 w-1 bg-[#1F9D55]" aria-hidden />}
          {/* Nom + pastille asso + poignée drag */}
          <div className="flex items-start justify-between gap-1">
            <span className={`min-w-0 break-words text-[12px] font-extrabold leading-[1.15] ${isCancelled ? "line-through decoration-[#D64545] decoration-2 text-[rgba(24,24,24,0.55)]" : "text-[#181818]"}`} title={b.spot_name || ""} data-testid={`booking-name-${b.id}`}>{b.spot_name}</span>
            <span className="mt-0.5 flex shrink-0 items-center gap-0.5">
              {bulletinsCount > 0 && (
                <BulletinsBadge count={bulletinsCount} spotId={b.spot_id} date={bookingISO} dup={b.bulletins_doublon} />
              )}
              {canDeclare && !isHistorical && (
                <span role="button" tabIndex={0} draggable={false}
                  data-testid={`declare-toggle-${b.id}`}
                  aria-pressed={declared}
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDeclare && onDeclare(b, !declared); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onDeclare && onDeclare(b, !declared); } }}
                  title={declared ? "Annuler la déclaration à la coordination" : "Marquer comme déclaré à la coordination"}
                  className={`grid h-4 w-4 shrink-0 cursor-pointer place-items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F9D55] ${declared ? "border-[#1F9D55] bg-[#1F9D55] text-white" : "border-black/25 bg-white/70 text-transparent hover:border-[#1F9D55] hover:text-[#1F9D55]"}`}>
                  <Check className="h-2.5 w-2.5" />
                </span>
              )}
              {!dragLocked && <GripVertical className="h-3 w-3 text-[rgba(24,24,24,0.28)] opacity-0 transition-opacity group-hover/card:opacity-100" />}
              <span className="h-2 w-2 rounded-full border border-black/20" title={b.association?.acronym} style={{ background: b.association?.color }} />
            </span>
          </div>
          {/* Statut + tags */}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <BookingBadge status={b.status} />
            {b.is_demo && <span className="rounded bg-[rgba(24,24,24,0.08)] px-1 py-0.5 text-[8px] font-bold uppercase text-[rgba(24,24,24,0.55)]" data-testid={`demo-tag-${b.id}`}>Démo</span>}
            {b.is_historical && <span className="rounded bg-[rgba(99,102,241,0.12)] px-1 py-0.5 text-[8px] font-bold uppercase text-[#4338CA]" data-testid={`hist-tag-${b.id}`}>Hist.</span>}
            {forced && (
              <span className="inline-flex items-center gap-0.5 rounded bg-[#D64545] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white shadow-sm" data-testid={`forced-tag-${b.id}`} title={forcedIsParis ? "Forcé — contrainte Paris contournée (dernière semaine du mois)" : "Rebooking forcé — règle de rotation contournée"}>
                <Flag className="h-2.5 w-2.5" />{forcedIsParis ? "Forcé · Paris" : "Forcé"}
              </span>
            )}
            {isField && (
              <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white shadow-sm" style={{ background: FIELD_REBOOK_COLOR }} data-testid={`field-tag-${b.id}`} title={`Rebooké sur le terrain${b.field_rebooked_by ? ` par ${b.field_rebooked_by}` : ""}`}>
                <Footprints className="h-2.5 w-2.5" />Terrain
              </span>
            )}
            {b.director_confirmed && (
              <span className="inline-flex items-center gap-0.5 rounded bg-[rgba(31,157,85,0.14)] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-[#1F7A44]" data-testid={`confirmed-tag-${b.id}`} title="Confirmé par le responsable — attestation disponible">
                <ShieldCheck className="h-2.5 w-2.5" />Confirmé
              </span>
            )}
            {declared && (
              <span className="inline-flex items-center gap-0.5 rounded bg-[#1F9D55] px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-white shadow-sm" data-testid={`declared-tag-${b.id}`} title={`Déclaré à la coordination${b.declared_by ? ` par ${b.declared_by}` : ""}`}>
                <ClipboardCheck className="h-2.5 w-2.5" />Déclaré
              </span>
            )}
            {canManageTeam && !isHistorical && (
              <span role="button" tabIndex={0} draggable={false}
                data-testid={`booking-team-${b.id}`}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(b); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); e.preventDefault(); onTeam && onTeam(b); } }}
                title="Composer l'équipe du jour (pointage terrain)"
                className={`inline-flex cursor-pointer items-center gap-0.5 rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)] ${teamCount ? "bg-[var(--shariti-orange)] text-white hover:bg-[#d9631f]" : "bg-[rgba(238,114,46,0.12)] text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.22)]"}`}>
                <Users className="h-2.5 w-2.5" />Équipe{typeof teamCount === "number" && teamCount > 0 ? ` (${teamCount})` : ""}
              </span>
            )}
          </div>
          {/* Adresse */}
          {b.spot_address && (
            <div className="mt-1 flex items-start gap-1 text-[9px] leading-snug text-[rgba(24,24,24,0.6)]" data-testid={`booking-address-${b.id}`}>
              <MapPin className="mt-[1px] h-2.5 w-2.5 shrink-0" /><span className="min-w-0 break-words text-[10px]" title={b.spot_address || ""}>{b.spot_address}</span>
            </div>
          )}
          {/* Responsable autorisé */}
          {b.spot_responsable && (
            <div className="mt-0.5 flex items-start gap-1 text-[9px] leading-snug text-[rgba(24,24,24,0.6)]" data-testid={`booking-responsable-${b.id}`}>
              <UserIcon className="mt-[1px] h-2.5 w-2.5 shrink-0" /><span className="min-w-0 break-words text-[10px]" title={`${b.spot_responsable || ""}${b.spot_responsable_fonction ? ` · ${b.spot_responsable_fonction}` : ""}`}>{b.spot_responsable}{b.spot_responsable_fonction ? ` · ${b.spot_responsable_fonction}` : ""}</span>
            </div>
          )}
          {/* Consignes réelles */}
          {b.consignes && (
            <div className="mt-1 rounded bg-[rgba(252,219,83,0.35)] px-1.5 py-0.5 text-[9px] font-medium leading-snug text-[#5A4200] break-words" data-testid={`consignes-${b.id}`}>{b.consignes}</div>
          )}
          {/* Date de booking (uniquement pour les créneaux live ; l'historique = date d'import non pertinente) */}
          {!isHistorical && b.booked_at && (
            <div className="mt-1 flex items-center gap-1 text-[8px] font-medium uppercase tracking-wide text-[rgba(24,24,24,0.42)]" data-testid={`booking-date-${b.id}`}>
              <CalendarClock className="h-2.5 w-2.5 shrink-0" /> Booké le {frenchDateShort(String(b.booked_at).slice(0, 10))}
            </div>
          )}
        </motion.button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="mb-1 flex items-start justify-between gap-2">
          <div className="min-w-0 text-sm font-extrabold leading-tight">{b.spot_name}</div>
          <button type="button" onClick={copyNameAddress} data-testid={`booking-copy-${b.id}`}
            title="Copier le nom du magasin et l'adresse exacte"
            className="-mr-1 -mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-ink transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--shariti-orange)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]">
            {copied ? <Check className="h-3.5 w-3.5 text-[#1F9D55]" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        {b.spot_address && <div className="flex items-start gap-1.5 text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /> {b.spot_address}</div>}
        {b.spot_responsable && <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-ink"><UserIcon className="h-3 w-3 shrink-0" /> {b.spot_responsable}{b.spot_responsable_fonction ? ` · ${b.spot_responsable_fonction}` : ""}</div>}
        {readonly ? (
          b.cdp?.name && (
            <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-ink" data-testid={`booking-cdp-detail-${b.id}`}>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/20" style={{ background: b.cdp.color }} />
              Réservé par <span className="font-semibold text-ink">{b.cdp.name}</span>
            </div>
          )
        ) : (
          <button type="button" onClick={() => setCdpMode(true)} data-testid={`booking-cdp-detail-${b.id}`}
            title="Créditer / changer le CDP qui a trouvé ce spot (adapte la couleur du créneau)"
            className="mt-1 flex w-full items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-[11px] text-muted-ink transition-colors hover:bg-[var(--surface-2)]">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/20" style={{ background: b.cdp?.color || "var(--surface-2)" }} />
            {b.cdp?.name
              ? <>Réservé par <span className="font-semibold text-ink">{b.cdp.name}</span></>
              : <span className="font-semibold text-[var(--shariti-orange)]">Créditer un CDP</span>}
            <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-ink" />
          </button>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <BookingBadge status={b.status} />
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-ink"><span className="h-2 w-2 rounded-full border border-black/20" style={{ background: b.association?.color }} /> {b.association?.acronym}</span>
          {b.code_campagne && <span className="rounded bg-[var(--surface-2)] px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#181818]" data-testid={`booking-code-${b.id}`}>{b.code_campagne}</span>}
        </div>
        {b.consignes && <div className="mt-2 rounded-md border border-[rgba(252,219,83,0.55)] bg-[rgba(252,219,83,0.35)] px-2 py-1 text-[11px] font-medium text-[#5A4200]">{b.consignes}</div>}
        {b.rebooking_forced && (
          <div className="mt-2 rounded-md border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.06)] px-2 py-1.5 text-[11px] text-[#9B2226]" data-testid={`forced-detail-${b.id}`}>
            <div className="flex items-center gap-1 font-bold"><AlertTriangle className="h-3.5 w-3.5" /> {forcedIsParis ? "Forçage · Contrainte Paris" : "Rebooking forcé (rotation)"}</div>
            {b.forced_by_user_name && <div className="mt-0.5">Par {b.forced_by_user_name}{b.forced_at ? ` le ${frenchDate(String(b.forced_at).slice(0, 10))}` : ""}</div>}
            {b.forced_reason && <div className="mt-0.5 text-[#7a2a2d]">{b.forced_reason}</div>}
          </div>
        )}
        {isField && (
          <div className="mt-2 rounded-md border px-2 py-1.5 text-[11px]" style={{ borderColor: "rgba(124,58,237,0.4)", background: "rgba(124,58,237,0.06)", color: "#5B21B6" }} data-testid={`field-detail-${b.id}`}>
            <div className="flex items-center gap-1 font-bold"><Footprints className="h-3.5 w-3.5" /> Rebooking terrain</div>
            <div className="mt-0.5">Reprogrammé sur le terrain{b.field_rebooked_by ? ` par ${b.field_rebooked_by}` : ""}{b.field_rebooked_at ? ` le ${frenchDate(String(b.field_rebooked_at).slice(0, 10))}` : ""}.</div>
          </div>
        )}
        {isCancelled && (
          <div className="mt-2 rounded-md border border-[rgba(214,69,69,0.4)] bg-[rgba(214,69,69,0.06)] px-2 py-1.5 text-[11px] text-[#9B2226]" data-testid={`cancelled-detail-${b.id}`}>
            <div className="flex items-center gap-1 font-bold"><Ban className="h-3.5 w-3.5" /> Créneau annulé (conservé)</div>
            {b.annule_by && <div className="mt-0.5">Par {b.annule_by}{b.annule_at ? ` le ${frenchDate(String(b.annule_at).slice(0, 10))}` : ""}</div>}
            {b.annulation_motif && <div className="mt-0.5 text-[#7a2a2d]">Motif : {ANNUL_MOTIF_LABELS[b.annulation_motif] || b.annulation_motif}{b.annulation_note ? ` — ${b.annulation_note}` : ""}</div>}
            <div className="mt-0.5 text-[10px] text-[#7a2a2d]">Exclu de la facturation et des stats. Réversible.</div>
          </div>
        )}
        {!isHistorical && b.booked_at && <div className="mt-2 text-[10px] text-[rgba(24,24,24,0.55)]">Booké le {frenchDate(String(b.booked_at).slice(0, 10))}{b.booked_by ? ` • ${b.booked_by}` : ""}</div>}
        <div className="my-2 border-t border-[var(--border-soft)]" />
        {spotMode ? (
          <div data-testid={`booking-change-spot-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <Store className="h-3.5 w-3.5" /> Changer le magasin
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">
              Corrige le magasin de ce créneau <b>sans perdre les heures, les bulletins ni le statut « Déclaré »</b>. Recherchez le bon magasin ci-dessous.
            </p>
            <div className="relative mb-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
              <input value={spotQuery} onChange={(e) => setSpotQuery(e.target.value)} autoFocus
                data-testid={`change-spot-search-${b.id}`} placeholder="Nom, enseigne ou ville…"
                className="w-full rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] py-1.5 pl-8 pr-2 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
            </div>
            <div className="max-h-[240px] space-y-1 overflow-y-auto no-scrollbar">
              {spotSearching && <div className="flex items-center gap-2 px-2 py-2 text-[12px] text-muted-ink"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Recherche…</div>}
              {!spotSearching && spotQuery.trim().length >= 2 && spotResults.length === 0 && (
                <p className="rounded-lg bg-[var(--surface-2)] px-2 py-2 text-[11px] text-muted-ink">Aucun magasin trouvé.</p>
              )}
              {spotResults.filter((s) => s.id !== b.spot_id).map((s) => (
                <button key={s.id} type="button" disabled={spotSaving}
                  data-testid={`change-spot-option-${b.id}-${s.id}`}
                  onClick={async () => {
                    setSpotSaving(true);
                    const ok = await (onChangeSpot && onChangeSpot(s.id));
                    setSpotSaving(false);
                    if (ok) { setSpotMode(false); setSpotQuery(""); setSpotResults([]); setOpen(false); }
                  }}
                  className="flex w-full items-start justify-between gap-2 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-left text-sm transition-colors hover:border-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.06)] disabled:opacity-50">
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-ink">{s.name || s.enseigne || "Magasin"}</span>
                    <span className="block truncate text-[11px] text-muted-ink">{[s.ville, s.dept ? `(${s.dept})` : null].filter(Boolean).join(" ")}</span>
                  </span>
                  {spotSaving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-ink" /> : <ArrowRightLeft className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
                </button>
              ))}
              {spotQuery.trim().length < 2 && (
                <p className="rounded-lg bg-[var(--surface-2)] px-2 py-2 text-[11px] text-muted-ink">Tapez au moins 2 caractères pour rechercher.</p>
              )}
            </div>
            <div className="mt-2">
              <button onClick={() => { setSpotMode(false); setSpotQuery(""); setSpotResults([]); }} data-testid={`change-spot-cancel-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
            </div>
          </div>
        ) : dupMode ? (
          <div data-testid={`booking-duplicate-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <Copy className="h-3.5 w-3.5" /> Dupliquer sur d'autres jours
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Cochez les jours de la semaine où ajouter ce spot (même statut et consignes).</p>
            {dupAvailable.length > 0 && (
              <button type="button" onClick={toggleSelectAll} data-testid={`duplicate-select-all-${b.id}`}
                className="mb-1.5 text-[11px] font-semibold text-[var(--shariti-orange)] hover:underline">
                {allAvailSelected ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            )}
            <div className="max-h-[220px] space-y-1 overflow-y-auto no-scrollbar">
              {dupDays.map((d) => {
                const occupied = !!(ligneOccupiedDates && ligneOccupiedDates.has(d.date));
                const checked = dupSel.has(d.date);
                return (
                  <button key={d.date} type="button" disabled={occupied} onClick={() => toggleDupDay(d.date)}
                    data-testid={`duplicate-day-${b.id}-${d.date}`}
                    className={`flex w-full items-center justify-between rounded-lg border px-2 py-1.5 text-sm transition-colors ${occupied ? "cursor-not-allowed opacity-45" : checked ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <span className="flex items-center gap-2">
                      <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${checked ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white" : "border-[var(--border-soft)]"}`}>
                        {checked && <Check className="h-3 w-3" />}
                      </span>
                      <span className="font-medium capitalize">{d.label}</span>
                      <span className="text-[11px] text-muted-ink">{frenchDateShort(d.date)}</span>
                    </span>
                    {occupied && <span className="text-[10px] font-semibold text-muted-ink">déjà pris</span>}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={resetDup} data-testid={`duplicate-cancel-${b.id}`}
                className="flex-1 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
              <button onClick={confirmDup} disabled={dupSel.size === 0} data-testid={`duplicate-confirm-${b.id}`}
                className="flex-1 rounded-lg bg-[var(--shariti-orange)] px-2 py-1.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
                Dupliquer{dupSel.size ? ` (${dupSel.size})` : ""}
              </button>
            </div>
          </div>
        ) : newContactMode ? (
          <div data-testid={`booking-new-contact-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <UserIcon className="h-3.5 w-3.5" /> Nouveau contact
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Enregistré sur le spot et appliqué comme contact ayant autorisé ce créneau.</p>
            <div className="space-y-1.5">
              <div className="flex gap-1.5">
                <input value={newContact.civilite} onChange={(e) => setNewContact((c) => ({ ...c, civilite: e.target.value }))}
                  placeholder="Civ." maxLength={12} data-testid={`new-contact-civilite-${b.id}`}
                  className="w-16 shrink-0 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
                <input value={newContact.prenom} onChange={(e) => setNewContact((c) => ({ ...c, prenom: e.target.value }))}
                  placeholder="Prénom" data-testid={`new-contact-prenom-${b.id}`}
                  className="min-w-0 flex-1 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
              </div>
              <input value={newContact.nom} onChange={(e) => setNewContact((c) => ({ ...c, nom: e.target.value }))}
                placeholder="Nom" data-testid={`new-contact-nom-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
              <input value={newContact.fonction} onChange={(e) => setNewContact((c) => ({ ...c, fonction: e.target.value }))}
                placeholder="Fonction (ex : Directeur)" data-testid={`new-contact-fonction-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
              <input value={newContact.telephone} onChange={(e) => setNewContact((c) => ({ ...c, telephone: e.target.value }))}
                placeholder="Téléphone" inputMode="tel" data-testid={`new-contact-telephone-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
              <input value={newContact.email} onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))}
                placeholder="E-mail (facultatif)" inputMode="email" data-testid={`new-contact-email-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setNewContactMode(false)} data-testid={`new-contact-cancel-${b.id}`}
                className="flex-1 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
              <button disabled={savingContact} data-testid={`new-contact-save-${b.id}`}
                onClick={async () => {
                  setSavingContact(true);
                  const ok = await (onCreateContact && onCreateContact(b, newContact));
                  setSavingContact(false);
                  if (ok) { setNewContactMode(false); setNewContact(emptyContact); setContactMode(false); setOpen(false); }
                }}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[var(--shariti-orange)] px-2 py-1.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
                {savingContact ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
              </button>
            </div>
          </div>
        ) : contactMode ? (
          <div data-testid={`booking-contact-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <UserIcon className="h-3.5 w-3.5" /> Contact qui a autorisé
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Choisissez le contact du magasin qui a autorisé cette venue.</p>
            <div className="max-h-[240px] space-y-1 overflow-y-auto no-scrollbar">
              <button type="button" onClick={() => { onSetContact && onSetContact(b, null); setContactMode(false); setOpen(false); }}
                data-testid={`contact-option-default-${b.id}`}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition-colors ${!hasOverride ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                <span className="min-w-0">
                  <span className="block truncate font-medium">Contact principal (par défaut)</span>
                  {spotContacts.find((c) => c.is_principal) && (
                    <span className="block truncate text-[11px] text-muted-ink">{contactLabel(spotContacts.find((c) => c.is_principal))}</span>
                  )}
                </span>
                {!hasOverride && <Check className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
              </button>
              {spotContacts.map((c, i) => {
                const lbl = contactLabel(c);
                const selected = hasOverride && contactLabel(b.authorized_contact) === lbl && (b.authorized_contact.fonction || null) === (c.fonction || null);
                return (
                  <button key={i} type="button" onClick={() => { onSetContact && onSetContact(b, c); setContactMode(false); setOpen(false); }}
                    data-testid={`contact-option-${b.id}-${i}`}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition-colors ${selected ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{lbl || "(sans nom)"}</span>
                      {(c.fonction || c.telephone) && <span className="block truncate text-[11px] text-muted-ink">{[c.fonction, c.telephone].filter(Boolean).join(" · ")}</span>}
                    </span>
                    {selected && <Check className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
                  </button>
                );
              })}
              {spotContacts.length === 0 && (
                <p className="rounded-lg bg-[var(--surface-2)] px-2 py-2 text-[11px] text-muted-ink">Aucun contact enregistré sur ce spot. Créez-en un ci-dessous.</p>
              )}
            </div>
            <button type="button" onClick={() => { setNewContact(emptyContact); setNewContactMode(true); }}
              data-testid={`contact-create-open-${b.id}`}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[var(--shariti-orange)] px-2 py-1.5 text-sm font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)]">
              <Plus className="h-4 w-4" /> Créer un nouveau contact
            </button>
            <div className="mt-2">
              <button onClick={() => setContactMode(false)} data-testid={`contact-cancel-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
            </div>
          </div>
        ) : cdpMode ? (
          <div data-testid={`booking-cdp-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <UserIcon className="h-3.5 w-3.5" /> Réservé par
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Choisissez le CDP qui a trouvé / réservé ce spot — la couleur du créneau s'adapte.</p>
            <div className="max-h-[240px] space-y-1 overflow-y-auto no-scrollbar">
              {cdpList.map((c) => {
                const selected = b.cdp_id === c.id;
                return (
                  <button key={c.id} type="button" onClick={() => { onSetCdp && onSetCdp(b, c); setCdpMode(false); setOpen(false); }}
                    data-testid={`cdp-option-${b.id}-${c.id}`}
                    className={`flex w-full items-center justify-between gap-2 rounded-lg border px-2 py-1.5 text-left text-sm transition-colors ${selected ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.08)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="h-3 w-3 shrink-0 rounded-full border border-black/20" style={{ background: c.color }} />
                      <span className="break-words font-medium">{c.name}</span>
                    </span>
                    {selected && <Check className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
                  </button>
                );
              })}
              {cdpList.length === 0 && (
                <p className="rounded-lg bg-[var(--surface-2)] px-2 py-2 text-[11px] text-muted-ink">Aucun CDP disponible.</p>
              )}
            </div>
            <div className="mt-2">
              <button onClick={() => setCdpMode(false)} data-testid={`cdp-cancel-${b.id}`}
                className="w-full rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
            </div>
          </div>
        ) : consignesMode ? (
          <div data-testid={`booking-consignes-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <ClipboardList className="h-3.5 w-3.5" /> Modifier les consignes
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Visibles sur la carte et reprises dans la diffusion (WhatsApp + email).</p>
            <textarea value={consignesDraft} onChange={(e) => setConsignesDraft(e.target.value)} rows={4}
              data-testid={`consignes-input-${b.id}`}
              placeholder="Ex : se présenter à l'accueil, rester devant l'entrée…"
              className="w-full resize-none rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setConsignesMode(false)} data-testid={`consignes-cancel-${b.id}`}
                className="flex-1 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
              <button onClick={() => { onSetConsignes && onSetConsignes(b, consignesDraft); setConsignesMode(false); setOpen(false); }}
                data-testid={`consignes-save-${b.id}`}
                className="flex-1 rounded-lg bg-[var(--shariti-orange)] px-2 py-1.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">Enregistrer</button>
            </div>
          </div>
        ) : cancelMode ? (
          <div data-testid={`booking-cancel-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-muted-ink">
              <Ban className="h-3.5 w-3.5" /> Annuler le créneau
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">Le créneau reste visible (barré) et conservé pour l'historique. Il est exclu de la facturation et des stats. Réversible.</p>
            <div className="mb-1 text-[10px] font-semibold uppercase text-muted-ink">Motif</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(ANNUL_MOTIF_LABELS).map(([v, l]) => (
                <button key={v} type="button" onClick={() => setCancelMotif(v)} data-testid={`cancel-motif-${b.id}-${v}`}
                  className={`rounded-lg border px-2 py-1.5 text-left text-[12px] font-medium transition-colors ${cancelMotif === v ? "border-[#D64545] bg-[rgba(214,69,69,0.08)] text-[#9B2226]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>{l}</button>
              ))}
            </div>
            <textarea value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} rows={2}
              data-testid={`cancel-note-${b.id}`}
              placeholder="Précision (facultatif)…"
              className="mt-2 w-full resize-none rounded-lg border border-[var(--border-soft)] bg-[var(--surface-1)] px-2 py-1.5 text-sm outline-none focus:border-[#D64545] focus:ring-2 focus:ring-[rgba(214,69,69,0.15)]" />
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setCancelMode(false)} data-testid={`cancel-back-${b.id}`}
                className="flex-1 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
              <button onClick={() => { onCancel && onCancel(cancelMotif, cancelNote.trim() || undefined); setCancelMode(false); setCancelNote(""); setCancelMotif("autre"); setOpen(false); }}
                data-testid={`cancel-confirm-${b.id}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#D64545] px-2 py-1.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                <Ban className="h-4 w-4" /> Annuler le créneau
              </button>
            </div>
          </div>
        ) : blacklistMode ? (
          <div data-testid={`booking-blacklist-panel-${b.id}`}>
            <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase text-[#9B2226]">
              <Ban className="h-3.5 w-3.5" /> Blacklister ce spot
            </div>
            <p className="mb-2 text-[11px] leading-snug text-muted-ink">
              <span className="font-semibold text-ink">{b.spot_name || "Ce magasin"}</span> ne réapparaîtra plus
              dans la <span className="font-semibold">prospection</span> ni dans la <span className="font-semibold">recherche de spots</span>,
              et ne pourra <span className="font-semibold">plus être réservé</span>. Le créneau actuel reste inchangé.
              Réactivation possible dans <span className="font-semibold">Prospection ▸ Liste noire</span>.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setBlacklistMode(false)} data-testid={`blacklist-back-${b.id}`}
                className="flex-1 rounded-lg border border-[var(--border-soft)] px-2 py-1.5 text-sm font-semibold hover:bg-[var(--surface-2)]">Retour</button>
              <button disabled={blacklistBusy}
                onClick={async () => { setBlacklistBusy(true); const ok = await (onBlacklist && onBlacklist(true)); setBlacklistBusy(false); if (ok) { setBlacklistMode(false); setOpen(false); } }}
                data-testid={`blacklist-confirm-${b.id}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#9B2226] px-2 py-1.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {blacklistBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Blacklister
              </button>
            </div>
          </div>
        ) : (
          <>
            <button onClick={onBulletins} data-testid={`booking-bulletins-${b.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]"><ClipboardList className="h-4 w-4" /> Bulletins & présence</button>
            <button onClick={onView} data-testid={`booking-view-${b.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)]"><ExternalLink className="h-4 w-4" /> Ouvrir la fiche</button>
            <button onClick={listenToSourceCall} disabled={callBusy} data-testid={`booking-listen-call-${b.id}`}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)] disabled:opacity-60"
              title="Écouter l'enregistrement de l'appel qui a permis de positionner cet emplacement">
              {callBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4 text-[var(--shariti-orange)]" />} Écouter l'appel
            </button>
            {canThank && (
              <button onClick={() => { onThank(); setOpen(false); }} disabled={thankBusy} data-testid={`booking-thank-${b.id}`}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[rgba(238,114,46,0.08)] disabled:opacity-60"
                title="Envoyer un mail de remerciement au magasin après la visite">
                {thankBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />} Remercier le magasin
              </button>
            )}
            {canDeclare && !isHistorical && !isCancelled && (
              <button onClick={() => onDeclare && onDeclare(b, !declared)} data-testid={`booking-declare-${b.id}`}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold transition-colors ${declared ? "text-[#1F7A44] hover:bg-[rgba(31,157,85,0.10)]" : "text-[var(--shariti-orange)] hover:bg-[rgba(238,114,46,0.08)]"}`}>
                {declared ? <CheckCircle2 className="h-4 w-4" /> : <ClipboardCheck className="h-4 w-4" />}
                {declared ? "Annuler la déclaration coordination" : "Déclarer à la coordination"}
              </button>
            )}
            {readonly ? (
              <div className="mt-1 flex items-center gap-1.5 rounded-lg bg-[rgba(99,102,241,0.08)] px-2 py-1.5 text-[11px] font-medium text-[#4338CA]" data-testid={`booking-readonly-${b.id}`}>
                <History className="h-3.5 w-3.5" /> {isHistorical ? "Créneau historique — lecture seule" : "Lecture seule — modification non autorisée"}
              </div>
            ) : isCancelled ? (
              <>
                {canCancel && (
                  <button onClick={() => { onRestore && onRestore(); setOpen(false); }} data-testid={`booking-restore-${b.id}`}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-[#1F7A44] hover:bg-[rgba(31,157,85,0.10)]">
                    <RotateCcw className="h-4 w-4" /> Rétablir le créneau
                  </button>
                )}
                <button onClick={onDelete} data-testid={`booking-delete-${b.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]"><Trash2 className="h-4 w-4" /> Supprimer définitivement</button>
              </>
            ) : (
              <>
                {dupDays.length > 0 && (
                  <button onClick={() => { setDupSel(new Set()); setDupMode(true); }} data-testid={`booking-duplicate-${b.id}`}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)]">
                    <Copy className="h-4 w-4 text-[var(--shariti-orange)]" /> Dupliquer sur d'autres jours
                  </button>
                )}
                <button onClick={() => setContactMode(true)} data-testid={`booking-contact-${b.id}`}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)]">
                  <UserIcon className="h-4 w-4 text-[var(--shariti-orange)]" /> Contact qui a autorisé
                </button>
                <button onClick={() => { setConsignesDraft(b.consignes || ""); setConsignesMode(true); }} data-testid={`booking-consignes-${b.id}`}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)]">
                  <ClipboardList className="h-4 w-4 text-[var(--shariti-orange)]" /> Modifier les consignes
                </button>
                {onChangeSpot && (
                  <button onClick={() => { setSpotQuery(""); setSpotResults([]); setSpotMode(true); }} data-testid={`booking-change-spot-${b.id}`}
                    className="my-1 flex w-full items-start gap-2 rounded-lg border border-[rgba(238,114,46,0.45)] bg-[rgba(238,114,46,0.09)] px-2.5 py-2 text-left transition-colors hover:bg-[rgba(238,114,46,0.17)]"
                    title="Corriger le magasin de ce créneau sans perdre les heures, les bulletins ni le statut « Déclaré »">
                    <ArrowRightLeft className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-[#8A3A10]">Changer le magasin</span>
                      <span className="block text-[10px] leading-tight text-[#8A3A10]/80">Corrige une erreur de magasin — conserve heures, bulletins et « Déclaré »</span>
                    </span>
                  </button>
                )}
                <div className="my-1 px-2 text-[10px] font-semibold uppercase text-muted-ink">Changer le statut</div>
                {BOOKING_STATUSES.map((s) => (
                  <button key={s.v} onClick={() => onStatus(s.v)} data-testid={`booking-status-set-${s.v}`} className={`flex w-full items-center rounded-lg px-2 py-1.5 text-sm hover:bg-[var(--surface-2)] ${b.status === s.v ? "font-bold text-[var(--shariti-orange)]" : ""}`}>{s.l}</button>
                ))}
                <div className="my-1 border-t border-[var(--border-soft)]" />
                <button onClick={() => onFieldToggle && onFieldToggle(b, !isField)} data-testid={`booking-field-toggle-${b.id}`}
                  className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[rgba(124,58,237,0.08)]"
                  style={{ color: FIELD_REBOOK_COLOR }}>
                  <Footprints className="h-4 w-4" /> {isField ? "Retirer le tag terrain" : "Marquer comme rebooking terrain"}
                </button>
                {canCancel && (
                  <button onClick={() => { setCancelMotif("autre"); setCancelNote(""); setCancelMode(true); }} data-testid={`booking-cancel-${b.id}`}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-[#9B2226] hover:bg-[rgba(214,69,69,0.08)]">
                    <Ban className="h-4 w-4" /> Annuler le créneau (garder barré)
                  </button>
                )}
                {canBlacklist && onBlacklist && b.spot_id && (
                  <button onClick={() => setBlacklistMode(true)} data-testid={`booking-blacklist-${b.id}`}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-[#9B2226] hover:bg-[rgba(155,34,38,0.08)]"
                    title="Blacklister ce spot : plus jamais proposé en prospection ni réservable">
                    <Ban className="h-4 w-4" /> Blacklister le spot
                  </button>
                )}
                <button onClick={onDelete} data-testid={`booking-delete-${b.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-[#D64545] hover:bg-[rgba(214,69,69,0.08)]"><Trash2 className="h-4 w-4" /> Supprimer</button>
              </>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
    </>
  );
}


function AssignDialog({ ctx, onClose, spots, search, setSearch, onAssign, initialSpot, chefs = [], onCreateChef, onUpdateChef, cdps = [], canCredit = false }) {
  const [status, setStatus] = useState("au_crayon");
  const [selected, setSelected] = useState(null);
  const [consignes, setConsignes] = useState("");
  const [fieldRebooking, setFieldRebooking] = useState(false);
  const [fieldBy, setFieldBy] = useState("");
  const [chefBoxOpen, setChefBoxOpen] = useState(false);
  const [creatingChef, setCreatingChef] = useState(false);
  const [creditedCdp, setCreditedCdp] = useState("");   // crédit CDP (couleur) — admin
  // Complétion de fiche (email/téléphone) proposée juste après création d'un responsable.
  const [completeChef, setCompleteChef] = useState(null);
  const [ceEmail, setCeEmail] = useState("");
  const [ceTel, setCeTel] = useState("");
  const [ceSaving, setCeSaving] = useState(false);
  useEffect(() => {
    if (ctx) {
      setSelected(initialSpot || null); setStatus("au_crayon"); setConsignes("");
      setFieldRebooking(false); setCreditedCdp("");
      setFieldBy(ctx.ligne?.chef_nom || ctx.ligne?.chef_prenom || "");
    }
  }, [ctx, initialSpot]);
  // Pré-remplir la consigne existante du spot sélectionné (si validée par l'équipe)
  useEffect(() => {
    if (selected && selected.consignes_source !== "import") setConsignes(selected.consignes || "");
    else setConsignes("");
  }, [selected]);
  if (!ctx) return null;
  const options = initialSpot && !spots.some((s) => s.id === initialSpot.id) ? [initialSpot, ...spots] : spots;
  const saveCompleteChef = async () => {
    if (!completeChef) return;
    setCeSaving(true);
    try {
      await onUpdateChef(completeChef.id, { email: ceEmail.trim(), tel: ceTel.trim() });
      toast.success("Coordonnées enregistrées");
      setCompleteChef(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setCeSaving(false);
    }
  };
  return (
    <>
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg overflow-hidden [&>*]:min-w-0">
        <DialogHeader>
          <DialogTitle>Assigner un spot</DialogTitle>
          <p className="text-xs text-muted-ink">{ctx.ligne.identifiant} • {frenchDate(ctx.date)}</p>
        </DialogHeader>
        <div className="min-w-0 space-y-3">
          {Number.isInteger(ctx.lane) && ctx.lane > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] px-3 py-2 text-xs font-semibold text-[#8A3A10]" data-testid="assign-parallel-hint">
              <CornerDownRight className="h-3.5 w-3.5 shrink-0" />
              Carte supplémentaire ce jour — {ctx.lane + 1}<sup>e</sup> spot en parallèle sur cette ligne.
            </div>
          )}
          {initialSpot && (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.07)] px-3 py-2 text-xs text-[#8A3A10]" data-testid="assign-prefilled">
              <MapPin className="h-3.5 w-3.5 shrink-0" />Spot pré-sélectionné : <span className="font-semibold truncate">{initialSpot.normalized_name}</span>
            </div>
          )}
          <div className="relative min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un spot…" className="h-10 w-full pl-9" data-testid="assign-search" autoFocus />
          </div>
          <div className="max-h-48 min-w-0 space-y-1 overflow-y-auto no-scrollbar">
            {options.map((s) => (
              <button key={s.id} onClick={() => setSelected(s)} data-testid={`assign-option-${s.id}`}
                className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm ${selected?.id === s.id ? "border-[var(--shariti-orange)] bg-[rgba(238,114,46,0.06)]" : "border-[var(--border-soft)] hover:bg-[var(--surface-2)]"}`}>
                <div className="min-w-0 flex-1">
                  <div className="break-words font-bold">{s.normalized_name}</div>
                  <div className="flex items-start gap-1 break-words text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{s.ville} {s.dept ? `(${s.dept})` : ""}</div>
                </div>
              </button>
            ))}
            {options.length === 0 && <div className="py-6 text-center text-sm text-muted-ink">Aucun spot trouvé.</div>}
          </div>
          <div className="min-w-0">
            <div className="mb-1 text-xs font-semibold">Statut de la réservation</div>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full" data-testid="assign-status"><SelectValue /></SelectTrigger>
              <SelectContent>{BOOKING_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-1 text-xs font-semibold"><ClipboardList className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />Consignes particulières <span className="font-normal text-muted-ink">(facultatif)</span></div>
            <Textarea value={consignes} onChange={(e) => setConsignes(e.target.value)} rows={2}
              placeholder="Ex. : se présenter à l'accueil, badge obligatoire, horaires spécifiques du responsable…"
              className="w-full resize-none text-sm" data-testid="assign-consignes" />
          </div>
          {/* Crédit CDP (admin) : attribuer le spot à un CDP par sa couleur, même sans ligne */}
          {canCredit && cdps.length > 0 && (
            <div className="min-w-0 rounded-lg border px-3 py-2.5" style={{ borderColor: creditedCdp ? "rgba(238,114,46,0.5)" : "var(--border-soft)", background: creditedCdp ? "rgba(238,114,46,0.05)" : "transparent" }}>
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold">
                <UserRound className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
                Créditer un CDP (couleur) <span className="font-normal text-muted-ink">(facultatif)</span>
              </div>
              <p className="mb-2 text-[11px] leading-relaxed text-muted-ink">Attribuez ce spot à un CDP même s'il n'a pas de ligne cette semaine : le créneau prendra sa couleur pour indiquer qui l'a trouvé.</p>
              <Select value={creditedCdp || "none"} onValueChange={(v) => setCreditedCdp(v === "none" ? "" : v)}>
                <SelectTrigger className="w-full" data-testid="assign-credit-cdp">
                  <SelectValue placeholder="Aucun (couleur de la ligne)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun (couleur de la ligne)</SelectItem>
                  {cdps.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`assign-credit-cdp-${c.id}`}>
                      <span className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {/* Rebooking terrain : reprogrammé par un responsable d'équipe sur place */}
          <div className="min-w-0 rounded-lg border px-3 py-2.5" style={{ borderColor: fieldRebooking ? "rgba(124,58,237,0.5)" : "var(--border-soft)", background: fieldRebooking ? "rgba(124,58,237,0.05)" : "transparent" }}>
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="assign-field-rebooking" className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: fieldRebooking ? "#5B21B6" : undefined }}>
                <Footprints className="h-3.5 w-3.5" style={{ color: FIELD_REBOOK_COLOR }} /> Rebooké par un responsable terrain
              </Label>
              <Switch id="assign-field-rebooking" checked={fieldRebooking} onCheckedChange={setFieldRebooking} data-testid="assign-field-rebooking-switch" />
            </div>
            {fieldRebooking && (
              <div className="mt-2">
                <div className="relative">
                  <Input value={fieldBy}
                    onChange={(e) => { setFieldBy(e.target.value); setChefBoxOpen(true); }}
                    onFocus={() => setChefBoxOpen(true)}
                    onBlur={() => setTimeout(() => setChefBoxOpen(false), 150)}
                    maxLength={80} autoComplete="off"
                    placeholder="Choisir ou saisir un responsable d'équipe…"
                    data-testid="assign-field-rebooked-by" />
                  {chefBoxOpen && (() => {
                    const q = fieldBy.trim();
                    const ql = q.toLowerCase();
                    const list = (chefs || []).filter((c) => c && c.nom && (!ql || c.nom.toLowerCase().includes(ql) || ql.includes(c.nom.toLowerCase())));
                    const exact = (chefs || []).some((c) => c && c.nom && c.nom.toLowerCase() === ql);
                    const canCreate = !!q && !exact && !!onCreateChef;
                    if (list.length === 0 && !canCreate) return null;
                    const handleCreate = async () => {
                      if (creatingChef) return;
                      setCreatingChef(true);
                      try {
                        const created = await onCreateChef(q);
                        setFieldBy(created?.nom || q);
                        setChefBoxOpen(false);
                        toast.success(`Responsable « ${created?.nom || q} » ajouté`);
                        // Propose de compléter la fiche (email/téléphone) pour plus tard.
                        if (created?.id && onUpdateChef) {
                          setCeEmail(created.email || "");
                          setCeTel(created.tel || "");
                          setCompleteChef(created);
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Création impossible");
                      } finally {
                        setCreatingChef(false);
                      }
                    };
                    return (
                      <div className="absolute left-0 right-0 bottom-[calc(100%+4px)] z-50 max-h-44 overflow-y-auto rounded-lg border border-[var(--border-soft)] bg-white p-1 shadow-[var(--shadow-md)]" data-testid="assign-field-chef-suggestions">
                        {list.map((c) => (
                          <button key={c.id} type="button"
                            onMouseDown={(e) => { e.preventDefault(); setFieldBy(c.nom); setChefBoxOpen(false); }}
                            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[rgba(124,58,237,0.08)]"
                            data-testid={`assign-field-chef-${c.id}`}>
                            <UserRound className="h-3.5 w-3.5 shrink-0" style={{ color: FIELD_REBOOK_COLOR }} />
                            <span className="break-words font-semibold">{c.nom}</span>
                          </button>
                        ))}
                        {canCreate && (
                          <button type="button"
                            onMouseDown={(e) => { e.preventDefault(); handleCreate(); }}
                            disabled={creatingChef}
                            className="mt-0.5 flex w-full items-center gap-2 rounded-md border-t border-[var(--border-soft)] px-2 py-1.5 text-left text-sm font-semibold hover:bg-[rgba(124,58,237,0.08)] disabled:opacity-50"
                            style={{ color: FIELD_REBOOK_COLOR }}
                            data-testid="assign-field-chef-create">
                            {creatingChef ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Plus className="h-3.5 w-3.5 shrink-0" />}
                            <span className="truncate">Créer le responsable « {q} »</span>
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <p className="mt-1 text-[11px] text-muted-ink">Sélectionnez un chef d'équipe dans la liste ou saisissez un nom. Ce créneau apparaîtra avec un <strong style={{ color: FIELD_REBOOK_COLOR }}>code couleur violet unique</strong> sur le planning.</p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button disabled={!selected} onClick={() => onAssign({ spot_id: selected.id, ligne_id: ctx.ligne.id, date: ctx.date, status, consignes: consignes.trim() || undefined, field_rebooking: fieldRebooking, field_rebooked_by: fieldRebooking ? (fieldBy.trim() || undefined) : undefined, credited_cdp_id: creditedCdp || undefined, lane: Number.isInteger(ctx.lane) ? ctx.lane : null })}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="assign-submit">Assigner</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Compléter la fiche du responsable juste après création (email + téléphone) */}
    <Dialog open={!!completeChef} onOpenChange={(o) => { if (!o && !ceSaving) setCompleteChef(null); }}>
      <DialogContent className="max-w-md" data-testid="complete-chef-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRound className="h-5 w-5" style={{ color: FIELD_REBOOK_COLOR }} /> Compléter la fiche du responsable
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "rgba(124,58,237,0.35)", background: "rgba(124,58,237,0.05)" }}>
            <span className="font-bold text-ink">{completeChef?.nom}</span>
            <span className="text-muted-ink"> — créé. Renseignez ses coordonnées pour les réutiliser plus tard <span className="italic">(facultatif)</span>.</span>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Email</label>
            <Input type="email" value={ceEmail} onChange={(e) => setCeEmail(e.target.value)}
              placeholder="responsable@exemple.fr" data-testid="complete-chef-email" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-ink">Téléphone</label>
            <Input type="tel" value={ceTel} onChange={(e) => setCeTel(e.target.value)}
              placeholder="06 12 34 56 78" data-testid="complete-chef-tel" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => setCompleteChef(null)} disabled={ceSaving}
            className="rounded-lg border border-[var(--border-soft)] px-3 py-2 text-sm font-semibold transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
            data-testid="complete-chef-later">Plus tard</button>
          <button onClick={saveCompleteChef} disabled={ceSaving || (!ceEmail.trim() && !ceTel.trim())}
            className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-bold text-white transition-opacity disabled:opacity-50"
            style={{ background: FIELD_REBOOK_COLOR }}
            data-testid="complete-chef-save">
            {ceSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// Normalise un numéro FR au format international pour les liens wa.me (WhatsApp).
// 06 12 34 56 78 -> 33612345678 ; +33 6… / 0033 6… / 612345678 gérés.
function waNumber(tel) {
  if (!tel) return "";
  let d = String(tel).replace(/[^\d+]/g, "");
  if (d.startsWith("+")) d = d.slice(1);
  else if (d.startsWith("00")) d = d.slice(2);
  else if (d.startsWith("0")) d = "33" + d.slice(1);
  else if (/^[1-9]\d{8}$/.test(d)) d = "33" + d;
  return d;
}


function DiffusionDialog({ open, onClose, week, onSent }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selected, setSelected] = useState({}); // chef_id -> bool
  const [edited, setEdited] = useState({}); // chef_id -> texte WhatsApp modifiable
  // --- Relier le chef (1 clic) : liste des fiches + état du popover ---
  const [chefsRef, setChefsRef] = useState([]);
  const [linkOpenFor, setLinkOpenFor] = useState(null); // chef_id du paquet dont le popover est ouvert
  const [linkQuery, setLinkQuery] = useState("");
  const [linking, setLinking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return api.get("/planning/diffusion", { params: { week } })
      .then((r) => {
        setData(r.data);
        const sel = {};
        const ed = {};
        (r.data.packets || []).forEach((p) => { sel[p.chef_id] = p.has_email; ed[p.chef_id] = p.whatsapp_text || ""; });
        setSelected(sel);
        setEdited(ed);
      })
      .catch(() => toast.error("Erreur de chargement de la diffusion"))
      .finally(() => setLoading(false));
  }, [week]);

  useEffect(() => {
    if (!open) return;
    load();
    api.get("/chefs-equipe").then((r) => setChefsRef((r.data || []).filter((c) => c.active !== false))).catch(() => {});
  }, [open, load]);

  // Relie un paquet (toutes ses lignes) à une fiche chef existante.
  const linkChef = async (packet, chefId) => {
    const ids = packet.ligne_ids || [];
    if (!ids.length) { toast.error("Aucune ligne à relier pour ce chef"); return; }
    setLinking(true);
    try {
      const { data: res } = await api.put(`/planning/lignes/${ids[0]}/chef`, {
        chef_equipe_id: chefId, ligne_ids: ids,
      });
      toast.success(`${res.linked} ligne(s) reliée(s) à ${res.chef_nom}` + (res.has_tel ? "" : " · téléphone à compléter"));
      setLinkOpenFor(null);
      setLinkQuery("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de relier le chef");
    } finally { setLinking(false); }
  };

  // Crée une fiche à partir du nom affiché puis la relie immédiatement.
  const createAndLink = async (packet) => {
    const nom = (packet.chef_nom || "").trim();
    if (!nom) return;
    setLinking(true);
    try {
      const { data: chef } = await api.post("/chefs-equipe", { nom });
      setChefsRef((prev) => (prev.some((c) => c.id === chef.id) ? prev : [...prev, chef]));
      await linkChef(packet, chef.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création de la fiche impossible");
      setLinking(false);
    }
  };

  const copyWa = async (text, nom) => {
    try { await navigator.clipboard.writeText(text); toast.success(`Texte WhatsApp de ${nom} copié`); }
    catch { toast.error("Copie impossible"); }
  };

  const openWa = (p) => {
    const num = waNumber(p.chef_tel);
    if (!num) { toast.error("Aucun téléphone pour ce chef — ajoutez-le dans Admin › Chefs d'équipe"); return; }
    const text = edited[p.chef_id] ?? p.whatsapp_text ?? "";
    const url = `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const send = async () => {
    const chef_ids = Object.keys(selected).filter((k) => selected[k]);
    if (chef_ids.length === 0) { toast.error("Sélectionnez au moins un chef d'équipe"); return; }
    setSending(true);
    try {
      const { data: res } = await api.post("/planning/diffusion/send", { week, chef_ids });
      const via = res.provider === "smtp" ? "par email (SMTP)" : "dans l'Outbox (journal)";
      toast.success(`Semaine validée · ${res.sent.length} chef(s) notifié(s) ${via}`);
      if (res.failed?.length) toast.error(`${res.failed.length} envoi(s) en échec`);
      onSent && onSent();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de diffusion"); }
    finally { setSending(false); }
  };

  const packets = data?.packets || [];
  const validation = data?.validation;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="diffusion-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[var(--shariti-orange)]" />Valider &amp; diffuser la semaine</DialogTitle>
          <p className="text-xs text-muted-ink">
            {data ? <>Semaine {data.week_number} · {data.week_label}</> : "Chargement…"}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2 py-6">{[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-[var(--surface-2)]" />)}</div>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {validation && (
              <div className="flex items-center gap-2 rounded-lg border border-[rgba(31,157,85,0.3)] bg-[rgba(31,157,85,0.07)] px-3 py-2 text-xs text-[#0F5A2F]" data-testid="diffusion-already-validated">
                <CheckCircle2 className="h-4 w-4" />Semaine déjà validée le {frenchDate(String(validation.validated_at).slice(0, 10))} par {validation.validated_by} — {validation.sent_count} chef(s) notifié(s). Vous pouvez re-diffuser.
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-[11px]">
              <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-1 font-semibold" data-testid="diffusion-summary-chefs">{packets.length} chef(s) d'équipe</span>
              <span className="rounded-full bg-[var(--surface-2)] px-2.5 py-1 font-semibold">{data?.bookings_total || 0} journée(s) planifiée(s)</span>
              {data?.bookings_without_chef > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(233,184,0,0.18)] px-2.5 py-1 font-semibold text-[#5A4200]"><AlertTriangle className="h-3 w-3" />{data.bookings_without_chef} sans chef rattaché</span>
              )}
            </div>

            {packets.length === 0 && (
              <div className="rounded-xl border border-dashed border-[var(--border-soft)] p-6 text-center text-sm text-muted-ink">
                Aucune journée rattachée à un chef d'équipe cette semaine. Rattachez les lignes à des équipes/chefs dans l'Administration.
              </div>
            )}

            {packets.map((p) => (
              <div key={p.chef_id} className="rounded-xl border border-[var(--border-soft)] bg-white p-3" data-testid={`diffusion-packet-${p.chef_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <label className="flex min-w-0 cursor-pointer items-start gap-2">
                    <input type="checkbox" checked={!!selected[p.chef_id]} disabled={!p.has_email}
                      onChange={(e) => setSelected((s) => ({ ...s, [p.chef_id]: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-[var(--shariti-orange)]" data-testid={`diffusion-check-${p.chef_id}`} />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <UserRound className="h-4 w-4 text-[var(--shariti-orange)]" />
                        <span className="font-bold">{p.chef_nom}</span>
                        {p.unlinked && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(233,184,0,0.18)] px-2 py-0.5 text-[10px] font-semibold text-[#5A4200]"
                            title="Cette ligne affiche un chef mais n'est pas reliée à une fiche chef d'équipe. Reliez-la dans l'Administration pour récupérer son téléphone/e-mail."
                            data-testid={`diffusion-unlinked-${p.chef_id}`}>
                            <AlertTriangle className="h-3 w-3" /> Fiche à relier
                          </span>
                        )}
                        {p.equipes.map((e) => <span key={e} className="rounded-full bg-[rgba(47,111,237,0.1)] px-2 py-0.5 text-[10px] font-semibold text-[#1D4ED8]">{e}</span>)}
                        <span className="text-[11px] text-muted-ink">· {p.count} journée(s)</span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3 text-muted-ink" />
                          {p.has_email
                            ? <span className="text-muted-ink">{p.chef_email}</span>
                            : <span className="font-semibold text-[#8A3A10]">Email manquant</span>}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3 text-muted-ink" />
                          {p.has_tel
                            ? <span className="text-muted-ink">{p.chef_tel}</span>
                            : <span className="font-semibold text-[#8A3A10]">Tél. manquant</span>}
                        </span>
                      </div>
                    </div>
                  </label>
                  <div className="flex shrink-0 flex-col gap-1.5">
                    {p.unlinked && (
                      <Popover open={linkOpenFor === p.chef_id}
                        onOpenChange={(o) => { setLinkOpenFor(o ? p.chef_id : null); setLinkQuery(""); }}>
                        <PopoverTrigger asChild>
                          <Button size="sm" variant="outline"
                            className="border-[rgba(233,184,0,0.6)] bg-[rgba(233,184,0,0.12)] text-[#5A4200] hover:bg-[rgba(233,184,0,0.22)]"
                            title="Relier cette ligne à une fiche chef d'équipe pour récupérer son téléphone/e-mail"
                            data-testid={`diffusion-link-chef-${p.chef_id}`}>
                            <Link2 className="mr-1.5 h-3.5 w-3.5" />Relier la fiche
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-72 p-2" data-testid={`diffusion-link-popover-${p.chef_id}`}>
                          <div className="mb-1.5 px-1 text-[11px] font-semibold text-[#181818]">Relier « {p.chef_nom} » à une fiche</div>
                          <div className="relative mb-2">
                            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
                            <Input autoFocus value={linkQuery} onChange={(e) => setLinkQuery(e.target.value)}
                              placeholder="Rechercher un chef…" className="h-8 pl-7 text-xs"
                              data-testid={`diffusion-link-search-${p.chef_id}`} />
                          </div>
                          <div className="max-h-48 space-y-0.5 overflow-y-auto">
                            {(() => {
                              const raw = linkQuery.toLowerCase().trim();
                              const list = (chefsRef || []).filter((c) => c?.nom && (!raw || c.nom.toLowerCase().includes(raw)));
                              const exact = (chefsRef || []).some((c) => c?.nom && c.nom.toLowerCase() === raw);
                              return (
                                <>
                                  {list.length === 0 && (
                                    <div className="px-2 py-2 text-center text-[11px] text-muted-ink">Aucune fiche trouvée</div>
                                  )}
                                  {list.slice(0, 30).map((c) => (
                                    <button key={c.id} type="button" disabled={linking}
                                      onClick={() => linkChef(p, c.id)}
                                      className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
                                      data-testid={`diffusion-link-option-${c.id}`}>
                                      <span className="min-w-0 break-words font-semibold text-[#181818]">{c.nom}</span>
                                      {c.tel
                                        ? <span className="shrink-0 text-[10px] font-semibold text-[#0F5A2F]">tél. ✓</span>
                                        : <span className="shrink-0 text-[10px] text-muted-ink">sans tél.</span>}
                                    </button>
                                  ))}
                                  {!exact && (
                                    <button type="button" disabled={linking}
                                      onClick={() => createAndLink(p)}
                                      className="mt-1 flex w-full items-center gap-1.5 rounded-md border border-dashed border-[var(--border-soft)] px-2 py-1.5 text-left text-xs font-semibold text-[var(--shariti-orange)] transition-colors hover:bg-[var(--surface-2)] disabled:opacity-50"
                                      data-testid={`diffusion-link-create-${p.chef_id}`}>
                                      {linking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                                      Créer et relier « {p.chef_nom} »
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                    <Button size="sm" onClick={() => openWa(p)} disabled={!p.has_tel}
                      className="bg-[#25D366] text-white hover:bg-[#1FB855] disabled:opacity-50"
                      title={p.has_tel ? "Ouvrir WhatsApp avec le planning pré-rempli" : "Téléphone manquant"}
                      data-testid={`diffusion-wa-send-${p.chef_id}`}>
                      <MessageSquare className="mr-1.5 h-3.5 w-3.5" />Envoyer WhatsApp
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => copyWa(edited[p.chef_id] ?? p.whatsapp_text, p.chef_nom)}
                      className="border-[var(--border-soft)]" data-testid={`diffusion-copy-wa-${p.chef_id}`}>
                      <Copy className="mr-1.5 h-3.5 w-3.5" />Copier le texte
                    </Button>
                  </div>
                </div>
                <textarea
                  value={edited[p.chef_id] ?? p.whatsapp_text ?? ""}
                  onChange={(e) => setEdited((s) => ({ ...s, [p.chef_id]: e.target.value }))}
                  rows={8}
                  data-testid={`diffusion-wa-text-${p.chef_id}`}
                  className="mt-2 max-h-56 w-full resize-y overflow-y-auto whitespace-pre-wrap rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5 font-[Figtree] text-[11px] leading-relaxed text-[#181818] outline-none focus:border-[var(--shariti-orange)] focus:ring-2 focus:ring-[rgba(238,114,46,0.15)]" />
                <p className="mt-1 text-[10px] text-muted-ink">Vous pouvez ajuster ce texte (consignes, détails…) avant l'envoi WhatsApp ou la copie.</p>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="diffusion-cancel">Annuler</Button>
          <Button onClick={send} disabled={sending || selectedCount === 0}
            className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="diffusion-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" />Valider &amp; diffuser ({selectedCount})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
